import React, { useState, useEffect, useRef } from 'react';
import {
    Container, Row, Col, Card, Badge, Spinner, Button, Accordion, Modal
} from 'react-bootstrap';
import {
    FaLeaf, FaMapMarkerAlt, FaPhone, FaTree, FaStore,
    FaStar, FaCheckCircle, FaSeedling, FaSun, FaTint,
    FaInstagram, FaWhatsapp, FaEnvelope, FaArrowRight,
    FaShoppingCart, FaAward, FaUsers, FaRulerCombined,
    FaPalette, FaWater, FaLightbulb, FaHardHat, FaHome,
    FaCouch, FaSwimmingPool, FaUmbrellaBeach, FaPaintBrush,
    FaImage, FaCamera, FaEye, FaBuilding, FaClock, FaInfoCircle,
    FaTimes, FaPaperPlane, FaUser, FaSpinner, FaBell, FaCalculator,FaGem,FaTruck,FaTag,FaShieldAlt,FaHeadset
} from 'react-icons/fa';
import { GiFlowerPot, GiFlowers } from 'react-icons/gi';
import beforeImage from '../assets/before.webp';
import AfterImage from '../assets/after.webp';

import Landscape from './Landscaping';
import aboutImage from "../assets/aboutpage.webp";
import { FaTiktok } from 'react-icons/fa';
import personworker from '../assets/personworker.webp';
import personowner from '../assets/person1owner.webp';
import emiratiimg from '../assets/owner.webp';
import logo from '../assets/logo.webp';

const API_BASE = '/nursery';

// ============================================================
// UTILITY FUNCTIONS
// ============================================================

const PlantPlaceholderColors = [
    '#2D7D46', '#1B5E20', '#388E3C', '#4CAF50', '#81C784', '#A5D6A7'
];

const PlantSVG = ({ color = '#2D7D46', index = 0 }) => (
    <svg width="100%" height="200" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg"
        style={{ display: 'block', background: `linear-gradient(135deg, ${color}22, ${color}44)` }}>
        <ellipse cx="100" cy="180" rx="50" ry="8" fill={color} opacity="0.15" />
        <rect x="97" y="120" width="6" height="60" rx="3" fill={color} opacity="0.7" />
        <ellipse cx="100" cy="100" rx="45" ry="50" fill={color} opacity="0.85" />
        <ellipse cx="70" cy="115" rx="28" ry="20" fill={color} opacity="0.7" transform="rotate(-30 70 115)" />
        <ellipse cx="130" cy="115" rx="28" ry="20" fill={color} opacity="0.7" transform="rotate(30 130 115)" />
        <ellipse cx="100" cy="80" rx="35" ry="40" fill={color} />
        {index % 2 === 0 && <circle cx="90" cy="75" r="12" fill="#FF8F00" opacity="0.9" />}
        {index % 3 === 0 && <circle cx="115" cy="90" r="10" fill="#E91E63" opacity="0.8" />}
        {index % 2 !== 0 && <circle cx="100" cy="65" r="11" fill="#FDD835" opacity="0.9" />}
    </svg>
);

function useCounter(target, duration = 2000, start = false) {
    const [count, setCount] = useState(0);
    useEffect(() => {
        if (!start || !target) return;
        let startTime = null;
        const step = (timestamp) => {
            if (!startTime) startTime = timestamp;
            const progress = Math.min((timestamp - startTime) / duration, 1);
            setCount(Math.floor(progress * target));
            if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
    }, [target, duration, start]);
    return count;
}

function useScrollReveal() {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);
    
    useEffect(() => {
        const timer = setTimeout(() => {
            setVisible(true);
        }, 5000);
        
        return () => clearTimeout(timer);
    }, []);
    
    return [ref, visible];
}

const isValidImageUrl = (url) => {
    if (!url) return false;
    return url.startsWith('http://') || url.startsWith('https://') || url.startsWith('/');
};

const getValidImage = (url, fallback = 'https://via.placeholder.com/800x400/2D7D46/fff?text=No+Image') => {
    if (isValidImageUrl(url)) return url;
    return fallback;
};

const SLIDES = [
    { 
        src: '/nursery/slideshow/slide13.webp', 
        caption: 'Premium Houseplants',
        sub: 'Elevate your indoor living space.',
        title: 'Bring Nature Indoors',
        description: 'Transform your home with our premium collection of indoor plants.',
        cta: 'Explore Indoor Plants'
    },
    { 
        src: '/nursery/slideshow/slide4.webp', 
        caption: 'Garden Landscape',
        sub: 'Transforming outdoor dreams into reality.',
        title: 'Build Dream Garden',
        description: 'Professional landscaping that turns ordinary spaces into extraordinary living environments.',
        cta: 'View Landscaping Projects'
    },
    { 
        src: '/nursery/slideshow/slide8.webp', 
        caption: 'Rare Exotic Species',
        sub: 'Unique plants sourced from around the globe.',
        title: 'Discover Rare Exotics',
        description: 'Exclusive collection of rare and exotic plants from around the world.',
        cta: 'Browse Exotic Collection'
    },
    { 
        src: '/nursery/slideshow/slide1.webp', 
        caption: 'Professional Plant Care',
        sub: 'Expert guidance for your green companions.',
        title: 'Discover Rare Exotics',
        description: 'Get professional advice to keep your plants healthy and thriving year-round.',
        cta: 'Get Care Tips'
    }
];

const DEFAULT_LANDSCAPING_PORTFOLIO = [
    { id: 1, title: 'Tropical Backyard Oasis', location: 'Illustrative Example', description: 'Lush tropical garden with palm trees, water features, and a custom-built pergola for outdoor entertaining.', image: 'https://media.istockphoto.com/id/1255835530/photo/modern-custom-suburban-home-exterior.jpg?s=612x612&w=0&k=20&c=0Dqjm3NunXjZtWVpsUvNKg2A4rK2gMvJ-827nb4AMU4=', beforeImage: 'https://st.hzcdn.com/simgs/0b41071b0f6adb55_4-9708/home-design.jpg', features: ['Palm Trees & Tropical Plants', 'Water Feature', 'Pergola', 'Outdoor Lighting'], price: '15,999', category: 'Residential', completed: '2024', active: true, type: 'landscaping' },
    { id: 2, title: 'Rooftop Garden Sanctuary', location: 'Illustrative Example', description: 'Transform your rooftop into a green paradise with panoramic views, custom planters, and irrigation systems.', image: 'https://img.magnific.com/premium-photo/serene-backyard-oasis-landscaped-garden-with-patio-pond_1341597-34598.jpg?semt=ais_hybrid&w=740&q=80', beforeImage: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS-hgCI3fYbcQRxvkUCGyQYeFxmb--5UhkpgQ&s', features: ['Custom Planters', 'Irrigation System', 'Outdoor Furniture', 'Privacy Screening'], price: '6,499', category: 'Residential', completed: '2024', active: true, type: 'landscaping' },
    { id: 3, title: 'Classic Arabic Courtyard', location: 'Illustrative Example', description: 'Traditional Arabic courtyard with water features, date palms, and authentic architectural elements.', image: 'https://images.pexels.com/photos/1643389/pexels-photo-1643389.jpeg?auto=compress&cs=tinysrgb&w=800', beforeImage: 'https://images.pexels.com/photos/1643389/pexels-photo-1643389.jpeg?auto=compress&cs=tinysrgb&w=400', features: ['Water Feature', 'Date Palms', 'Traditional Architecture', 'Cultural Design'], price: '14,999', category: 'Residential', completed: '2024', active: true, type: 'landscaping' }
];


const DEFAULT_PLANT_OFFERS = [
    { id: 1, title: 'The Indoor Oasis Bundle', description: '3 Premium Indoor Plants + Free Decorative Pots', features: ['3 Premium Indoor Plants', 'Free Decorative Pots', 'Plant Care Kit'], color: '#4CAF50', active: true, type: 'plants' },
    { id: 2, title: 'Flowering Garden Set', description: '5 Seasonal Flowering Plants + Fertilizer', features: ['5 Seasonal Flowering Plants', '1 Bag of Fertilizer', 'Expert Planting Guide'], color: '#E91E63', active: true, type: 'plants' },
    { id: 3, title: 'Corporate Greenify', description: '10 Medium Office Plants + Maintenance', features: ['10 Medium Office Plants', 'Monthly Maintenance (1st Month Free)', 'Elegant Stone Pots'], color: '#1B5E20', active: true, type: 'plants' },
];

const qmStyles = {
    wrap: { position: 'relative', background: '#fff', borderRadius: 24, overflow: 'hidden', padding: '36px 40px 32px' },
    topAccent: { position: 'absolute', top: 0, left: 0, right: 0, height: 5, background: 'linear-gradient(90deg, #2D7D46, #c9a03d, #2D7D46)' },
    closeBtn: { position: 'absolute', top: 18, right: 18, width: 36, height: 36, borderRadius: '50%', border: 'none', background: '#f4faf5', color: '#2D7D46', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: 14, transition: 'all 0.2s' },
    header: { textAlign: 'center', marginBottom: 26 },
    headerBadge: { display: 'inline-flex', alignItems: 'center', background: '#E8F5E9', color: '#2D7D46', padding: '6px 16px', borderRadius: 30, fontSize: 12, fontWeight: 700, marginBottom: 14, letterSpacing: 0.5 },
    headerTitle: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: '1.7rem', fontWeight: 700, color: '#1B5E20', marginBottom: 8 },
    headerSub: { color: '#666', fontSize: 14, maxWidth: 460, margin: '0 auto', lineHeight: 1.6 },
    form: { marginTop: 6 },
    serviceToggleRow: { display: 'flex', gap: 10, marginBottom: 22, flexWrap: 'wrap' },
    serviceToggle: { flex: '1 1 140px', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '10px 14px', borderRadius: 14, border: '2px solid', fontWeight: 700, fontSize: 13, cursor: 'pointer', transition: 'all 0.25s' },
    label: { display: 'block', fontSize: 12, fontWeight: 700, color: '#1B5E20', marginBottom: 6, letterSpacing: 0.3, textTransform: 'uppercase' },
    inputWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
    inputIcon: { position: 'absolute', left: 14, color: '#2D7D46', fontSize: 13 },
    input: { width: '100%', padding: '12px 14px 12px 40px', borderRadius: 12, border: '2px solid #E8F5E9', fontSize: 14, outline: 'none', color: '#333', transition: 'border-color 0.2s' },
    textarea: { width: '100%', padding: '12px 16px', borderRadius: 12, border: '2px solid #E8F5E9', fontSize: 14, outline: 'none', resize: 'vertical', color: '#333', fontFamily: 'inherit' },
    errorText: { color: '#d32f2f', fontSize: 11, marginTop: 4, fontWeight: 600 },
    submitBtn: { width: '100%', marginTop: 24, background: 'linear-gradient(135deg, #2D7D46, #388E3C)', color: '#fff', border: 'none', borderRadius: 50, padding: '15px 0', fontWeight: 800, fontSize: 15, cursor: 'pointer', boxShadow: '0 12px 30px rgba(45,125,70,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'transform 0.2s' },
    footnote: { textAlign: 'center', fontSize: 12, color: '#999', marginTop: 14 },
    successWrap: { textAlign: 'center', padding: '30px 10px' },
    successIcon: { fontSize: 56, color: '#2D7D46', marginBottom: 18 },
    doneBtn: { marginTop: 20, background: 'linear-gradient(135deg, #2D7D46, #388E3C)', color: '#fff', border: 'none', borderRadius: 50, padding: '13px 40px', fontWeight: 700, fontSize: 14, cursor: 'pointer', boxShadow: '0 10px 25px rgba(45,125,70,0.25)' },
};

// ============================================================
// LAZY IMAGE COMPONENT
// ============================================================

const LazyImage = ({ src, alt, style, className, onError, sectionKey, immediate = false }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [imageSrc, setImageSrc] = useState(null);

    useEffect(() => {
        if (src) {
            setImageSrc(src);
        }
    }, [src]);

    useEffect(() => {
        if (imageSrc) {
            const img = new Image();
            img.src = imageSrc;
            img.onload = () => setIsLoaded(true);
            img.onerror = () => setHasError(true);
        }
    }, [imageSrc]);

    if (!isLoaded || !imageSrc) {
        return (
            <div 
                style={{ 
                    ...style, 
                    background: 'linear-gradient(135deg, #1a5c32, #2D7D46)',
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    minHeight: '120px',
                    position: 'relative',
                    width: '100%',
                    height: '100%',
                }}
            >
                <FaSpinner style={{ 
                    animation: 'spin 1s linear infinite', 
                    fontSize: '32px',
                    color: '#A5D6A7'
                }} />
            </div>
        );
    }

    return (
        <div 
            style={{ 
                position: 'relative',
                width: '100%',
                height: '100%',
                overflow: 'hidden',
                background: '#1a5c32',
            }}
        >
            <img loading="lazy"
                src={imageSrc}
                alt={alt}
                style={{
                    ...style,
                    opacity: isLoaded ? 1 : 0,
                    transition: 'opacity 0.5s ease-in-out',
                    width: '100%',
                    height: '100%',
                    objectFit: style?.objectFit || 'cover',
                    display: 'block',
                }}
                className={className}
                onLoad={() => setIsLoaded(true)}
                onError={(e) => {
                    setHasError(true);
                    if (onError) onError(e);
                }}
            />

            {hasError && (
                <div style={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#2D7D46',
                    color: '#fff',
                    fontSize: '30px',
                }}>
                    🌿
                </div>
            )}
        </div>
    );
};

// ============================================================
// COMPONENT: FaqItem
// ============================================================

function FaqItem({ icon, iconColor, iconBg, tag, q, a, isOpen, onClick }) {
    return (
        <div style={{
            background: '#fff', borderRadius: 20,
            border: `1px solid ${isOpen ? '#A5D6A7' : '#E8F5E9'}`,
            overflow: 'hidden',
            boxShadow: isOpen ? '0 8px 30px rgba(45,125,70,0.12)' : '0 2px 10px rgba(0,0,0,0.04)',
            transition: 'all 0.3s ease',
        }}>
            <button onClick={onClick} style={{
                width: '100%', background: isOpen ? '#f6fdf7' : '#fff',
                border: 'none', padding: '20px 24px',
                display: 'flex', alignItems: 'stretch', gap: 16,
                cursor: 'pointer', textAlign: 'left', transition: 'background 0.3s',
                minHeight: '80px',
            }}>
                <div style={{
                    width: 42, 
                    minHeight: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                }}>
                    <div style={{
                        width: 42, height: 42, borderRadius: 12,
                        background: iconBg, color: iconColor,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16, flexShrink: 0, transition: 'transform 0.3s',
                        transform: isOpen ? 'rotate(-10deg) scale(1.1)' : 'rotate(0deg) scale(1)',
                    }}>{icon}</div>
                </div>
                <div style={{ 
                    flex: 1, 
                    display: 'flex', 
                    flexDirection: 'column',
                    justifyContent: 'center',
                }}>
                    <div style={{ 
                        fontSize: 10, fontWeight: 700, letterSpacing: 1, 
                        color: iconColor, textTransform: 'uppercase', marginBottom: 4 
                    }}>{tag}</div>
                    <div style={{ 
                        fontWeight: 700, color: '#1B5E20', fontSize: 15, lineHeight: 1.4 
                    }}>{q}</div>
                </div>
                <div style={{
                    width: 32, height: 32, borderRadius: '50%',
                    background: isOpen ? '#2D7D46' : '#E8F5E9',
                    color: isOpen ? '#fff' : '#2D7D46',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 18, fontWeight: 700, flexShrink: 0,
                    transition: 'all 0.3s',
                    transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
                    alignSelf: 'center',
                }}>+</div>
            </button>
            <div style={{
                maxHeight: isOpen ? 200 : 0, overflow: 'hidden',
                transition: 'max-height 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
            }}>
                <div style={{
                    padding: '16px 24px 20px 82px',
                    fontSize: 14, color: '#555', lineHeight: 1.8,
                    borderTop: '1px solid #E8F5E9',
                }}>{a}</div>
            </div>
        </div>
    );
}

// ============================================================
// COMPONENT: PortfolioHoverCard
// ============================================================

function PortfolioHoverCard({ project, visible, idx, onQuoteClick }) {
    const [hovered, setHovered] = useState(false);
    
const getImageUrl = (url) => {
    if (!url) return 'https://images.pexels.com/photos/1595696/pexels-photo-1595696.jpeg?auto=compress&cs=tinysrgb&w=800';

    // If it's already a full URL
    if (url.startsWith('http://') || url.startsWith('https://')) {
        return url;
    }

    // If it starts with /uploads/, it's already correct
    if (url.startsWith('/uploads/')) {
        return url;
    }

    // If it starts with uploads/ (no leading slash)
    if (url.startsWith('uploads/')) {
        return `/${url}`;
    }

    // If it's just a filename (has extension)
    if (url.match(/\.(jpg|jpeg|png|gif|webp|avif)$/i)) {
        return `/uploads/${url}`;
    }

    // Fallback
    return url;
};    const imageUrl = getImageUrl(project.image || project.image_url || project.imageUrl);

    return (
        <div
            style={{
                position: 'relative', 
                borderRadius: 24, 
                overflow: 'hidden',
                height: 300, 
                cursor: 'default',
                boxShadow: hovered ? '0 24px 50px rgba(45,125,70,0.35)' : '0 15px 35px rgba(0,0,0,0.2)',
                transition: 'box-shadow 0.4s ease',
            }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <LazyImage
                src={imageUrl}
                alt={project.title}
                sectionKey="portfolio"
                style={{
                    width: '100%', 
                    height: '100%', 
                    objectFit: 'cover',
                    transition: 'transform 0.5s ease',
                    transform: hovered ? 'scale(1.08)' : 'scale(1)',
                }}
                onError={(e) => { 
                    e.target.src = 'https://images.pexels.com/photos/1595696/pexels-photo-1595696.jpeg?auto=compress&cs=tinysrgb&w=800'; 
                }}
            />

            <div style={{
                position: 'absolute', 
                inset: 0,
                background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.1) 50%, transparent 100%)',
                transition: 'opacity 0.4s ease',
                opacity: hovered ? 0 : 1,
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'flex-end', 
                padding: 24,
                pointerEvents: 'none',
            }}>
                <h5 style={{ color: '#fff', fontWeight: 700, marginBottom: 4, fontSize: 16 }}>
                    {project.title}
                </h5>
                <p style={{ color: '#A5D6A7', fontSize: 13, margin: 0 }}>
                    <FaMapMarkerAlt size={11} style={{ marginRight: 4 }} />
                    {project.location}
                </p>
            </div>

            <div style={{
                position: 'absolute', 
                inset: 0,
                background: 'linear-gradient(135deg, rgba(27,94,32,0.96), rgba(45,125,70,0.94))',
                transition: 'opacity 0.4s ease',
                opacity: hovered ? 1 : 0,
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'space-between',
                padding: 24,
                pointerEvents: hovered ? 'auto' : 'none',
            }}>
                <div>
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'flex-start', 
                        marginBottom: 10 
                    }}>
                        <span style={{
                            background: 'rgba(255,255,255,0.2)', 
                            color: '#fff',
                            fontSize: 11, 
                            fontWeight: 700, 
                            padding: '4px 12px', 
                            borderRadius: 20
                        }}>
                            {project.category || 'Residential'}
                        </span>
                        
                    </div>
                    <h4 style={{ 
                        color: '#fff', 
                        fontWeight: 800, 
                        fontSize: 17, 
                        marginBottom: 8, 
                        lineHeight: 1.3 
                    }}>
                        {project.title}
                    </h4>
                    <p style={{ 
                        color: 'rgba(255,255,255,0.85)', 
                        fontSize: 12, 
                        lineHeight: 1.6, 
                        margin: 0 
                    }}>
                        {project.description?.slice(0, 100)}...
                    </p>
                </div>

                <div>
                    <div style={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 6, 
                        marginBottom: 14 
                    }}>
                        {(project.features || []).slice(0, 3).map((f, fi) => (
                            <span key={fi} style={{
                                background: 'rgba(255,255,255,0.15)', 
                                color: '#A5D6A7',
                                fontSize: 10, 
                                padding: '3px 10px', 
                                borderRadius: 20,
                                display: 'flex', 
                                alignItems: 'center', 
                                gap: 4
                            }}>
                                <FaCheckCircle size={9} /> {f}
                            </span>
                        ))}
                    </div>
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        gap: 10,
                    }}>
                        <span style={{ 
                            color: 'rgba(255,255,255,0.7)', 
                            fontSize: 11 
                        }}>
                            <FaMapMarkerAlt size={10} style={{ marginRight: 4 }} />
                            {project.location}
                        </span>
                        <button
                            onClick={(e) => { e.stopPropagation(); onQuoteClick && onQuoteClick(project); }}
                            style={{
                                background: '#FDD835',
                                color: '#1B5E20',
                                border: 'none',
                                borderRadius: 20,
                                padding: '6px 14px',
                                fontSize: 11,
                                fontWeight: 800,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                                boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                                pointerEvents: 'auto',
                            }}
                        >
                            Get a Quote
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

// ============================================================
// COMPONENT: QuoteModal
// ============================================================

function QuoteModal({ show, onClose, initialType = 'landscaping', presetProject = '' }) {
    const [form, setForm] = useState({
        name: '', phone: '', location: '', serviceType: initialType, budget: '', message: ''
    });
    const [submitted, setSubmitted] = useState(false);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const submitRef = useRef(false);

    useEffect(() => {
        if (show) {
            setForm(f => ({ ...f, serviceType: initialType, message: presetProject ? `I'm interested in: ${presetProject}` : f.message }));
            setSubmitted(false);
            setErrors({});
            setIsSubmitting(false);
            submitRef.current = false;
        }
    }, [show, initialType, presetProject]);

    const update = (key, val) => setForm(f => ({ ...f, [key]: val }));

    const validate = () => {
        const errs = {};
        if (!form.name.trim()) errs.name = 'Please enter your name';
        if (!form.phone.trim()) errs.phone = 'Please enter a phone number';
        if (!form.location.trim()) errs.location = 'Please enter your location';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        if (isSubmitting || submitRef.current) return;
        if (!validate()) return;

        setIsSubmitting(true);
        submitRef.current = true;

        const serviceLabel = form.serviceType === 'landscaping' ? 'Landscaping Project' : form.serviceType === 'plants' ? 'Plants & Flowers' : 'Landscaping + Plants';
        const lines = [
            `*New Quote Request — Nasmat Al Zohour*`,
            `Name: ${form.name}`,
            `Phone: ${form.phone}`,
            `Location: ${form.location}`,
            `Service: ${serviceLabel}`,
            form.budget ? `Budget: AED ${form.budget}` : null,
            form.message ? `Details: ${form.message}` : null,
        ].filter(Boolean);

        const text = encodeURIComponent(lines.join('\n'));
        setSubmitted(true);
        setIsSubmitting(false);
        
        setTimeout(() => {
            window.open(`https://wa.me/971552698951?text=${text}`, '_blank', 'noreferrer');
            setTimeout(() => {
                submitRef.current = false;
            }, 1000);
        }, 300);
    };

    return (
        <Modal show={show} onHide={onClose} centered size="lg" contentClassName="quote-modal-content">
            <div style={qmStyles.wrap}>
                <div style={qmStyles.topAccent} />
                <button onClick={onClose} style={qmStyles.closeBtn} aria-label="Close">
                    <FaTimes />
                </button>

                {!submitted ? (
                    <>
                        <div style={qmStyles.header}>
                            <div style={qmStyles.headerBadge}>
                                <FaRulerCombined style={{ marginRight: 8 }} />
                                {initialType === 'landscaping' ? 'Transform Your Space' : 'Green Your Space'}
                            </div>
                            <h3 style={qmStyles.headerTitle}>
                                {initialType === 'landscaping' ? 'Request Your Landscaping Quote' : 'Request Your Plant Quote'}
                            </h3>
                            <p style={qmStyles.headerSub}>
                                {initialType === 'landscaping' 
                                    ? 'Tell us about your outdoor space — we\'ll create a custom design just like our portfolio projects.'
                                    : 'Tell us about your plant needs — we\'ll recommend the perfect greenery for your space.'}
                            </p>
                            {presetProject && (
                                <div style={{
                                    marginTop: 12,
                                    padding: '10px 16px',
                                    background: '#E8F5E9',
                                    borderRadius: 12,
                                    display: 'inline-block',
                                    fontSize: 13,
                                    color: '#1B5E20'
                                }}>
                                    <FaImage style={{ marginRight: 8 }} />
                                    You're inquiring about: <strong>{presetProject}</strong>
                                </div>
                            )}
                        </div>

                        <form onSubmit={handleSubmit} style={qmStyles.form}>
                            <div style={qmStyles.serviceToggleRow}>
                                {[
                                    { key: 'landscaping', label: 'Landscaping', icon: <FaHome /> },
                                    { key: 'plants', label: 'Plants & Flowers', icon: <GiFlowerPot /> },
                                    { key: 'both', label: 'Both', icon: <FaLeaf /> },
                                ].map(opt => (
                                    <button
                                        type="button"
                                        key={opt.key}
                                        onClick={() => update('serviceType', opt.key)}
                                        style={{
                                            ...qmStyles.serviceToggle,
                                            background: form.serviceType === opt.key ? 'linear-gradient(135deg, #2D7D46, #388E3C)' : '#f4faf5',
                                            color: form.serviceType === opt.key ? '#fff' : '#2D7D46',
                                            borderColor: form.serviceType === opt.key ? '#2D7D46' : '#E8F5E9',
                                        }}
                                    >
                                        {opt.icon}<span>{opt.label}</span>
                                    </button>
                                ))}
                            </div>

                            <Row className="g-3">
                                <Col md={6}>
                                    <label style={qmStyles.label}>Full Name</label>
                                    <div style={qmStyles.inputWrap}>
                                        <FaUser style={qmStyles.inputIcon} />
                                        <input
                                            style={{ ...qmStyles.input, borderColor: errors.name ? '#d32f2f' : '#E8F5E9' }}
                                            placeholder="Your name"
                                            value={form.name}
                                            onChange={(e) => update('name', e.target.value)}
                                        />
                                    </div>
                                    {errors.name && <div style={qmStyles.errorText}>{errors.name}</div>}
                                </Col>
                                <Col md={6}>
                                    <label style={qmStyles.label}>Phone / WhatsApp</label>
                                    <div style={qmStyles.inputWrap}>
                                        <FaWhatsapp style={qmStyles.inputIcon} />
                                        <input
                                            style={{ ...qmStyles.input, borderColor: errors.phone ? '#d32f2f' : '#E8F5E9' }}
                                            placeholder="+971 5X XXX XXXX"
                                            value={form.phone}
                                            onChange={(e) => update('phone', e.target.value)}
                                        />
                                    </div>
                                    {errors.phone && <div style={qmStyles.errorText}>{errors.phone}</div>}
                                </Col>
                                <Col md={6}>
                                    <label style={qmStyles.label}>Location / Emirate</label>
                                    <div style={qmStyles.inputWrap}>
                                        <FaMapMarkerAlt style={qmStyles.inputIcon} />
                                        <input
                                            style={{ ...qmStyles.input, borderColor: errors.location ? '#d32f2f' : '#E8F5E9' }}
                                            placeholder="e.g. Al Barsha, Dubai"
                                            value={form.location}
                                            onChange={(e) => update('location', e.target.value)}
                                        />
                                    </div>
                                    {errors.location && <div style={qmStyles.errorText}>{errors.location}</div>}
                                </Col>
                                <Col md={6}>
                                    <label style={qmStyles.label}>Estimated Budget (AED)</label>
                                    <div style={qmStyles.inputWrap}>
                                        <span style={{ ...qmStyles.inputIcon, fontWeight: 800, fontSize: 13 }}>AED</span>
                                        <input
                                            style={qmStyles.input}
                                            placeholder="Optional"
                                            value={form.budget}
                                            onChange={(e) => update('budget', e.target.value)}
                                        />
                                    </div>
                                </Col>
                                <Col md={12}>
                                    <label style={qmStyles.label}>Project Details</label>
                                    <textarea
                                        style={qmStyles.textarea}
                                        placeholder="Describe your space, plant preferences, or landscaping goals..."
                                        rows={4}
                                        value={form.message}
                                        onChange={(e) => update('message', e.target.value)}
                                    />
                                </Col>
                            </Row>

                            <button 
                                type="submit" 
                                style={{
                                    ...qmStyles.submitBtn,
                                    opacity: isSubmitting ? 0.7 : 1,
                                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                                }}
                                disabled={isSubmitting}
                            >
                                {isSubmitting ? (
                                    <>
                                        <FaSpinner style={{ marginRight: 10, animation: 'spin 1s linear infinite' }} /> 
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <FaPaperPlane style={{ marginRight: 10 }} /> Send Quote Request
                                    </>
                                )}
                            </button>
                            <p style={qmStyles.footnote}>We'll open WhatsApp with your details pre-filled so our team can respond right away.</p>
                        </form>
                    </>
                ) : (
                    <div style={qmStyles.successWrap}>
                        <div style={qmStyles.successIcon}><FaCheckCircle /></div>
                        <h3 style={qmStyles.headerTitle}>Quote Request Sent!</h3>
                        <p style={qmStyles.headerSub}>Continue in WhatsApp to chat with our team — they'll follow up shortly with your personalised quotation.</p>
                        <button onClick={onClose} style={qmStyles.doneBtn}>Done</button>
                    </div>
                )}
            </div>
        </Modal>
    );
}

// ============================================================
// COMPONENT: PlantCareScheduler
// ============================================================

function PlantCareScheduler({ plantName, plantId }) {
  const [showScheduler, setShowScheduler] = useState(false);
  const [reminder, setReminder] = useState({
    frequency: 'weekly',
    day: 'Monday',
    time: '09:00',
    type: 'watering',
    plantName: plantName || 'My Plant',
    phone: '',
  });
  const [saved, setSaved] = useState(false);
  const [scheduledReminders, setScheduledReminders] = useState([]);

  useEffect(() => {
    const saved = localStorage.getItem('plant_reminders');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setScheduledReminders(parsed);
      } catch (e) {}
    }
  }, []);

  const saveReminder = () => {
    if (!reminder.phone) {
      alert('Please enter your WhatsApp number to receive reminders');
      return;
    }

    const newReminder = {
      id: Date.now(),
      ...reminder,
      plantId: plantId || 'general',
      createdAt: new Date().toISOString(),
    };

    const updated = [...scheduledReminders, newReminder];
    setScheduledReminders(updated);
    localStorage.setItem('plant_reminders', JSON.stringify(updated));
    setSaved(true);

    const message = encodeURIComponent(
      `🌱 *Plant Care Reminder Set!*\n\n` +
      `Plant: ${reminder.plantName}\n` +
      `Reminder: ${reminder.type === 'watering' ? '💧 Watering' : '🌿 Fertilizing'}\n` +
      `Frequency: ${reminder.frequency}\n` +
      `Day: ${reminder.day}\n` +
      `Time: ${reminder.time}\n\n` +
      `We'll remind you via WhatsApp. Happy planting! 🌿`
    );
    window.open(`https://wa.me/971552698951?text=${message}`, '_blank');

    setTimeout(() => setSaved(false), 3000);
  };

  const deleteReminder = (id) => {
    const updated = scheduledReminders.filter(r => r.id !== id);
    setScheduledReminders(updated);
    localStorage.setItem('plant_reminders', JSON.stringify(updated));
  };

  const reminderTypes = [
    { value: 'watering', label: '💧 Watering' },
    { value: 'fertilizing', label: '🌿 Fertilizing' },
    { value: 'misting', label: '💨 Misting' },
    { value: 'repotting', label: '🪴 Repotting' },
  ];

  const frequencies = [
    { value: 'daily', label: 'Daily' },
    { value: 'weekly', label: 'Weekly' },
    { value: 'biweekly', label: 'Bi-Weekly' },
    { value: 'monthly', label: 'Monthly' },
  ];

  const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

  return (
    <div style={{ position: 'relative' }}>
      {showScheduler && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.6)',
          backdropFilter: 'blur(8px)',
          zIndex: 9999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px',
        }} onClick={() => setShowScheduler(false)}>
          <div style={{
            background: '#fff',
            borderRadius: '24px',
            maxWidth: '500px',
            width: '100%',
            padding: '32px',
            position: 'relative',
            maxHeight: '90vh',
            overflowY: 'auto',
          }} onClick={(e) => e.stopPropagation()}>
            
            <button
              onClick={() => setShowScheduler(false)}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: '#f0f0f0',
                border: 'none',
                borderRadius: '50%',
                width: '32px',
                height: '32px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '16px',
              }}
            >
              ✕
            </button>

            <h3 style={{
              fontFamily: "'Playfair Display', serif",
              color: '#1B5E20',
              fontSize: '20px',
              marginBottom: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px'
            }}>
              <FaBell style={{ color: '#2D7D46' }} /> Plant Care Reminder
            </h3>
            <p style={{ color: '#666', fontSize: '14px', marginBottom: '24px' }}>
              Get WhatsApp reminders for {plantName || 'your plant'}
            </p>

            {saved ? (
              <div style={{
                textAlign: 'center',
                padding: '30px 0',
              }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>✅</div>
                <h4 style={{ color: '#2D7D46', fontWeight: 700 }}>Reminder Set!</h4>
                <p style={{ color: '#666' }}>We'll notify you on WhatsApp.</p>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#1B5E20', marginBottom: '4px' }}>
                    Plant Name
                  </label>
                  <input
                    type="text"
                    value={reminder.plantName}
                    onChange={(e) => setReminder({ ...reminder, plantName: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '12px',
                      border: '2px solid #E8F5E9',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#1B5E20', marginBottom: '4px' }}>
                    Reminder Type
                  </label>
                  <select
                    value={reminder.type}
                    onChange={(e) => setReminder({ ...reminder, type: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '12px',
                      border: '2px solid #E8F5E9',
                      fontSize: '14px',
                      outline: 'none',
                      background: '#fff',
                    }}
                  >
                    {reminderTypes.map(t => (
                      <option key={t.value} value={t.value}>{t.label}</option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#1B5E20', marginBottom: '4px' }}>
                    Frequency
                  </label>
                  <select
                    value={reminder.frequency}
                    onChange={(e) => setReminder({ ...reminder, frequency: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '12px',
                      border: '2px solid #E8F5E9',
                      fontSize: '14px',
                      outline: 'none',
                      background: '#fff',
                    }}
                  >
                    {frequencies.map(f => (
                      <option key={f.value} value={f.value}>{f.label}</option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#1B5E20', marginBottom: '4px' }}>
                    Day
                  </label>
                  <select
                    value={reminder.day}
                    onChange={(e) => setReminder({ ...reminder, day: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '12px',
                      border: '2px solid #E8F5E9',
                      fontSize: '14px',
                      outline: 'none',
                      background: '#fff',
                    }}
                  >
                    {days.map(d => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>

                <div style={{ marginBottom: '16px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#1B5E20', marginBottom: '4px' }}>
                    Time
                  </label>
                  <input
                    type="time"
                    value={reminder.time}
                    onChange={(e) => setReminder({ ...reminder, time: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '12px',
                      border: '2px solid #E8F5E9',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                  />
                </div>

                <div style={{ marginBottom: '24px' }}>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 700, color: '#1B5E20', marginBottom: '4px' }}>
                    WhatsApp Number <span style={{ color: '#ff6b6b' }}>*</span>
                  </label>
                  <input
                    type="tel"
                    placeholder="+971 5X XXX XXXX"
                    value={reminder.phone}
                    onChange={(e) => setReminder({ ...reminder, phone: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '10px 14px',
                      borderRadius: '12px',
                      border: '2px solid #E8F5E9',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                  />
                </div>

                <button
                  onClick={saveReminder}
                  style={{
                    width: '100%',
                    padding: '14px',
                    background: 'linear-gradient(135deg, #2D7D46, #388E3C)',
                    color: '#fff',
                    border: 'none',
                    borderRadius: '50px',
                    fontSize: '15px',
                    fontWeight: 700,
                    cursor: 'pointer',
                    boxShadow: '0 8px 24px rgba(45,125,70,0.3)',
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'scale(1.02)';
                    e.currentTarget.style.boxShadow = '0 12px 32px rgba(45,125,70,0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = '0 8px 24px rgba(45,125,70,0.3)';
                  }}
                >
                  <FaWhatsapp style={{ marginRight: '8px' }} /> Set Reminder
                </button>
              </>
            )}

            {scheduledReminders.filter(r => r.plantId === plantId || r.plantName === plantName).length > 0 && (
              <div style={{ marginTop: '24px', borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}>
                <h5 style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                  Your Reminders
                </h5>
                {scheduledReminders.filter(r => r.plantId === plantId || r.plantName === plantName).map((r, i) => (
                  <div key={i} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 12px',
                    background: '#f9fdf6',
                    borderRadius: '8px',
                    marginBottom: '8px',
                    fontSize: '13px',
                  }}>
                    <div>
                      <span style={{ fontWeight: 600 }}>{r.plantName}</span>
                      <span style={{ color: '#999', marginLeft: '8px' }}>
                        {r.type === 'watering' ? '💧' : '🌿'} {r.day} {r.time}
                      </span>
                    </div>
                    <button
                      onClick={() => deleteReminder(r.id)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#ff6b6b',
                        cursor: 'pointer',
                        fontSize: '14px',
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// COMPONENT: QuickQuoteCalculator
// ============================================================

function QuickQuoteCalculator({ onRequestQuote }) {
  const [area, setArea] = useState(50);
  const [packageType, setPackageType] = useState('basic');
  const [hasWaterFeature, setHasWaterFeature] = useState(false);
  const [hasLighting, setHasLighting] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);

  const packages = {
    basic: { label: '🌿 Basic', rate: 50, min: 30, desc: 'Essential plants & turf' },
    premium: { label: '🌟 Premium', rate: 120, min: 50, desc: 'Exotic plants & design' },
    luxury: { label: '👑 Luxury', rate: 200, min: 80, desc: 'Complete transformation' },
  };

  const calculatePrice = () => {
    let total = area * packages[packageType].rate;
    if (hasWaterFeature) total += area * 15;
    if (hasLighting) total += area * 10;
    return total;
  };

  const formatPrice = (num) => {
    return num.toLocaleString('en-AE');
  };

  const handleRequestQuote = () => {
    const message = encodeURIComponent(
      `🏠 *Landscaping Quote Request*\n\n` +
      `Area: ${area} m²\n` +
      `Package: ${packages[packageType].label}\n` +
      `Water Feature: ${hasWaterFeature ? 'Yes' : 'No'}\n` +
      `Lighting: ${hasLighting ? 'Yes' : 'No'}\n` +
      `Estimate: AED ${formatPrice(calculatePrice())}\n\n` +
      `Please send me a detailed quote.`
    );
    window.open(`https://wa.me/971552698951?text=${message}`, '_blank');
    if (onRequestQuote) onRequestQuote();
  };

  return (
    <div style={{
      background: '#fff',
      borderRadius: '24px',
      padding: '32px',
      boxShadow: '0 20px 60px rgba(45,125,70,0.12)',
      border: '1px solid #E8F5E9',
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        marginBottom: '20px',
      }}>
        <div style={{
          width: '44px',
          height: '44px',
          borderRadius: '12px',
          background: 'linear-gradient(135deg, #2D7D46, #4CAF50)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#fff',
          fontSize: '20px',
        }}>
          <FaRulerCombined />
        </div>
        <div>
          <h4 style={{ margin: 0, color: '#1B5E20', fontWeight: 700 }}>Quick Quote Calculator</h4>
          <p style={{ margin: 0, color: '#666', fontSize: '13px' }}>Get an instant estimate for your project</p>
        </div>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
          <label style={{ fontSize: '13px', fontWeight: 600, color: '#1B5E20' }}>
            Garden Area: <strong>{area} m²</strong>
          </label>
          <span style={{ fontSize: '12px', color: '#999' }}>Min: 30m²</span>
        </div>
        <input
          type="range"
          min="30"
          max="500"
          value={area}
          onChange={(e) => setArea(Number(e.target.value))}
          style={{
            width: '100%',
            height: '6px',
            borderRadius: '3px',
            background: 'linear-gradient(90deg, #A5D6A7, #2D7D46)',
            outline: 'none',
            WebkitAppearance: 'none',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '10px', color: '#999', marginTop: '4px' }}>
          <span>30m²</span>
          <span>150m²</span>
          <span>300m²</span>
          <span>500m²</span>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px', marginBottom: '20px' }}>
        {Object.entries(packages).map(([key, pkg]) => (
          <button
            key={key}
            onClick={() => setPackageType(key)}
            style={{
              padding: '10px 8px',
              borderRadius: '12px',
              border: packageType === key ? '2px solid #2D7D46' : '2px solid #E8F5E9',
              background: packageType === key ? '#f0faf3' : '#fff',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              textAlign: 'center',
              fontSize: '12px',
              fontWeight: packageType === key ? 700 : 500,
              color: packageType === key ? '#1B5E20' : '#666',
            }}
          >
            <div style={{ fontSize: '16px', marginBottom: '2px' }}>{pkg.label.split(' ')[0]}</div>
            <div>{pkg.label.split(' ')[1] || ''}</div>
            <div style={{ fontSize: '10px', color: '#999' }}>AED {pkg.rate}/m²</div>
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={hasWaterFeature}
            onChange={(e) => setHasWaterFeature(e.target.checked)}
            style={{ accentColor: '#2D7D46', width: '16px', height: '16px' }}
          />
          <span>💧 Water Feature (+AED {formatPrice(area * 15)})</span>
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={hasLighting}
            onChange={(e) => setHasLighting(e.target.checked)}
            style={{ accentColor: '#2D7D46', width: '16px', height: '16px' }}
          />
          <span>💡 Outdoor Lighting (+AED {formatPrice(area * 10)})</span>
        </label>
      </div>

      <div style={{
        background: 'linear-gradient(135deg, #1B5E20, #2D7D46)',
        borderRadius: '16px',
        padding: '20px',
        marginBottom: '16px',
        textAlign: 'center',
      }}>
        <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: '12px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '1px' }}>
          Estimated Price
        </div>
        <div style={{ color: '#FDD835', fontSize: '32px', fontWeight: 800, fontFamily: "'Playfair Display', serif" }}>
          AED {formatPrice(calculatePrice())}
        </div>
        <button
          onClick={() => setShowBreakdown(!showBreakdown)}
          style={{
            background: 'rgba(255,255,255,0.1)',
            border: 'none',
            color: 'rgba(255,255,255,0.8)',
            fontSize: '11px',
            cursor: 'pointer',
            padding: '4px 12px',
            borderRadius: '20px',
            marginTop: '4px',
          }}
        >
          {showBreakdown ? 'Hide breakdown' : 'Show breakdown'} ▼
        </button>

        {showBreakdown && (
          <div style={{ marginTop: '12px', textAlign: 'left', fontSize: '12px', color: 'rgba(255,255,255,0.8)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
              <span>{packages[packageType].label} ({area} m² × AED {packages[packageType].rate})</span>
              <span>AED {formatPrice(area * packages[packageType].rate)}</span>
            </div>
            {hasWaterFeature && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <span>💧 Water Feature</span>
                <span>AED {formatPrice(area * 15)}</span>
              </div>
            )}
            {hasLighting && (
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0' }}>
                <span>💡 Outdoor Lighting</span>
                <span>AED {formatPrice(area * 10)}</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ display: 'flex', gap: '10px' }}>
        <button
          onClick={handleRequestQuote}
          style={{
            flex: 1,
            padding: '12px',
            background: 'linear-gradient(135deg, #25D366, #128C7E)',
            color: '#fff',
            border: 'none',
            borderRadius: '50px',
            fontWeight: 700,
            fontSize: '14px',
            cursor: 'pointer',
            boxShadow: '0 8px 24px rgba(37,211,102,0.3)',
            transition: 'all 0.3s ease',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'scale(1.02)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          <FaWhatsapp /> Get Detailed Quote
        </button>
        <button
          onClick={() => {
            setArea(50);
            setPackageType('basic');
            setHasWaterFeature(false);
            setHasLighting(false);
          }}
          style={{
            padding: '12px 16px',
            background: 'transparent',
            color: '#666',
            border: '2px solid #E8F5E9',
            borderRadius: '50px',
            fontWeight: 600,
            fontSize: '13px',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
          }}
        >
          Reset
        </button>
      </div>

      <p style={{ fontSize: '11px', color: '#999', marginTop: '12px', textAlign: 'center' }}>
        ⚡ This is a rough estimate. Final quote depends on site visit and material selection.
      </p>
    </div>
  );
}

// ============================================================
// SECTION COMPONENTS
// ============================================================

// 1. HERO SECTION
function HeroSection({ slideIdx, setSlideIdx }) {
    const [isMobile, setIsMobile] = useState(false);
    
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    return (
        <section style={{
            ...styles.hero,
            position: 'relative',
            padding: isMobile ? '60px 0 30px' : '80px 0',
            overflow: 'hidden',
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            background: '#0d3320',
        }}>
            <div style={{
                position: 'absolute',
                top: isMobile ? '100px' : '40px',
                left: isMobile ? '5%' : '34%',
                transform: 'translateX(-50%)',
                zIndex: 10,
                textAlign: 'center',
                pointerEvents: 'none',
                opacity: 0,
                animation: 'slideFromBottom 1s ease forwards 0.3s',
                width: isMobile ? '100%' : 'auto',
                padding: isMobile ? '0 12px' : '0',
            }}>
  <span
  style={{
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: isMobile ? 'clamp(18px, 4.5vw, 24px)' : '40px',
    fontWeight: 600,
    letterSpacing: isMobile ? '2px' : '6px',
    textTransform: 'uppercase',
    background: 'linear-gradient(135deg, #ffffff 0%, #f4e4bc 60%, #c9a03d 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    filter: 'drop-shadow(0 2px 10px rgba(0,0,0,0.3))',
    display: 'block',
    whiteSpace: 'nowrap',
  }}
>
  Nasmat Al Zohour
</span>

<span
  style={{
    fontFamily: "'Playfair Display', Georgia, serif",
    fontSize: isMobile ? 'clamp(16px, 4vw, 22px)' : '20px',
    fontWeight: 500,
    letterSpacing: isMobile ? '1px' : '4px',
    textTransform: 'uppercase',
    background: 'linear-gradient(135deg, #ffffff 0%, #f4e4bc 60%, #c9a03d 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    filter: 'drop-shadow(0 2px 10px rgba(0,0,0,0.3))',
    display: 'block',
    // marginTop: '5px',
    whiteSpace: 'nowrap',
  }}
>
  Nursery and Landscaping
</span>
	
                <div style={{
                    width: isMobile ? '10px' : '40px',
                    height: '1px',
                    background: 'linear-gradient(90deg, transparent, #c9a03d, transparent)',
                    margin: isMobile ? '4px auto 0' : '8px auto 0',
                }} />
            </div>

            <div style={{ 
                position: 'absolute', 
                inset: 0, 
                zIndex: 0, 
                overflow: 'hidden',
                background: '#0d3320',
                width: '100%',
                height: '100%',
                minHeight: '100vh',
            }}>
                {SLIDES.map((slide, i) => (
                    <div
                        key={i}
                        style={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            backgroundImage: `url(${slide.src})`,
                            backgroundSize: isMobile ? 'cover' : (i === slideIdx ? '115%' : '100%'),
                            backgroundPosition: isMobile ? 'center 30%' : 'center center',
                            backgroundRepeat: 'no-repeat',
                            opacity: i === slideIdx ? 1 : 0,
                            transition: isMobile 
                                ? 'opacity 1.5s ease-in-out' 
                                : 'opacity 1.5s ease-in-out, background-size 10s ease-in-out, transform 10s ease-in-out',
                            willChange: isMobile ? 'opacity' : 'opacity, background-size, transform',
                            transform: (!isMobile && i === slideIdx) ? 'scale(1.02)' : 'scale(1)',
                        }}
                    />
                ))}

                <div style={{
                    position: 'absolute',
                    inset: 0,
                    zIndex: 1,
                    background: isMobile 
                        ? 'linear-gradient(180deg, rgba(13,51,32,0.2) 0%, rgba(13,51,32,0.6) 40%, rgba(13,51,32,0.9) 100%)'
                        : 'radial-gradient(ellipse at center, rgba(13,51,32,0.2) 0%, rgba(13,51,32,0.6) 60%, rgba(13,51,32,0.8) 100%)',
                    pointerEvents: 'none',
                }} />

                {!isMobile && (
                    <div style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 1,
                        pointerEvents: 'none',
                        overflow: 'hidden',
                        opacity: 0.15,
                    }}>
                        <div style={{
                            position: 'absolute',
                            width: '200%',
                            height: '200%',
                            top: '-50%',
                            left: '-50%',
                            animation: 'driftLeaves 25s linear infinite',
                            backgroundImage: `
                                radial-gradient(circle at 10% 20%, rgba(165,214,167,0.3) 0%, transparent 8%),
                                radial-gradient(circle at 30% 60%, rgba(165,214,167,0.2) 0%, transparent 10%),
                                radial-gradient(circle at 50% 10%, rgba(165,214,167,0.25) 0%, transparent 12%),
                                radial-gradient(circle at 70% 70%, rgba(165,214,167,0.15) 0%, transparent 9%),
                                radial-gradient(circle at 90% 30%, rgba(165,214,167,0.2) 0%, transparent 11%),
                                radial-gradient(circle at 20% 80%, rgba(165,214,167,0.1) 0%, transparent 14%),
                                radial-gradient(circle at 60% 40%, rgba(165,214,167,0.2) 0%, transparent 8%),
                                radial-gradient(circle at 80% 90%, rgba(165,214,167,0.15) 0%, transparent 10%)
                            `,
                            backgroundSize: '100% 100%',
                            backgroundRepeat: 'no-repeat',
                        }} />

                        {['🍃', '🌿', '🍂', '🌱', '🍀'].map((leaf, i) => (
                            Array.from({ length: 15 }).map((_, j) => {
                                const left = Math.random() * 100;
                                const delay = Math.random() * 20;
                                const duration = 18 + Math.random() * 22;
                                const size = 12 + Math.random() * 20;
                                return (
                                    <div
                                        key={`${i}-${j}`}
                                        style={{
                                            position: 'absolute',
                                            left: `${left}%`,
                                            top: `${Math.random() * 100}%`,
                                            fontSize: `${size}px`,
                                            color: `rgba(165,214,167,${0.08 + Math.random() * 0.12})`,
                                            pointerEvents: 'none',
                                            animation: `floatLeafPattern ${duration}s ease-in-out infinite`,
                                            animationDelay: `${delay}s`,
                                            transform: `translateX(0px) rotate(${Math.random() * 360}deg)`,
                                            opacity: 0.3 + Math.random() * 0.4,
                                        }}
                                    >
                                        {leaf}
                                    </div>
                                );
                            })
                        ))}
                    </div>
                )}

                {!isMobile && (
                    <>
                        <div style={{
                            position: 'absolute',
                            top: '10%',
                            right: '5%',
                            zIndex: 1,
                            fontSize: '60px',
                            color: 'rgba(201,160,61,0.06)',
                            pointerEvents: 'none',
                            fontFamily: "'Playfair Display', serif",
                            animation: 'spinSlow 30s linear infinite',
                        }}>✦</div>
                        <div style={{
                            position: 'absolute',
                            bottom: '15%',
                            left: '3%',
                            zIndex: 1,
                            fontSize: '80px',
                            color: 'rgba(165,214,167,0.04)',
                            pointerEvents: 'none',
                            fontFamily: "'Playfair Display', serif",
                            animation: 'spinSlow 40s linear infinite reverse',
                        }}>✧</div>
                    </>
                )}
            </div>

            <div style={{
                ...styles.heroContentContainer,
                padding: isMobile ? '0 12px' : '0 5%',
                marginTop: isMobile ? '20px' : '40px',
            }}>
                <Row className="justify-content-center">
                    <Col lg={9} xl={8} className="text-center" style={{ 
                        zIndex: 5,
                        padding: isMobile ? '0 4px' : '0',
                    }}>
                        <div 
                            key={slideIdx}
                            className="hero-content-wrapper"
                            style={{
                                animation: 'heroContentFadeOut 0.4s ease forwards, heroContentFadeIn 0.8s ease forwards 0.5s',
                            }}
                        >
                            <div 
                                style={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: isMobile ? '5px' : '10px',
                                    background: 'rgba(255,255,255,0.08)',
                                    backdropFilter: 'blur(12px)',
                                    border: '1px solid rgba(255,255,255,0.15)',
                                    borderRadius: '50px',
                                    padding: isMobile ? '4px 12px 4px 8px' : '8px 22px 8px 18px',
                                    marginBottom: isMobile ? '14px' : '28px',
                                    marginLeft: 'auto',
                                    marginRight: 'auto',
                                    boxShadow: '0 0 40px rgba(45,125,70,0.15)',
                                    opacity: 0,
                                    transform: isMobile ? 'translateX(0)' : 'translateX(-60px)',
                                    animation: 'slideFromLeft 0.8s ease forwards',
                                    animationDelay: '0.6s',
                                    maxWidth: isMobile ? '200px' : '280px',
                                    width: 'auto',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                <span style={{
                                    display: 'inline-block',
                                    width: isMobile ? '5px' : '8px',
                                    height: isMobile ? '5px' : '8px',
                                    borderRadius: '50%',
                                    background: '#4ADE80',
                                    animation: 'pulseDot 2s ease-in-out infinite',
                                    flexShrink: 0,
                                }} />
                                <span style={{
                                    color: 'rgba(255,255,255,0.9)',
                                    fontSize: isMobile ? 'clamp(8px, 2vw, 10px)' : '12px',
                                    fontWeight: 600,
                                    letterSpacing: isMobile ? '0.5px' : '2px',
                                    textTransform: 'uppercase',
                                    fontFamily: "'Inter', sans-serif",
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    textAlign: 'center',
                                }}>
                                    <FaLeaf style={{ marginRight: isMobile ? 3 : 8, color: '#4ADE80', fontSize: isMobile ? '9px' : '12px' }} /> 
                                    {SLIDES[slideIdx].caption}
                                </span>
                            </div>
                            
                            <h1 
                                style={{ 
                                    fontFamily: "'Playfair Display', Georgia, serif",
                                    fontSize: isMobile 
                                        ? 'clamp(1.6rem, 7vw, 2.4rem)' 
                                        : 'clamp(2.5rem, 6vw, 4.5rem)',
                                    fontWeight: 800,
                                    lineHeight: isMobile ? '1.1' : '1.05',
                                    marginBottom: isMobile ? '6px' : '12px',
                                    background: 'linear-gradient(135deg, #ffffff 0%, #f4e4bc 40%, #c9a03d 70%, #ffffff 100%)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                    backgroundSize: '200% 200%',
                                    textShadow: 'none',
                                    filter: 'drop-shadow(0 2px 20px rgba(201,160,61,0.15))',
                                    opacity: 0,
                                    transform: isMobile ? 'translateX(0)' : 'translateX(-80px)',
                                    animation: 'gradientShift 4s ease-in-out infinite, slideFromLeft 0.9s ease forwards',
                                    animationDelay: '0s, 0.7s',
                                }}
                            >
                                {SLIDES[slideIdx].title}
                            </h1>
                            
                            <p 
                                style={{ 
                                    fontFamily: "'Playfair Display', Georgia, serif",
                                    fontSize: isMobile 
                                        ? 'clamp(0.7rem, 2.5vw, 1rem)' 
                                        : 'clamp(1rem, 2vw, 1.5rem)',
                                    color: 'rgba(255,255,255,0.5)',
                                    fontWeight: 400,
                                    fontStyle: 'italic',
                                    marginBottom: isMobile ? '8px' : '20px',
                                    letterSpacing: isMobile ? '0.5px' : '2px',
                                    opacity: 0,
                                    transform: isMobile ? 'translateX(0)' : 'translateX(-60px)',
                                    animation: 'slideFromLeft 0.8s ease forwards',
                                    animationDelay: '0.8s',
                                }}
                            >
                                {SLIDES[slideIdx].sub}
                            </p>
                            
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: isMobile ? '8px' : '16px',
                                marginBottom: isMobile ? '10px' : '24px',
                                opacity: 0,
                                transform: isMobile ? 'translateX(0)' : 'translateX(-40px)',
                                animation: 'slideFromLeft 0.7s ease forwards',
                                animationDelay: '0.9s',
                            }}>
                                <div style={{
                                    width: isMobile ? '20px' : '60px',
                                    height: '1px',
                                    background: 'linear-gradient(90deg, transparent, rgba(201,160,61,0.5))',
                                }} />
                                <span style={{ color: '#c9a03d', fontSize: isMobile ? '12px' : '18px' }}>✦</span>
                                <div style={{
                                    width: isMobile ? '20px' : '60px',
                                    height: '1px',
                                    background: 'linear-gradient(90deg, rgba(201,160,61,0.5), transparent)',
                                }} />
                            </div>
                            
                            <p style={{
                                fontFamily: "'Inter', sans-serif",
                                fontSize: isMobile 
                                    ? 'clamp(0.75rem, 2vw, 0.9rem)' 
                                    : 'clamp(1rem, 1.4vw, 1.15rem)',
                                color: 'rgba(255,255,255,0.8)',
                                fontWeight: 300,
                                lineHeight: isMobile ? '1.5' : '1.8',
                                maxWidth: isMobile ? '100%' : '620px',
                                margin: '0 auto',
                                letterSpacing: '0.3px',
                                opacity: 0,
                                transform: 'translateY(40px)',
                                animation: 'slideFromBottom 0.8s ease forwards',
                                animationDelay: '1.0s',
                            }}>
                                {SLIDES[slideIdx].description}
                            </p>
                            
                            <div style={{ 
                                display: 'flex',
                                gap: isMobile ? '8px' : '16px',
                                justifyContent: 'center',
                                flexWrap: 'wrap',
                                marginTop: isMobile ? '16px' : '32px',
                                opacity: 0,
                                transform: 'translateY(50px)',
                                animation: 'slideFromBottom 0.9s ease forwards',
                                animationDelay: '1.2s',
                                flexDirection: isMobile ? 'column' : 'row',
                                alignItems: 'center',
                                width: '100%',
                            }}>
                                <a 
                                    href="#featured-plants" 
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        background: 'linear-gradient(135deg, #c9a03d, #b8860b)',
                                        color: '#fff',
                                        padding: isMobile ? '10px 16px' : '14px 34px',
                                        borderRadius: '50px',
                                        fontWeight: 700,
                                        fontSize: isMobile ? 'clamp(11px, 2.5vw, 13px)' : '14px',
                                        textDecoration: 'none',
                                        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                        boxShadow: '0 8px 32px rgba(201,160,61,0.35)',
                                        fontFamily: "'Inter', sans-serif",
                                        letterSpacing: '0.5px',
                                        width: isMobile ? '100%' : 'auto',
                                        maxWidth: isMobile ? '280px' : 'auto',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isMobile) {
                                            e.currentTarget.style.transform = 'translateY(-4px) scale(1.03)';
                                            e.currentTarget.style.boxShadow = '0 12px 48px rgba(201,160,61,0.5)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isMobile) {
                                            e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                            e.currentTarget.style.boxShadow = '0 8px 32px rgba(201,160,61,0.35)';
                                        }
                                    }}
                                >
                                    <FaArrowRight style={{ fontSize: isMobile ? '11px' : '14px' }} />
                                    {SLIDES[slideIdx].cta}
                                </a>
                                
                                <a 
                                    href="https://wa.me/971552698951" 
                                    target="_blank" 
                                    rel="noreferrer"
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '8px',
                                        background: 'rgba(255,255,255,0.1)',
                                        backdropFilter: 'blur(12px)',
                                        border: '1.5px solid rgba(255,255,255,0.25)',
                                        color: '#fff',
                                        padding: isMobile ? '10px 16px' : '14px 34px',
                                        borderRadius: '50px',
                                        fontWeight: 600,
                                        fontSize: isMobile ? 'clamp(11px, 2.5vw, 13px)' : '14px',
                                        textDecoration: 'none',
                                        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                        fontFamily: "'Inter', sans-serif",
                                        letterSpacing: '0.5px',
                                        width: isMobile ? '100%' : 'auto',
                                        maxWidth: isMobile ? '280px' : 'auto',
                                    }}
                                    onMouseEnter={(e) => {
                                        if (!isMobile) {
                                            e.currentTarget.style.transform = 'translateY(-4px) scale(1.03)';
                                            e.currentTarget.style.background = 'rgba(37,211,102,0.2)';
                                            e.currentTarget.style.borderColor = '#25D366';
                                            e.currentTarget.style.boxShadow = '0 8px 32px rgba(37,211,102,0.2)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        if (!isMobile) {
                                            e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                            e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                                            e.currentTarget.style.borderColor = 'rgba(255,255,255,0.25)';
                                            e.currentTarget.style.boxShadow = 'none';
                                        }
                                    }}
                                >
                                    <FaWhatsapp style={{ fontSize: isMobile ? '15px' : '18px', color: '#25D366' }} />
                                    WhatsApp Us
                                </a>
                            </div>
                        </div>
                    </Col>
                </Row>
            </div>
        </section>
    );
}

// 2. ABOUT SECTION

function AboutSection() {
    return (
        <section id="about-section" style={{
            padding: '10px 0',
            background: '#f9fdf6',
            position: 'relative',
            overflow: 'hidden',

            transform: 'translateY(0)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
        }}>
            <div style={{
                position: 'absolute',
                top: '-200px',
                right: '-200px',
                width: '500px',
                height: '500px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(45,125,70,0.03) 0%, transparent 70%)',
                pointerEvents: 'none',
            }} />
            <div style={{
                position: 'absolute',
                bottom: '-100px',
                left: '-100px',
                width: '300px',
                height: '300px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(201,160,61,0.03) 0%, transparent 70%)',
                pointerEvents: 'none',
            }} />
            <Container>
                <Row className="align-items-center g-5">
                    <Col lg={6} md={12}>
                        <div style={{
                            position: 'relative',
                            borderRadius: '24px',
                            overflow: 'hidden',
                            boxShadow: '0 30px 80px rgba(0,0,0,0.15), 0 10px 30px rgba(0,0,0,0.05)',
                            transform: 'perspective(1000px) rotateY(-3deg) rotateX(2deg)',
                            transition: 'transform 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                            cursor: 'pointer',
                            maxHeight: '550px',
                            display: 'block',
                        }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'perspective(1000px) rotateY(-5deg) rotateX(3deg) scale(1.02)';
                                e.currentTarget.style.boxShadow = '0 40px 100px rgba(0,0,0,0.2)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'perspective(1000px) rotateY(-3deg) rotateX(2deg) scale(1)';
                                e.currentTarget.style.boxShadow = '0 30px 80px rgba(0,0,0,0.15)';
                            }}
                        >
                            <LazyImage
                                src={aboutImage}
                                alt="About Nasmat Al Zohour - Nursery & Landscaping"
                                sectionKey="about"
                                style={{
                                    width: '100%',
                                    height: '550px',
                                    objectFit: 'cover',
                                    display: 'block',
                                    transition: 'transform 0.8s ease',
                                }}
                                onError={(e) => {
                                    e.target.src = 'https://images.pexels.com/photos/1595696/pexels-photo-1595696.jpeg?auto=compress&cs=tinysrgb&w=1200';
                                }}
                            />
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'linear-gradient(135deg, rgba(45,125,70,0.1) 0%, transparent 50%, rgba(201,160,61,0.05) 100%)',
                                pointerEvents: 'none',
                            }} />
                            <div style={{
                                position: 'absolute',
                                top: '-50%',
                                right: '-50%',
                                width: '100%',
                                height: '100%',
                                background: 'linear-gradient(135deg, transparent 40%, rgba(255,255,255,0.1) 50%, transparent 60%)',
                                transform: 'rotate(45deg)',
                                pointerEvents: 'none',
                                animation: 'shineMove 8s ease-in-out infinite',
                            }} />
                            <div style={{
                                position: 'absolute',
                                bottom: '12px',
                                left: '12px',
                                background: 'rgba(255, 255, 255, 0.95)',
                                backdropFilter: 'blur(8px)',
                                padding: '5px 10px',
                                borderRadius: '30px',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '6px',
                                // Designed Border: Soft gradient border effect
                                border: '1.5px solid transparent',
                                backgroundImage: 'linear-gradient(rgba(255,255,255,0.95), rgba(255,255,255,0.95)), linear-gradient(135deg, #2D7D46, #c9a03d)',
                                backgroundOrigin: 'border-box',
                                backgroundClip: 'padding-box, border-box',
                                boxShadow: '0 4px 12px rgba(45, 125, 70, 0.15)', // Styled green-tinted shadow
                            }}>
                                <div style={{
                                    background: 'linear-gradient(135deg, #2D7D46, #4CAF50)',
                                    borderRadius: '50%',
                                    width: '20px',
                                    height: '20px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#fff',
                                    fontSize: '10px',
                                }}>
                                    <FaTree />
                                </div>
                                <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                    <div style={{
                                        fontSize: '7px',
                                        color: '#777',
                                        fontWeight: 700,
                                        letterSpacing: '0.3px',
                                        lineHeight: '1',
                                        textTransform: 'uppercase',
                                    }}>
                                        Since 1985
                                    </div>
                                    <div style={{
                                        fontSize: '9px',
                                        fontWeight: 800,
                                        color: '#1B5E20',
                                        lineHeight: '1.1',
                                        whiteSpace: 'nowrap',
                                    }}>
                                        500+ Happy Clients
                                    </div>
                                </div>
                            </div>
                            <div style={{
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                width: '80px',
                                height: '80px',
                                borderTop: '4px solid #c9a03d',
                                borderLeft: '4px solid #c9a03d',
                                borderTopLeftRadius: '24px',
                                pointerEvents: 'none',
                                opacity: 0.5,
                            }} />
                        </div>
                    </Col>
                    <Col lg={6} md={12}>
                        <div style={{
                            padding: '20px 0',
                        }}>
                            <div style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '10px',
                                background: 'rgba(45,125,70,0.08)',
                                padding: '6px 18px 6px 14px',
                                borderRadius: '50px',
                                marginBottom: '20px',
                                border: '1px solid rgba(45,125,70,0.1)',
                            }}>
                                <span style={{
                                    display: 'inline-block',
                                    width: '6px',
                                    height: '6px',
                                    borderRadius: '50%',
                                    background: '#2D7D46',
                                    animation: 'pulseDot 2s ease-in-out infinite',
                                }} />
                                <span style={{
                                    fontSize: '11px',
                                    fontWeight: 700,
                                    color: '#2D7D46',
                                    letterSpacing: '2px',
                                    textTransform: 'uppercase',
                                    fontFamily: "'Inter', sans-serif",
                                }}>
                                    About Our Nursery
                                </span>
                            </div>
                            <h2 style={{
                                fontFamily: "'Playfair Display', Georgia, serif",
                                fontSize: 'clamp(2rem, 4vw, 3rem)',
                                fontWeight: 800,
                                color: '#1B5E20',
                                lineHeight: '1.2',
                                marginBottom: '20px',
                            }}>
                                Bringing Nature Into <br />
                                <span style={{
                                    background: 'linear-gradient(135deg, #2D7D46, #c9a03d)',
                                    WebkitBackgroundClip: 'text',
                                    WebkitTextFillColor: 'transparent',
                                }}>
                                    Everyday Spaces
                                </span>
                            </h2>
                            <div style={{
                                width: '80px',
                                height: '3px',
                                background: 'linear-gradient(90deg, #2D7D46, #c9a03d)',
                                borderRadius: '2px',
                                marginBottom: '24px',
                            }} />
                            <div style={{ position: "relative", overflow: "hidden" }}>
                                <img loading="lazy"
                                    src={emiratiimg}
                                    alt="Background"
                                    style={{
                                        position: "absolute",
                                        top: "10%",
                                        left: "48%",
                                        width: "55%",
                                        height: "118%",
                                        objectFit: "cover",
                                        opacity: 0.25,
                                        zIndex: 0,
                                    }}
                                />

                                <p
                                    style={{
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: "clamp(0.95rem, 1.2vw, 1.05rem)",
                                        color: "#040202",
                                        lineHeight: "1.9",
                                        marginBottom: "16px",
                                    }}
                               >

                                 <strong style={{ color: "#1A5C21" }}>Nasmat Al Zohour</strong> is a trusted garden centre and landscape
                                    solutions provider, bringing nature into everyday spaces since 1985. Founded under the vision and leadership of  <strong style={{ color: "#1A5C21" }}>Mr. Saeed Mohamed Nasser</strong>, we are committed to
                                    quality, innovation, and exceptional customer service, creating beautiful
                                    and sustainable green spaces.

                                </p>

                                <p
                                    style={{
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: "clamp(0.95rem, 1.2vw, 1.05rem)",
                                        color: "#040303",
                                        lineHeight: "1.9",
                                    }}
                                >
                                    Catering to both <strong style={{ color: "#4E8B5A" }}>residential</strong>{" "}
                                    and <strong style={{ color: "#4E8B5A" }}>commercial</strong> clients, we
                                    specialize in high-quality indoor and outdoor ornamental plants, as well as
                                    customized landscaping services tailored to urban and semi-urban
                                    environments.
                                </p>
                            </div>
                            <Row className="g-3 mb-4">
                                {[
                                    { number: '1985', label: 'Established', icon: '🌱' },
                                    { number: '500+', label: 'Happy Clients', icon: '😊' },
                                    { number: '50+', label: 'Plant Varieties', icon: '🌿' },
                                    { number: '4.9★', label: 'Rating', icon: '⭐' },
                                ].map((stat, idx) => (
                                    <Col key={idx} xs={6} md={3}>
                                        <div style={{
                                            textAlign: 'center',
                                            padding: '16px 8px',
                                            background: '#fff',
                                            borderRadius: '16px',
                                            border: '1px solid #f0f0f0',
                                            transition: 'all 0.3s ease',
                                            boxShadow: '0 2px 10px rgba(0,0,0,0.02)',
                                        }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-4px)';
                                                e.currentTarget.style.boxShadow = '0 8px 25px rgba(45,125,70,0.08)';
                                                e.currentTarget.style.borderColor = '#A5D6A7';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = '0 2px 10px rgba(0,0,0,0.02)';
                                                e.currentTarget.style.borderColor = '#f0f0f0';
                                            }}
                                        >
                                            <div style={{
                                                fontSize: '24px',
                                                marginBottom: '4px',
                                            }}>
                                                {stat.icon}
                                            </div>
                                            <div style={{
                                                fontSize: '20px',
                                                fontWeight: 800,
                                                color: '#1B5E20',
                                                lineHeight: '1.2',
                                            }}>
                                                {stat.number}
                                            </div>
                                            <div style={{
                                                fontSize: '11px',
                                                color: '#999',
                                                fontWeight: 500,
                                            }}
                                            >
                                                {stat.label}
                                            </div>
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    </Col>
                </Row>
            </Container>
        </section>
    );
}



// 3. MARQUEE BAR
function MarqueeBar() {
    const items = ['🌿 Flowering Plants', '🌵 Succulents', '🍋 Fruit Trees', '🌿 Indoor Plants', '🌿 Fresh Herbs', '🌸 Bougainvillea', '🪴 Rare Exotics', '🌴 Palm Trees', '🌻 Sunflowers', '🌹 Roses'];
    
    return (
        <div style={styles.marqueeBar}>
            <div style={styles.marqueeTrack}>
                {[...items, ...items].map((item, i) => (
                    <span key={i} style={styles.marqueeItem}>{item}</span>
                ))}
            </div>
        </div>
    );
}

// 4. FEATURE PILLS


// 4. FEATURE PILLS
function FeaturePills({ featuresRef, featuresVis, hoveredPill, setHoveredPill, visible }) {
    // Rearranged: 'Healthy Guarantee' is now exactly in the center (index 2 out of 5)
    // Updated: 'Customer Service' changed to 'Quality Check' with a matching verification icon
    const features = [
        { icon: <FaLeaf size={24} />, label: 'Eco Friendly', sub: 'Sustainable Practices' },
        { icon: <FaCheckCircle size={24} />, label: 'Quality Check', sub: 'Strict Plant Audits' }, 
        { 
            // The centerpiece icon styled inside a stylized modern wheel element
            icon: (
                <div style={{ position: 'relative', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {/* Rotating Wheel / Sunburst Outer Layer */}
                    <div style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        border: '3px dashed #c9a03d',
                        borderRadius: '50%',
                        animation: 'spin 20s linear infinite',
                    }} />
                    <FaShieldAlt size={22} style={{ zIndex: 1 }} />
                </div>
            ), 
            label: 'Healthy Guarantee', 
            sub: '100% Pest-Free Plants' 
        },
        { icon: <FaTree size={24} />, label: 'Landscaping', sub: 'Professional Garden Design' },
        { icon: <FaUsers size={24} />, label: 'Community', sub: 'Plant Lovers Club' },
    ];

    const [isDesktop, setIsDesktop] = useState(false);

    useEffect(() => {
        const handleResize = () => setIsDesktop(window.innerWidth >= 992);
        handleResize();
        window.addEventListener('resize', handleResize);
        
        // Injecting basic keyframe for the center wheel spinning animation if it doesn't exist
        if (!document.getElementById('wheel-spin-style')) {
            const style = document.createElement('style');
            style.id = 'wheel-spin-style';
            style.innerHTML = `@keyframes spin { 100% { transform: rotate(360deg); } }`;
            document.head.appendChild(style);
        }

        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const globalFontFamily = "'Inter', 'Segoe UI', system-ui, sans-serif";

    return (
        <section ref={featuresRef} style={{ 
            opacity: visible ? 1 : 0, 
            transform: visible ? 'translateY(0)' : 'translateY(40px)', 
            transition: 'opacity 0.7s ease, transform 0.7s ease',
            padding: '40px 0'
        }}>
            <Container fluid style={{ paddingLeft: '15px', paddingRight: '15px' }}>
                <Row className="justify-content-center align-items-center g-2 g-md-3">
                    {features.map((f, i) => {
                        const isCenter = i === 2; // Index 2 is the exact center element

                        return (
                            <Col 
                                key={i} 
                                xs={isCenter ? 12 : 6}   // Gives full width prominence to the center wheel on mobile
                                sm={isCenter ? 12 : 6}   
                                md={isCenter ? 3 : 3}   
                                style={{
                                    paddingLeft: '4px',
                                    paddingRight: '4px',
                                    flex: isDesktop ? '0 0 20%' : undefined,
                                    maxWidth: isDesktop ? '20%' : undefined
                                }}
                                className="d-flex justify-content-center"
                            >
                                <div style={{ 
                                    width: '100%',
                                    maxWidth: isDesktop ? '280px' : '100%', 
                                    padding: isDesktop ? '24px 20px' : '20px 12px',
                                    transitionDelay: `${i * 0.1}s`, 
                                    opacity: visible ? 1 : 0, 
                                    // Scale up the center card slightly more to emphasize the layout structure
                                    transform: hoveredPill === i 
                                        ? 'translateY(-8px) scale(1.06)' 
                                        : isCenter ? 'scale(1.03)' : 'translateY(0)',
                                    boxShadow: hoveredPill === i 
                                        ? '0 16px 40px rgba(45,125,70,0.25)' 
                                        : isCenter ? '0 10px 25px rgba(201,160,61,0.15)' : '0 2px 8px rgba(0,0,0,0.06)',
                                    // Border color: white for all, orange only for center
                                    border: isCenter ? '2px solid #c9a03d' : '2px solid #ffffff',
                                    transition: 'all 0.4s ease',
                                    backgroundColor: isCenter ? '#FFFDF9' : '#fff', // Soft golden tint for the center highlight
                                    borderRadius: '16px',
                                    textAlign: 'center',
                                    cursor: 'pointer',
                                    minHeight: isDesktop ? '150px' : '120px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }} 
                                onMouseEnter={() => setHoveredPill(i)} 
                                onMouseLeave={() => setHoveredPill(null)}
                                >
                                    <div style={{ 
                                        transform: hoveredPill === i ? 'scale(1.2)' : 'scale(1)', 
                                        transition: 'transform 0.3s',
                                        fontSize: isDesktop ? '34px' : '28px',
                                        marginBottom: isDesktop ? '12px' : '8px',
                                        color: isCenter ? '#c9a03d' : '#2D7D46', // Golden theme for the center wheel element
                                        lineHeight: 1,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                    }}>{f.icon}</div>
                                    
                                    <div style={{ 
                                        fontFamily: globalFontFamily,
                                        fontSize: isDesktop ? '17px' : '14px',
                                        fontWeight: '700',
                                        color: isCenter ? '#3E2723' : '#1B3A2D',
                                        marginBottom: isDesktop ? '6px' : '4px',
                                        lineHeight: 1.2,
                                    }}>{f.label}</div>
                                    
                                    <div style={{ 
                                        fontFamily: globalFontFamily,
                                        fontSize: isDesktop ? '13px' : '11px',
                                        color: isCenter ? '#8D6E63' : '#6B8A7A',
                                        fontWeight: '400',
                                        lineHeight: 1.2,
                                    }}>{f.sub}</div>
                                </div>
                            </Col>
                        );
                    })}
                </Row>
            </Container>
        </section>
    );
}



// 5. FEATURED PLANTS
function FeaturedPlants({ loading, data, lightboxImage, setLightboxImage, closeLightbox, visible }) {
    const getPlantList = () => {
        const plants = data?.featured_plants || [];
        if (plants.length === 0) return [];
        return [...plants, ...plants];
    };

    const plantList = getPlantList();

    return (
        <section id="featured-plants-section" style={{ 
            ...styles.sectionLight, 
            background: '#f9fdf6', 
            padding: '40px 0 60px',
            overflow: 'hidden',
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
        }}>
            <div style={{ 
                paddingLeft: '50px', 
                paddingRight: '50px',
                marginBottom: 30 
            }}>
                <div style={{ ...styles.sectionHeader, marginBottom: 4 }}>
                    <h2 style={{ ...styles.sectionTitle, fontSize: '2rem', marginBottom: 4 }}>Featured Plants</h2>
                    <p style={{ ...styles.sectionSub, fontSize: 14, color: '#888' }}>Handpicked selections from our premium collection</p>
                </div>
            </div>
            
            {loading ? (
                <div className="text-center py-5">
                    <Spinner animation="grow" variant="success" />
                    <p className="mt-3 text-muted">Loading…</p>
                </div>
            ) : plantList.length === 0 ? (
                <div style={{ 
                    textAlign: 'center', 
                    padding: '60px 20px',
                    color: '#999'
                }}>
                    <div style={{ fontSize: 48, marginBottom: 16 }}>🌱</div>
                    <p style={{ fontSize: 16 }}>No featured plants available</p>
                </div>
            ) : (
                <div style={{ 
                    overflow: 'hidden', 
                    position: 'relative', 
                    width: '100%', 
                    padding: '10px 0' 
                }}>
                    <div 
                        className="plant-scroll-track"
                        style={{
                            display: 'flex',
                            gap: '16px',
                            animation: 'scrollPlantsRight 60s linear infinite',
                            width: 'max-content',
                            paddingLeft: '50px',
                        }}
                        onMouseEnter={(e) => { 
                            e.currentTarget.style.animationPlayState = 'paused'; 
                        }}
                        onMouseLeave={(e) => { 
                            e.currentTarget.style.animationPlayState = 'running'; 
                        }}
                    >





                        {plantList.map((plant, idx) => {
let imageUrl = plant.image_url;
                            if (imageUrl) {
                                if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
                                    // Keep as is
                                }
                                else if (imageUrl.startsWith('/uploads/')) {
                                    // Keep as is
                                }
                                else if (imageUrl.startsWith('uploads/')) {
                                    imageUrl = `/${imageUrl}`;
                                }
                                else if (imageUrl.match(/\.(jpg|jpeg|png|gif|webp|avif)$/i)) {
                                    imageUrl = `/uploads/${imageUrl}`;
                                }
                                else {
                                    imageUrl = null;
                                }
                            } else {
                                imageUrl = null;
                            }
                            return (
                                <div
                                    key={`${plant.id || idx}-${idx}`}
                                    style={{
                                        flex: '0 0 350px',
                                        width: 350,
                                        height: 250,
                                        borderRadius: 16,
                                        overflow: 'hidden',
                                        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                                        border: '1px solid #f0f0f0',
                                        position: 'relative',
                                        transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                                        cursor: 'pointer',
                                        background: '#f5f8f5'
                                    }}
                                    onClick={() => setLightboxImage({ url: imageUrl, name: plant.name })}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-6px) scale(1.02)';
                                        e.currentTarget.style.boxShadow = '0 12px 40px rgba(45,125,70,0.2)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                        e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.08)';
                                    }}
                                >
                                    {imageUrl ? (
                                        <img loading="lazy"
                                            src={imageUrl}
                                            alt={plant.name}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                display: 'block',
                                                transition: 'transform 0.5s ease'
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'scale(1.08)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'scale(1)';
                                            }}
                                            onError={(e) => {
                                                  const fallback = document.createElement('div');
                                                  fallback.style.cssText = 'width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:48px;background:#f0f5f0;color:#aaa;';
                                                  fallback.textContent = '🌿';
                                                  e.currentTarget.replaceWith(fallback);
                                              }}
                                          />
                                    ) : (
                                        <div style={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            fontSize: 48,
                                            background: '#f0f5f0',
                                            color: '#aaa'
                                        }}>
                                            🌿
                                        </div>
                                    )}
                                    <div style={{ 
                                        position: 'absolute',
                                        bottom: 0,
                                        left: 0,
                                        right: 0,
                                        padding: '16px 16px 14px',
                                        background: 'linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.3) 60%, transparent 100%)',
                                        pointerEvents: 'none'
                                    }}>
                                        <div style={{ 
                                            fontWeight: 600, 
                                            color: '#fff', 
                                            fontSize: 14,
                                            lineHeight: 1.3,
                                            textShadow: '0 1px 4px rgba(0,0,0,0.3)'
                                        }}>
                                            {plant.name || 'Plant'}
                                        </div>
                                        <div style={{ 
                                            fontSize: 11, 
                                            color: 'rgba(255,255,255,0.75)',
                                            marginTop: 2,
                                            textShadow: '0 1px 3px rgba(0,0,0,0.3)'
                                        }}>
                                            {plant.botanical_name || 'Plant'}
                                        </div>
                                        <div style={{ marginTop: '8px' }}>
                                            <PlantCareScheduler plantName={plant.name} plantId={plant.id} />
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {lightboxImage && (
                <div 
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0,0,0,0.92)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '40px 20px',
                        cursor: 'pointer',
                        animation: 'fadeIn 0.3s ease'
                    }}
                    onClick={closeLightbox}
                >
                    <button                        style={{
                            position: 'absolute',
                            top: 20,
                            right: 30,
                            background: 'rgba(255,255,255,0.15)',
                            border: 'none',
                            color: '#fff',
                            fontSize: 32,
                            width: 50,
                            height: 50,
                            borderRadius: '50%',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            transition: 'all 0.3s ease',
                            backdropFilter: 'blur(8px)',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
                            e.currentTarget.style.transform = 'scale(1.1)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
                            e.currentTarget.style.transform = 'scale(1)';
                        }}
                        onClick={(e) => {
                            e.stopPropagation();
                            closeLightbox();
                        }}
                    >
                        ✕
                    </button>

                    <div 
                        style={{
                            maxWidth: '90vw',
                            maxHeight: '90vh',
                            position: 'relative',
                            borderRadius: 12,
                            overflow: 'hidden',
                            boxShadow: '0 30px 80px rgba(0,0,0,0.6)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <img loading="lazy" 
                            src={lightboxImage.url} 
                            alt={lightboxImage.name}
                            style={{
                                maxWidth: '100%',
                                maxHeight: '90vh',
                                objectFit: 'contain',
                                display: 'block',
                            }}
                            onError={(e) => {
                                e.target.src = 'https://via.placeholder.com/800x600/2D7D46/fff?text=Image+Not+Available';
                            }}
                        />
                        
                        <div style={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            padding: '20px 30px',
                            background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, transparent 100%)',
                            color: '#fff',
                        }}>
                            <h3 style={{ 
                                margin: 0, 
                                fontSize: 20, 
                                fontWeight: 700,
                                textShadow: '0 2px 8px rgba(0,0,0,0.3)'
                            }}>
                                {lightboxImage.name}
                            </h3>
                            <p style={{ 
                                margin: '4px 0 0', 
                                fontSize: 14, 
                                opacity: 0.8,
                                textShadow: '0 1px 4px rgba(0,0,0,0.3)'
                            }}>
                                Click anywhere to close
                            </p>
                        </div>
                    </div>
                </div>
            )}

        </section>
    );
}

// ============================================================
// REMAINING SECTION COMPONENTS
// ============================================================

// 6. LANDSCAPING EXCELLENCE
function LandscapingExcellence({ openLandscapeQuote }) {
    const services = [
        { icon: <FaRulerCombined />, title: '3D Garden Design', text: 'Visualize your dream garden with our professional 3D architectural rendering service.' },
        { icon: <FaHardHat />, title: 'Hardscaping', text: 'Expert construction of pathways, water features, stone walls, and pergolas.' },
        { icon: <FaWater />, title: 'Smart Irrigation', text: 'Custom-engineered watering systems designed for maximum efficiency in UAE climates.' },
        { icon: <FaLeaf />, title: 'Softscaping & Turf', text: 'Selection of premium plants and high-quality grass for a lush, vibrant environment.' },
        { icon: <FaLightbulb />, title: 'Outdoor Lighting', text: 'Strategic lighting design to bring your garden to life after the sun goes down.' },
        { icon: <FaAward />, title: 'Estate Maintenance', text: 'Comprehensive care programs to keep your landscaping pristine through every season.' },
    ];

    return (
        <section id="landscaping-section" style={styles.landscapeSection}>
            <Container>
                <div style={styles.landscapeHeader}>
                    <div style={styles.landscapeBadge}>PROFESSIONAL SERVICES</div>
                    <h2 style={{ ...styles.sectionTitle, color: '#fff' }}>Landscaping Excellence</h2>
                    <p style={{ ...styles.sectionSub, color: 'rgba(255,255,255,0.85)' }}>We don't just plant; we create living masterpieces. From conceptual 3D designs to complete outdoor transformations.</p>
                </div>
                <Row className="g-4">
                    {services.map((s, i) => (
                        <Col key={i} md={4}><div style={styles.serviceCard}><div style={styles.serviceIcon}>{s.icon}</div><h4 style={styles.serviceTitle}>{s.title}</h4><p style={styles.serviceText}>{s.text}</p></div></Col>
                    ))}
                </Row>
                <div className="text-center mt-5" style={{ display: 'flex', gap: 16, justifyContent: 'center', flexWrap: 'wrap' }}>
                    <button onClick={() => openLandscapeQuote('landscaping')} className="adv-cta-btn" style={styles.btnSecondary}><FaRulerCombined style={{ marginRight: 8 }} /> Request a Free Quote</button>
                    <a href="https://wa.me/971552698951" className="adv-cta-btn" style={styles.btnSecondary} target="_blank" rel="noreferrer"><FaPhone style={{ marginRight: 8 }} /> Book a Consultation</a>
                    <button 
                        onClick={() => {
                            document.getElementById('quote-calculator')?.scrollIntoView({ behavior: 'smooth' });
                        }}
                        style={{
                            ...styles.btnSecondary,
                            background: '#FDD835',
                            color: '#1B5E20',
                            border: 'none',
                            padding: '14px 30px',
                            borderRadius: '50px',
                            fontWeight: 700,
                            fontSize: 15,
                            cursor: 'pointer',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '8px',
                            transition: 'all 0.3s',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'scale(1.05)';
                            e.currentTarget.style.boxShadow = '0 8px 25px rgba(253,216,53,0.4)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'scale(1)';
                            e.currentTarget.style.boxShadow = 'none';
                        }}
                    >
                        <FaCalculator style={{ marginRight: 8 }} /> Quick Estimate
                    </button>
                </div>
            </Container>
        </section>
    );
}

// 9. PORTFOLIO SECTION
function PortfolioSection({ portfolioProjects, portfolioRef, portfolioVis, openLandscapeQuote, visible }) {
    return (
        <section id="portfolio-section" ref={portfolioRef} style={{
            ...styles.portfolioSection,
            background: '#f5faf5',
            padding: '70px 0',
            position: 'relative',
            overflow: 'hidden',
            opacity: visible ? 1 : 0,
            transform: visible ? 'translateY(0)' : 'translateY(40px)',
            transition: 'opacity 0.7s ease, transform 0.7s ease',
        }}>
            <div style={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: '400px',
                height: '400px',
                background: 'radial-gradient(circle, rgba(45,125,70,0.03) 0%, transparent 70%)',
                pointerEvents: 'none',
            }} />
            
            <Container>
                <div style={{ 
                    ...styles.sectionHeader, 
                    opacity: portfolioVis ? 1 : 0, 
                    transform: portfolioVis ? 'translateY(0)' : 'translateY(30px)', 
                    transition: 'all 0.8s ease' 
                }}>
                    <div style={{
                        display: 'inline-block',
                        padding: '8px 24px',
                        background: 'linear-gradient(135deg, #2D7D46, #388E3C)',
                        borderRadius: '50px',
                        marginBottom: '16px',
                    }}>
                        <FaPalette size={18} color="#fff" style={{ marginRight: 8 }} />
                        <span style={{ color: '#fff', fontSize: '12px', fontWeight: 700, letterSpacing: '2px' }}>
                            PORTFOLIO
                        </span>
                    </div>
                    <h2 style={{ 
                        ...styles.sectionTitle, 
                        color: '#1B5E20',
                        fontSize: 'clamp(2rem, 4vw, 2.8rem)',
                    }}>
                        Landscaping Portfolio
                    </h2>
                    <p style={{ 
                        ...styles.sectionSub, 
                        color: '#555',
                        maxWidth: '600px',
                    }}>
                        Hover over any project to see full details, then request a quote for a similar look.
                    </p>
                </div>
                
                <Row className="g-4">
                    {portfolioProjects.map((project, idx) => (
                        <Col key={project.id || idx} md={4} sm={6}>
                            <PortfolioHoverCard
                                project={project}
                                visible={portfolioVis}
                                idx={idx}
                                onQuoteClick={(p) => openLandscapeQuote('landscaping', p.title)}
                            />
                        </Col>
                    ))}
                </Row>
                
                <div style={{ 
                    textAlign: 'center', 
                    marginTop: '40px' 
                }}>
                    <button
                        onClick={() => openLandscapeQuote('landscaping')}
                        style={{
                            padding: '14px 40px',
                            background: 'linear-gradient(135deg, #2D7D46, #388E3C)',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '50px',
                            fontWeight: 700,
                            fontSize: '14px',
                            cursor: 'pointer',
                            boxShadow: '0 8px 25px rgba(45,125,70,0.25)',
                            transition: 'all 0.3s ease',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '10px',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-3px) scale(1.02)';
                            e.currentTarget.style.boxShadow = '0 12px 35px rgba(45,125,70,0.35)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0) scale(1)';
                            e.currentTarget.style.boxShadow = '0 8px 25px rgba(45,125,70,0.25)';
                        }}
                    >
                        <FaArrowRight /> Get a Free Quote
                    </button>
                </div>
            </Container>
        </section>
    );
}

// 8. BEFORE AFTER
function BeforeAfter({ baRef, baVis, baAfter, setBaAfter, openLandscapeQuote }) {
    const lightColors = {
        pureWhite: '#FFFFFF',
        softMint: '#F0F7F2',
        lightGreen: '#E8F5E9',
        sageGreen: '#C8E6C9',
        warmCream: '#FAF8F5',
        softGold: '#FFF8E1'
    };

    return (
        <section id="before-after-section" ref={baRef} style={{ ...styles.baSection, background: '#f5f9f5' }}>
            <Container>
                <div style={{ ...styles.sectionHeader, opacity: baVis ? 1 : 0, transform: baVis ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease' }}>
                    <div style={{ ...styles.landscapeBadge, background: '#2D7D46', color: '#fff', marginBottom: 16 }}>TRANSFORMATION</div>
                    <h2 style={styles.sectionTitle}>The Power of Professional Landscaping</h2>
                    <p style={styles.sectionSub}>See how we turn ordinary spaces into extraordinary living environments.</p>
                    <div style={styles.baBtnGroup}>
                        <button onClick={() => setBaAfter(false)} style={{ ...styles.baToggleBtn, background: !baAfter ? '#2D7D46' : '#f0f0f0', color: !baAfter ? '#fff' : '#999', boxShadow: !baAfter ? '0 8px 25px rgba(45,125,70,0.35)' : 'none', transform: !baAfter ? 'scale(1.05)' : 'scale(1)' }}>Before</button>
                        <button onClick={() => setBaAfter(true)} style={{ ...styles.baToggleBtn, background: baAfter ? '#2D7D46' : '#f0f0f0', color: baAfter ? '#fff' : '#999', boxShadow: baAfter ? '0 8px 25px rgba(45,125,70,0.35)' : 'none', transform: baAfter ? 'scale(1.05)' : 'scale(1)' }}>After</button>
                    </div>
                </div>
                <Row className="align-items-stretch g-4">
                    <Col lg={7}>
                        <div style={{ ...styles.baFrame, background: '#ffffff', opacity: baVis ? 1 : 0, transform: baVis ? 'scale(1)' : 'scale(0.95)', transition: 'all 0.8s ease 0.2s', position: 'relative', overflow: 'hidden', height: 500 }}>
                            <LazyImage 
                                src={beforeImage} 
                                alt="Before landscaping" 
                                sectionKey="beforeAfter"
                                style={{ ...styles.baFullImg, position: 'absolute', inset: 0, opacity: !baAfter ? 1 : 0, transition: 'opacity 0.7s ease' }} 
                                onError={(e) => { e.target.src = 'https://images.pexels.com/photos/1643389/pexels-photo-1643389.jpeg?auto=compress&cs=tinysrgb&w=1200'; }} 
                            />
                            <img loading="lazy" 
                                src="https://images.unsplash.com/photo-1585320806297-9794b3e4eeae?auto=format&fit=crop&q=80&w=1200" 
                                alt="After landscaping" 
                                style={{ ...styles.baFullImg, position: 'absolute', inset: 0, opacity: baAfter ? 1 : 0, transition: 'opacity 0.7s ease' }} 
                                onError={(e) => { e.target.src = 'https://images.pexels.com/photos/1595696/pexels-photo-1595696.jpeg?auto=compress&cs=tinysrgb&w=1200'; }} 
                            />
                            <div style={{ ...styles.baBadgeFloat, background: baAfter ? '#2D7D46' : '#2D7D46', color: '#fff' }}>{baAfter ? 'AFTER' : 'BEFORE'}</div>
                        </div>
                    </Col>
                    <Col lg={5}>

<div style={{
    ...styles.baInfoPanel,
    background: 'linear-gradient(145deg, #ffffff 0%, #e8f5e9 35%, #c8e6c9 70%, #f1f8e9 100%)',
    boxShadow: '0 20px 60px rgba(45,125,70,0.12)',
    border: '1px solid rgba(45,125,70,0.1)',
    position: 'relative',
    overflow: 'hidden',
    padding: '40px 30px',
    borderRadius: '16px',
}}>
<div style={{
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 30% 20%, rgba(120,160,130,0.2) 0%, transparent 60%)',
    pointerEvents: 'none'
}}></div>                            <div style={{ position: 'relative', zIndex: 1 }}>
                                {!baAfter ? (
                                    <div>
                                        <div style={{ 
                                            ...styles.baInfoBadge, 
                                            background: 'rgba(211,47,47,0.08)', 
                                            color: '#c62828',
                                            padding: '8px 16px',
                                            borderRadius: '20px',
                                            display: 'inline-block',
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            letterSpacing: '0.5px',
                                            backdropFilter: 'blur(10px)',
                                            border: '1px solid rgba(211,47,47,0.15)',
                                            marginBottom: '20px'
                                        }}>BEFORE OUR INTERVENTION</div>
                                        <ul style={{ 
                                            ...styles.baList, 
                                            color: '#1A2E1A',
                                            padding: 0,
                                            margin: 0,
                                            listStyle: 'none'
                                        }}>
                                            {['Uneven sandy patches with poor soil quality.', 'Inefficient manual watering causing high water bills.', 'Lack of shaded areas or structured walkways.', 'No cohesive design or visual appeal.'].map((t, i) => <li key={i} style={{ 
                                                ...styles.baListItem, 
                                                color: '#1A2E1A',
                                                padding: '12px 0',
                                                borderBottom: '1px solid rgba(0,0,0,0.05)',
                                                fontSize: '15px',
                                                lineHeight: 1.5,
                                                display: 'flex',
                                                alignItems: 'flex-start'
                                            }}>
                                                <span style={{ color: '#c62828', marginRight: 12, fontWeight: 700, fontSize: '18px' }}>✗</span>
                                                <span>{t}</span>
                                            </li>)}
                                        </ul>
                                        <button onClick={() => setBaAfter(true)} style={{
                                            ...styles.baSeeResultBtn,
                                            background: 'linear-gradient(135deg, #2D7D46, #3A9D5A)',
                                            color: '#ffffff',
                                            border: 'none',
                                            padding: '14px 30px',
                                            borderRadius: '50px',
                                            fontSize: '16px',
                                            fontWeight: 600,
                                            marginTop: '25px',
                                            width: '100%',
                                            boxShadow: '0 8px 25px rgba(45,125,70,0.25)',
                                            transition: 'all 0.3s ease',
                                            cursor: 'pointer'
                                        }} 
                                        onMouseEnter={(e) => {
                                            e.target.style.transform = 'translateX(5px)';
                                            e.target.style.boxShadow = '0 12px 35px rgba(45,125,70,0.35)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.transform = 'translateX(0)';
                                            e.target.style.boxShadow = '0 8px 25px rgba(45,125,70,0.25)';
                                        }}>See the After Result →</button>
                                    </div>
                                ) : (
                                    <div>
                                        <div style={{ 
                                            ...styles.baInfoBadge, 
                                            background: 'rgba(45,125,70,0.12)', 
                                            color: '#1B5E20',
                                            padding: '8px 16px',
                                            borderRadius: '20px',
                                            display: 'inline-block',
                                            fontSize: '13px',
                                            fontWeight: 600,
                                            letterSpacing: '0.5px',
                                            backdropFilter: 'blur(10px)',
                                            border: '1px solid rgba(45,125,70,0.2)',
                                            marginBottom: '20px'
                                        }}>AFTER NASMAT TOUCH</div>
                                        <ul style={{ 
                                            ...styles.baList, 
                                            color: '#1A2E1A',
                                            padding: 0,
                                            margin: 0,
                                            listStyle: 'none'
                                        }}>
                                            {['Premium Bermuda grass turf and exotic flowering beds.', 'Automated smart irrigation with 40% water savings.', 'Teak wood Pergola and Italian stone pathways.', 'Complete visual transformation with premium aesthetics.'].map((t, i) => <li key={i} style={{ 
                                                ...styles.baListItem, 
                                                color: '#1A2E1A',
                                                padding: '12px 0',
                                                borderBottom: '1px solid rgba(0,0,0,0.05)',
                                                fontSize: '15px',
                                                lineHeight: 1.5,
                                                display: 'flex',
                                                alignItems: 'flex-start'
                                            }}>
                                                <span style={{ color: '#2E7D32', marginRight: 12, fontWeight: 700, fontSize: '18px' }}>✓</span>
                                                <span>{t}</span>
                                            </li>)}
                                        </ul>
                                        <button onClick={() => openLandscapeQuote('landscaping', 'A transformation like the After result')} style={{
                                            ...styles.baSeeResultBtn,
                                            background: '#2D7D46',
                                            color: '#ffffff',
                                            border: 'none',
                                            padding: '14px 30px',
                                            borderRadius: '50px',
                                            fontSize: '16px',
                                            fontWeight: 600,
                                            marginTop: '25px',
                                            width: '100%',
                                            boxShadow: '0 8px 25px rgba(45,125,70,0.3)',
                                            transition: 'all 0.3s ease',
                                            cursor: 'pointer'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.background = '#3A9D5A';
                                            e.target.style.transform = 'scale(1.02)';
                                            e.target.style.boxShadow = '0 12px 35px rgba(45,125,70,0.4)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.background = '#2D7D46';
                                            e.target.style.transform = 'scale(1)';
                                            e.target.style.boxShadow = '0 8px 25px rgba(45,125,70,0.3)';
                                        }}>Book This For Your Garden →</button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>
        </section>
    );
}

// 9. PROCESS SECTION
function ProcessSection({ processRef, processVis }) {
    const steps = [
        { icon: <FaUsers />, title: 'Discovery', text: 'On-site consultation to understand your lifestyle and garden goals.' },
        { icon: <FaPalette />, title: 'Design', text: 'Custom 3D rendering of your garden with material and plant selection.' },
        { icon: <FaGem />, title: 'Curation', text: 'Sourcing rare exotic plants and premium materials specifically for your project.' },
        { icon: <FaHardHat />, title: 'Creation', text: 'Expert implementation by our team of builders and horticulturists.' },
        { icon: <FaSeedling />, title: 'Growth', text: 'Final handover with a custom maintenance plan to keep it lush.' },
    ];

    return (
        <section id="process-section" ref={processRef} style={{ ...styles.processSection, background: '#eef3ee', padding: '35px 0' }}>
            <Container>
                <div style={{ ...styles.sectionHeader, opacity: processVis ? 1 : 0, transform: processVis ? 'translateY(0)' : 'translateY(20px)', transition: 'all 0.8s ease', marginBottom: '24px' }}>
                    <h2 style={{ ...styles.sectionTitle, marginBottom: '6px' }}>Our Masterplan Process</h2>
                    <p style={styles.sectionSub}>Five steps to your personal sanctuary.</p>
                </div>
                <div style={{
                    ...styles.timelineRow,
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'center',
                    gap: '20px'
                }}>
                    {steps.map((step, idx) => (
                        <div key={idx} style={{ 
                            ...styles.processStep, 
                            background: '#ffffff',
                            flex: '1 1 calc(18% - 16px)',
                            minWidth: '210px',
                            opacity: processVis ? 1 : 0, 
                            transform: processVis ? 'translateY(0)' : 'translateY(30px)', 
                            borderRadius: '12px', 
                            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.05)', 
                            padding: '18px 16px',
                            border: '1px solid rgba(0,0,0,0.04)', 
transition: 'all 0.6s ease ' + (idx * 0.15) + 's, transform 0.3s ease, box-shadow 0.3s ease',                            cursor: 'default'
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-5px)';
                            e.currentTarget.style.boxShadow = '0 10px 30px rgba(0, 0, 0, 0.1)';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 6px 20px rgba(0, 0, 0, 0.05)';
                        }}>
                            <div style={{
                                ...styles.processIconBox,
                                marginBottom: '10px'
                            }}>{step.icon}</div>
                            <div style={{
                                ...styles.stepNum,
                                marginTop: '4px',
                                marginBottom: '4px'
                            }}>{idx + 1}</div>
                            <h4 style={{
                                ...styles.processStepTitle,
                                fontSize: '16px',
                                marginBottom: '6px'
                            }}>{step.title}</h4>
                            <p style={{
                                ...styles.processStepText,
                                fontSize: '13px',
                                lineHeight: '1.45', 
                                marginBottom: '0' 
                            }}>{step.text}</p>
                        </div>
                    ))}
                </div>
            </Container>
        </section>
    );
}

// 10. QUIZ SECTION
function QuizSection({ data, quizStep, quizAnswers, quizResult, handleQuizAnswer, resetQuiz }) {
    // ... (full QuizSection code from your original)
    // Keeping it short for brevity - use your original implementation
    const lightColors = {
        pureWhite: '#c0e9b5',
        softMint: '#afd3b9',
        lightGreen: '#E8F5E9',
        sageGreen: '#C8E6C9',
        warmCream: '#b6deac',
        softGold: '#FFF8E1'
    };
    const [weatherData, setWeatherData] = useState({
        temperature: '--',
        condition: 'Loading...',
        icon: '⏳',
        humidity: '--',
        windSpeed: '--',
        uvIndex: '--'
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const DUBAI_LAT = 25.2048;
    const DUBAI_LON = 55.2708;

    const fetchWeather = async () => {
        try {
            setLoading(true);
            setError(null);
           const something = 'value';
setLoading(true); 
            const API_KEY = '60cbe456572d637b38b9ac32a27696cb';
            
            const weatherUrl = `https://api.openweathermap.org/data/2.5/weather?lat=${DUBAI_LAT}&lon=${DUBAI_LON}&units=metric&appid=${API_KEY}`;
            const response = await fetch(weatherUrl);

            if (!response.ok) {
                throw new Error('Dubai weather fetch failed');
            }

            const data = await response.json();

            let uvIndex = 8;
            try {
                const uvResponse = await fetch(
                    `https://api.openweathermap.org/data/2.5/uvi?lat=${DUBAI_LAT}&lon=${DUBAI_LON}&appid=${API_KEY}`
                );
                if (uvResponse.ok) {
                    const uvData = await uvResponse.json();
                    uvIndex = uvData.value || 8;
                }
            } catch (err) {
                console.warn('UV Index fallback to 8');
            }

            setWeatherData({
                temperature: Math.round(data.main.temp),
                condition: data.weather[0].main,
                icon: getWeatherIcon(data.weather[0].icon),
                humidity: data.main.humidity,
                windSpeed: Math.round(data.wind.speed * 3.6),
                uvIndex: uvIndex
            });
            setLoading(false);
            setError(null);

        } catch (err) {
            console.error('Dubai weather error:', err);
            setError('Unable to fetch Dubai weather');
            setLoading(false);
            
            setWeatherData({
                temperature: '32',
                condition: 'Clear',
                icon: '☀️',
                humidity: '45',
                windSpeed: '15',
                uvIndex: '8'
            });
        }
    };

    const getWeatherIcon = (iconCode) => {
        const iconMap = {
            '01d': '☀️', '01n': '🌙',
            '02d': '⛅', '02n': '☁️',
            '03d': '☁️', '03n': '☁️',
            '04d': '☁️', '04n': '☁️',
            '09d': '🌧️', '09n': '🌧️',
            '10d': '🌦️', '10n': '🌧️',
            '11d': '⛈️', '11n': '⛈️',
            '13d': '❄️', '13n': '❄️',
            '50d': '🌫️', '50n': '🌫️'
        };
        return iconMap[iconCode] || '🌈';
    };

    const getWeatherBackground = () => {
        if (error || loading || weatherData.temperature === '--') {
            return { 
                background: `linear-gradient(145deg, ${lightColors.pureWhite} 0%, ${lightColors.softMint} 35%, ${lightColors.sageGreen} 70%, ${lightColors.warmCream} 100%)`,
                icon: '📍' 
            };
        }
        const temp = weatherData.temperature;
        if (temp > 40) return { 
            background: `linear-gradient(145deg, ${lightColors.pureWhite} 0%, ${lightColors.softMint} 35%, ${lightColors.sageGreen} 70%, ${lightColors.warmCream} 100%)`,
            icon: '🌵' 
        };
        else if (temp > 35) return { 
            background: `linear-gradient(145deg, ${lightColors.pureWhite} 0%, ${lightColors.softMint} 35%, ${lightColors.sageGreen} 70%, ${lightColors.warmCream} 100%)`,
            icon: '☀️' 
        };
        else if (temp > 25) return { 
            background: `linear-gradient(145deg, ${lightColors.pureWhite} 0%, ${lightColors.softMint} 35%, ${lightColors.sageGreen} 70%, ${lightColors.warmCream} 100%)`,
            icon: '🌤️' 
        };
        else if (temp > 15) return { 
            background: `linear-gradient(145deg, ${lightColors.pureWhite} 0%, ${lightColors.softMint} 35%, ${lightColors.sageGreen} 70%, ${lightColors.warmCream} 100%)`,
            icon: '⛅' 
        };
        else return { 
            background: 'linear-gradient(135deg, #F3E5F5, #E1BEE7, #CE93D8)',
            icon: '❄️' 
        };
    };

    const getWeatherColors = () => {
        if (error || loading || weatherData.temperature === '--') {
            return { primary: '#5D4037', secondary: '#8D6E63', text: '#3E2723' };
        }
        const temp = weatherData.temperature;
        if (temp > 40) return { primary: '#BF360C', secondary: '#E64A19', text: '#4E1A0A' };
        else if (temp > 35) return { primary: '#E65100', secondary: '#F57C00', text: '#4E2A0A' };
        else if (temp > 25) return { primary: '#1B5E20', secondary: '#2E7D32', text: '#0A2E0A' };
        else if (temp > 15) return { primary: '#0D47A1', secondary: '#1976D2', text: '#061A3E' };
        else return { primary: '#4A148C', secondary: '#7B1FA2', text: '#1A0533' };
    };

    const getExpertTip = () => {
        if (error) {
            return `⚠️ ${error}. Showing default Dubai weather data.`;
        }
        if (loading || weatherData.temperature === '--') {
            return "🌱 Fetching Dubai weather data...";
        }
        const temp = weatherData.temperature;
        const humidity = weatherData.humidity;
        
        if (temp > 40) return "Extreme heat! Water plants early morning or after sunset. Move potted plants to shade.";
        else if (temp > 35 && humidity < 30) return "⚠️ Extreme dry heat! Water deeply and mist plants. Use shade cloth for outdoor plants.";
        else if (temp > 35) return "🔥 High temperature! Mist indoor tropicals twice today. Water outdoor plants after sunset.";
        else if (temp > 30 && humidity < 40) return "☀️ Dry heat! Increase watering. Use pebble trays for indoor humidity.";
        else if (temp > 25) return "🌿 Perfect growing weather! Maintain regular watering schedules.";
        else if (temp < 18) return "❄️ Cooler temperatures! Bring sensitive tropicals indoors.";
        else return "🌱 Mild conditions ideal for plant care. Check soil moisture before watering.";
    };

    useEffect(() => {
        fetchWeather();
        const interval = setInterval(fetchWeather, 900000);
        return () => clearInterval(interval);
    }, []);

    const weatherBg = getWeatherBackground();
    const colors = getWeatherColors();
    const expertTip = getExpertTip();

    return (
        <section id="quiz-section" style={styles.quizSection}>
            <Container>
                <Row className="align-items-stretch g-5">
                    <Col lg={6} style={{ display: 'flex' }}>
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            height: '100%', 
                            width: '100%',
                            background: 'rgba(255,255,255,0.06)', 
                            backdropFilter: 'blur(16px)',
                            borderRadius: '24px',
                            padding: '30px',
                            border: '1px solid rgba(255,255,255,0.15)',
                            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.05)'
                        }}>
                            <h2 style={{ ...styles.sectionTitle, color: '#ecece8', textAlign: 'left', fontWeight: 700 }}>
                                Find Your Perfect Plant Match
                            </h2>
                            <p style={{ ...styles.sectionSub, color: 'rgba(255,255,255,0.85)', margin: '0 0 30px 0', textAlign: 'left' }}>
                                Answer 3 quick questions and let our experts recommend the ideal greenery for your lifestyle.
                            </p>
                            <div style={{ flex: 1, display: 'flex' }}>
                                {!quizResult ? (
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                        <div style={styles.quizProgress}>Question {quizStep + 1} of 3</div>
                                        {quizStep === 0 && <div className="animate__animated animate__fadeIn"><h4 style={{ color: '#fff', fontWeight: 600, marginBottom: 20 }}>What is the light level in your room?</h4><div style={styles.quizBtnGroup}><Button variant="outline-light" style={styles.quizOption} onClick={() => handleQuizAnswer('light', 'Low')}>Low Light (No windows)</Button><Button variant="outline-light" style={styles.quizOption} onClick={() => handleQuizAnswer('light', 'Medium')}>Indirect Light (Bright room)</Button><Button variant="outline-light" style={styles.quizOption} onClick={() => handleQuizAnswer('light', 'High')}>Direct Sunlight (Balcony/Window)</Button></div></div>}
                                        {quizStep === 1 && <div className="animate__animated animate__fadeIn"><h4 style={{ color: '#fff', fontWeight: 600, marginBottom: 20 }}>How much space do you have?</h4><div style={styles.quizBtnGroup}><Button variant="outline-light" style={styles.quizOption} onClick={() => handleQuizAnswer('space', 'Small')}>Small Spot (Desk/Shelf)</Button><Button variant="outline-light" style={styles.quizOption} onClick={() => handleQuizAnswer('space', 'Medium')}>Corner Area (Floor Plant)</Button><Button variant="outline-light" style={styles.quizOption} onClick={() => handleQuizAnswer('space', 'Large')}>Large Area (Garden/Patio)</Button></div></div>}
                                        {quizStep === 2 && <div className="animate__animated animate__fadeIn"><h4 style={{ color: '#fff', fontWeight: 600, marginBottom: 20 }}>How much effort can you put into care?</h4><div style={styles.quizBtnGroup}><Button variant="outline-light" style={styles.quizOption} onClick={() => handleQuizAnswer('effort', 'Low')}>"I forget to water"</Button><Button variant="outline-light" style={styles.quizOption} onClick={() => handleQuizAnswer('effort', 'Medium')}>"I can water weekly"</Button><Button variant="outline-light" style={styles.quizOption} onClick={() => handleQuizAnswer('effort', 'High')}>"I love pampering plants"</Button></div></div>}
                                    </div>
                                ) : (
                                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center' }}>
                                        <div style={styles.resultIcon}>✨</div>
                                        <h3 style={{ color: '#e9e7e4', fontWeight: 700, marginBottom: 10 }}>Your Perfect Matches</h3>
                                        <p style={{ color: 'rgba(255,255,255,0.85)', marginBottom: 24 }}>Based on your lifestyle, we recommend these {quizResult.category} plants:</p>
                                        <div style={{ display: 'flex', gap: 15, marginBottom: 30, justifyContent: 'center', flexWrap: 'wrap' }}>
                                            {quizResult.plants.map((plant, idx) => {
                                                const plantImg = plant.image_url ? (plant.image_url.startsWith('http') ? plant.image_url : plant.image_url.startsWith('/') ? plant.image_url : `${API_BASE}${plant.image_url}`) : null;
                                                return (
                                                    <div key={idx} style={styles.compactPlantCard}>
                                                        <div style={{ height: 120, background: '#eee', overflow: 'hidden' }}>
                                                            {plantImg ? <LazyImage src={plantImg} alt={plant.name} sectionKey="quiz" style={styles.compactPlantImg} onError={(e) => { e.target.src = 'https://via.placeholder.com/200x120/2D7D46/fff?text=Plant'; }} /> : <div style={{ transform: 'scale(0.6)', transformOrigin: 'top center' }}><PlantSVG color={PlantPlaceholderColors[idx % PlantPlaceholderColors.length]} index={idx} /></div>}
                                                        </div>
                                                        <div style={styles.compactPlantBody}>
                                                            <div style={{ fontWeight: 700, color: '#1B5E20', fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{plant.name}</div>
                                                            <a href={`https://wa.me/971552698951?text=I'm interested in the ${plant.name} recommended by the quiz`} target="_blank" rel="noreferrer" style={styles.compactPlantLink}>Interested <FaArrowRight size={10} style={{ marginLeft: 4 }} /></a>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        <Button variant="light" onClick={resetQuiz} style={{ borderRadius: 30, fontWeight: 700, padding: '10px 30px', background: '#fff', color: '#2D7D46' }}>Retake Quiz</Button>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Col>
                    
                    <Col lg={6} style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ 
                            ...styles.climateCard,
                            width: '100%',
                            padding: '35px 30px',
                            borderRadius: '24px',
                            background: weatherBg.background, 
                            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.06), inset 0 1px 0 rgba(255,255,255,0.8)',
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)',
                            border: '1px solid rgba(255,255,255,0.6)'
                        }}>
                            <div style={{
                                position: 'absolute',
                                top: -50,
                                right: -50,
                                width: 200,
                                height: 200,
                                borderRadius: '50%',
                                background: 'rgba(255,255,255,0.2)',
                                pointerEvents: 'none'
                            }}></div>
                            <div style={{
                                position: 'absolute',
                                bottom: -80,
                                left: -80,
                                width: 250,
                                height: 250,
                                borderRadius: '50%',
                                background: 'rgba(255,255,255,0.15)',
                                pointerEvents: 'none'
                            }}></div>
                            
                            <div style={{ position: 'relative', zIndex: 1 }}>
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'space-between', 
                                    alignItems: 'center', 
                                    marginBottom: 25
                                }}>
                                    <h4 style={{ fontWeight: 800, color: colors.primary, fontSize: '18px', margin: 0 }}>
                                        🌿 Dubai Garden Climate
                                    </h4>
                                    <span style={{ 
                                        fontSize: '11px', 
                                        fontWeight: 800,
                                        padding: '5px 14px',
                                        background: 'rgba(255,255,255,0.9)',
                                        color: colors.primary,
                                        borderRadius: '30px',
                                        border: `1px solid rgba(0,0,0,0.06)`,
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)'
                                    }}>
                                        {loading ? '⏳ LOADING...' : error ? '❌ ERROR' : '📍 Dubai, UAE'}
                                    </span>
                                </div>
                                
                                <div style={{ 
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '24px',
                                    marginBottom: '30px'
                                }}>
                                    <div style={{
                                        width: '85px',
                                        height: '85px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        background: 'rgba(255,255,255,0.5)',
                                        borderRadius: '50%',
                                        backdropFilter: 'blur(4px)',
                                        boxShadow: '0 4px 20px rgba(0,0,0,0.04)'
                                    }}>
                                        <span style={{ fontSize: '40px', lineHeight: 1 }}>
                                            {loading ? '⏳' : error ? '⚠️' : weatherData.icon || '☀️'}
                                        </span>
                                    </div>
                                    <div style={{ textAlign: 'left' }}>
                                        <div style={{ 
                                            fontSize: '50px', 
                                            fontWeight: 900, 
                                            color: colors.primary, 
                                            lineHeight: 1,
                                            letterSpacing: '-1.5px',
                                            textShadow: '0 2px 4px rgba(255,255,255,0.3)'
                                        }}>
                                            {loading ? '--' : `${weatherData.temperature}°C`}
                                        </div>
                                        <div style={{ 
                                            fontSize: '14px', 
                                            color: colors.secondary, 
                                            fontWeight: 700,
                                            marginTop: '6px',
                                            textTransform: 'uppercase',
                                            letterSpacing: '0.5px'
                                        }}>
                                            {loading ? 'Loading...' : error ? 'Error' : weatherData.condition}
                                        </div>
                                    </div>
                                </div>
                                
                                <div style={{ 
                                    height: '1px', 
                                    width: '100%',
                                    background: 'linear-gradient(to right, transparent, rgba(0,0,0,0.06), transparent)',
                                    margin: '0 auto 25px'
                                }} />
                                
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr 1fr',
                                    gap: '12px',
                                    width: '100%',
                                    marginBottom: '25px'
                                }}>
                                    <div style={{ 
                                        background: 'rgba(255,255,255,0.85)', 
                                        borderRadius: '14px', 
                                        padding: '12px 10px', 
                                        border: '1px solid rgba(255,255,255,0.5)',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
                                    }}>
                                        <div style={{ fontSize: '10px', color: 'rgba(0,0,0,0.4)', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px', letterSpacing: '0.3px' }}>💧 Humidity</div>
                                        <div style={{ fontSize: '18px', fontWeight: 800, color: colors.text }}>{loading ? '--' : `${weatherData.humidity}%`}</div>
                                    </div>
                                    <div style={{ 
                                        background: 'rgba(255,255,255,0.85)', 
                                        borderRadius: '14px', 
                                        padding: '12px 10px', 
                                        border: '1px solid rgba(255,255,255,0.5)',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
                                    }}>
                                        <div style={{ fontSize: '10px', color: 'rgba(0,0,0,0.4)', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px', letterSpacing: '0.3px' }}>💨 Wind</div>
                                        <div style={{ fontSize: '18px', fontWeight: 800, color: colors.text }}>{loading ? '--' : `${weatherData.windSpeed} km/h`}</div>
                                    </div>
                                    <div style={{ 
                                        background: 'rgba(255,255,255,0.85)', 
                                        borderRadius: '14px', 
                                        padding: '12px 10px', 
                                        border: '1px solid rgba(255,255,255,0.5)',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.03)'
                                    }}>
                                        <div style={{ fontSize: '10px', color: 'rgba(0,0,0,0.4)', fontWeight: 700, textTransform: 'uppercase', marginBottom: '4px', letterSpacing: '0.3px' }}>☀️ UV Index</div>
                                        <div style={{ fontSize: '18px', fontWeight: 800, color: colors.text }}>{loading ? '--' : weatherData.uvIndex}</div>
                                    </div>
                                </div>
                                
                                <div style={{ 
                                    background: 'rgba(255,255,255,0.9)',
                                    padding: '16px 20px',
                                    borderRadius: '16px',
                                    textAlign: 'left',
                                    border: '1px solid rgba(255,255,255,0.8)',
                                    borderLeft: `4px solid ${colors.secondary}`,
                                    boxShadow: '0 4px 15px rgba(0,0,0,0.04)',
                                    backdropFilter: 'blur(4px)'
                                }}>
                                    <h5 style={{ 
                                        fontSize: '13px',
                                        marginBottom: '6px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: colors.primary,
                                        fontWeight: 800
                                    }}>
                                        <FaTint style={{ marginRight: '8px', color: colors.secondary }} /> 
                                        {error ? '⚠️ Error' : '🌴 Dubai Plant Care Tip:'}
                                    </h5>
                                    <p style={{ 
                                        fontSize: '12.5px',
                                        lineHeight: '1.6',
                                        margin: 0,
                                        color: colors.text,
                                        fontWeight: 500
                                    }}>
                                        {expertTip}
                                        {error && (
                                            <Button 
                                                onClick={fetchWeather} 
                                                variant="outline-secondary" 
                                                size="sm"
                                                style={{ 
                                                    marginTop: '10px', 
                                                    fontSize: '11px',
                                                    fontWeight: 700,
                                                    borderRadius: '20px',
                                                    border: '2px solid #666',
                                                    color: '#333',
                                                    padding: '5px 16px',
                                                    display: 'block'
                                                }}
                                            >
                                                🔄 Retry Now
                                            </Button>
                                        )}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>
        </section>
    );
}

// 11. WHY CHOOSE US
function WhyChooseUs({ whyRef, whyVis, data }) {
    const points = [
        'Locally grown & freshly sourced plants every week',
        'Expert guidance from horticulturists',
        'Wide variety: Flowers, Indoor, Fruit Trees, Herbs & more',
        'Competitive wholesale pricing for bulk buyers',
        'Delivery services available for select locations',
        'Complete landscaping solutions for residential & commercial'
    ];

    const categories = data?.categories?.length > 0 ? data.categories : ['Flowering', 'Indoor', 'Fruit Trees', 'Succulents', 'Herbs', 'Fertilizers'];

    return (
        <section id="why-choose-us" ref={whyRef} style={styles.whySection}>
            <Container>
                <Row className="align-items-stretch g-5">
                    <Col md={6} style={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        opacity: whyVis ? 1 : 0, 
                        transform: whyVis ? 'translateX(0)' : 'translateX(-60px)', 
                        transition: 'all 0.8s ease'
                    }}>
                        <div style={styles.sectionHeader}>
                            <FaAward size={36} color="#fff" style={{ animation: 'spinSlow 6s linear infinite' }} />
                            <h2 style={{ ...styles.sectionTitle, color: '#fff' }}>Why Nasmat Al Zohour?</h2>
                            <p style={{ ...styles.sectionSub, color: 'rgba(255,255,255,0.85)' }}>Your trusted partner for premium plants in the UAE</p>
                        </div>
                        {points.map((point, i) => (
                            <div key={i} style={{ ...styles.checkRow, opacity: whyVis ? 1 : 0, transform: whyVis ? 'translateX(0)' : 'translateX(-40px)', transition: `all 0.5s ease ${i * 0.1 + 0.3}s` }}>
                                <FaCheckCircle size={20} color="#d2d7d2" style={{ marginRight: 12, flexShrink: 0 }} />
                                <span style={{ color: 'rgba(255,255,255,0.92)', fontSize: 15 }}>{point}</span>
                            </div>
                        ))}
                    </Col>
                    <Col md={6} style={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        opacity: whyVis ? 1 : 0, 
                        transform: whyVis ? 'translateX(0)' : 'translateX(60px)', 
                        transition: 'all 0.8s ease 0.2s'
                    }}>
                        <div style={{
                            ...styles.categoriesGrid,
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                        }}>
                            <h5 style={{ color: '#fff', marginBottom: 20, fontWeight: 600 }}>
                                <FaTree style={{ marginRight: 8 }} /> Our Plant Categories
                            </h5>
                            <Row className="g-3">
                                {categories.map((cat, i) => (
                                    <Col xs={6} key={i}>
                                        <div style={{ 
                                            ...styles.catChip, 
                                            opacity: whyVis ? 1 : 0, 
                                            transform: whyVis ? 'scale(1)' : 'scale(0.8)', 
                                            transition: `all 0.4s ease ${i * 0.08 + 0.5}s`,
                                            background: 'rgba(255,255,255,0.08)',
                                            border: '1px solid rgba(255,255,255,0.15)',
                                            borderRadius: '12px',
                                            padding: '12px 16px',
                                            color: '#fff',
                                            fontWeight: 500,
                                            fontSize: '14px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.target.style.background = 'rgba(255,255,255,0.18)';
                                            e.target.style.transform = 'translateY(-3px)';
                                            e.target.style.boxShadow = '0 8px 25px rgba(0,0,0,0.2)';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.target.style.background = 'rgba(255,255,255,0.08)';
                                            e.target.style.transform = 'translateY(0)';
                                            e.target.style.boxShadow = 'none';
                                        }}>
                                            <GiFlowers style={{ marginRight: 8, color: '#A5D6A7' }} />
                                            {cat}
                                        </div>
                                    </Col>
                                ))}
                            </Row>
                        </div>
                    </Col>
                </Row>
            </Container>
        </section>
    );
}

// 12. PACKAGES SECTION
function PackagesSection({ packagesRef, packagesVis, activePackage, setActivePackage, openLandscapeQuote }) {
    const packages = [
        { id: 1, name: 'The Urban Balcony', price: '4,999', icon: <FaSeedling />, features: ['Custom Planters & Soil', 'Auto-Misting System', 'Outdoor Lighting', 'Organic Fertilization'] },
        { id: 2, name: 'The Desert Oasis', price: '12,499', icon: <FaTree />, features: ['2D/3D Design Model', 'Hardscaping & Tiles', 'Palm & Accent Trees', 'Smart Irrigation (App Controlled)'] },
        { id: 3, name: 'The Estate Royal', price: '29,999+', icon: <FaStore />, features: ['Full Architectural Masterplan', 'Custom Pergola & Seating', 'Water Feature Installation', '1 Year Free Maintenance'] },
    ];

    const sameColor = '#2D7D46';

    return (
        <section id="packages-section" ref={packagesRef} style={{ ...styles.packagesSection, background: '#f5f9f5' }}>
            <Container>
                <div style={{ ...styles.sectionHeader, opacity: packagesVis ? 1 : 0, transform: packagesVis ? 'translateY(0)' : 'translateY(30px)', transition: 'all 0.8s ease' }}>
                    <h2 style={styles.sectionTitle}>Tailored Landscaping Packages</h2>
                    <p style={styles.sectionSub}>Choose the perfect plan for your outdoor space. Professional results, guaranteed.</p>
                </div>
                <Row className="g-4">
                    {packages.map((pkg, idx) => (
                        <Col key={idx} md={4}>
                            <div style={{ 
                                ...styles.packageCard, 
                                background: '#ffffff',
                                opacity: packagesVis ? 1 : 0, 
                                transform: activePackage === pkg.id ? 'scale(1.05)' : packagesVis ? 'scale(1)' : 'scale(0.95)', 
                                borderColor: activePackage === pkg.id ? sameColor : '#e8f0e8', 
                                transition: `all 0.6s cubic-bezier(0.175, 0.885, 0.32, 1.275) ${idx * 0.1}s`, 
                                boxShadow: activePackage === pkg.id ? `0 20px 60px ${sameColor}33` : '0 10px 30px rgba(0,0,0,0.05)' 
                            }} 
                            onMouseEnter={() => setActivePackage(pkg.id)}>
                                <div style={{ ...styles.packageIconBox, background: sameColor }}>{pkg.icon}</div>
                                <h4 style={styles.packageName}>{pkg.name}</h4>
                                <div style={styles.packageDivider} />
                                <ul style={styles.packageFeatures}>{pkg.features.map((f, i) => <li key={i}><FaCheckCircle color={sameColor} style={{ marginRight: 8 }} />{f}</li>)}</ul>
                                <button onClick={() => openLandscapeQuote('landscaping', pkg.name)} style={{ ...styles.packageBtn, background: sameColor, border: 'none', width: '100%', cursor: 'pointer' }}>Choose {pkg.name}</button>
                            </div>
                        </Col>
                    ))}
                </Row>
            </Container>
        </section>
    );
}

// 13. TESTIMONIALS
function Testimonials({ testimonialRef, testimonialVis }) {
    const testimonials = [
        { 
            text: 'It was a nice experience, I was able to find certain rare varieties of plants.', 
            name: 'Kishore Kumar KV', 
            location: '5 reviews · 9 hours ago',
            rating: 5,
            isNew: true,
            initial: 'K'
        },
        { 
            text: 'The most beautiful plants in all of Sharjah! The staff really knows their flora.', 
            name: 'Yasser Abu Raya', 
            location: '87 reviews · 4 years ago',
            rating: 5,
            initial: 'Y'
        },
        { 
            text: 'Perfect quality Bougainvillea — they delivered and planted it for me. Excellent service!', 
            name: 'Surya Narayana Reddy Velagala', 
            location: 'Local Guide · 3 reviews · 161 photos · a year ago',
            rating: 5,
            initial: 'S'
        },
        { 
            text: 'Amazing variety and healthy plants. Will definitely come back again!', 
            name: 'Kamran Safdar', 
            location: 'Google User · 2 months ago',
            rating: 5,
            initial: 'K'
        },
    ];

    return (
        <section id="testimonials-section" ref={testimonialRef} style={{ 
            ...styles.testimonialsSection, 
            background: 'linear-gradient(135deg, #0d3320 0%, #1a5c32 100%)',
            padding: '70px 0',
        }}>
            <Container>
                <div style={{ textAlign: 'center', marginBottom: 50 }}>
                    <span style={{
                        display: 'inline-block',
                        background: 'linear-gradient(135deg, #c9a03d, #b8860b)',
                        color: '#fff',
                        padding: '6px 24px',
                        borderRadius: 30,
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: 2,
                        textTransform: 'uppercase',
                        marginBottom: 12
                    }}>
                        <FaStar style={{ marginRight: 8 }} /> Testimonials
                    </span>
                    <h2 style={{ ...styles.sectionTitle, color: '#ffffff', fontSize: '2.2rem', marginBottom: 10 }}>
                        What Our Customers Say
                    </h2>
                    <p style={{ ...styles.sectionSub, color: 'rgba(255,255,255,0.8)', maxWidth: 500, margin: '0 auto' }}>
                        Real feedback from our happy customers
                    </p>
                </div>

                <Row className="g-4 justify-content-center">
                    {testimonials.map((t, i) => (
                        <Col 
                            key={i} 
                            xs={12} 
                            sm={6} 
                            lg={3}
                            style={{ 
                                opacity: testimonialVis ? 1 : 0, 
                                transform: testimonialVis ? 'translateY(0)' : 'translateY(40px)', 
                                transition: `all 0.6s ease ${i * 0.15}s`,
                                display: 'flex',
                            }}
                        >
                            <div style={{ 
                                ...styles.testimonialCard, 
                                background: 'rgba(255,255,255,0.08)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                borderRadius: 24,
                                padding: '30px 25px',
                                boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                                transition: 'all 0.4s ease',
                                cursor: 'default',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                width: '100%',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-8px)';
                                e.currentTarget.style.boxShadow = '0 20px 50px rgba(0,0,0,0.25)';
                                e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.15)';
                                e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                            }}
                            >
                                <div style={{ 
                                    display: 'flex', 
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    marginBottom: 12,
                                    position: 'relative'
                                }}>
                                    <div style={{ display: 'flex', gap: 3 }}>
                                        {[...Array(5)].map((_, s) => (
                                            <FaStar key={s} color={s < t.rating ? "#FDD835" : "rgba(255,255,255,0.2)"} size={15} />
                                        ))}
                                    </div>
                                    {t.isNew && (
                                        <span style={{
                                            background: '#FDD835',
                                            color: '#0d3320',
                                            padding: '2px 12px',
                                            borderRadius: 20,
                                            fontSize: 9,
                                            fontWeight: 700,
                                            textTransform: 'uppercase',
                                            letterSpacing: 1,
                                            whiteSpace: 'nowrap',
                                            position: 'absolute',
                                            right: 0
                                        }}>
                                            NEW
                                        </span>
                                    )}
                                </div>

                                <p style={{ 
                                    color: 'rgba(255,255,255,0.9)',
                                    fontSize: '0.95rem',
                                    lineHeight: 1.6,
                                    marginBottom: 16,
                                    flex: 1,
                                    fontStyle: 'italic',
                                    textAlign: 'center',
                                }}>
                                    "{t.text}"
                                </p>

                                <div style={{ 
                                    display: 'flex', 
                                    alignItems: 'center', 
                                    gap: 12,
                                    borderTop: '1px solid rgba(255,255,255,0.08)',
                                    paddingTop: 14,
                                    marginTop: 'auto'
                                }}>
                                    <div style={{ 
                                        background: 'linear-gradient(135deg, #c9a03d, #b8860b)',
                                        color: '#fff',
                                        width: 42,
                                        height: 42,
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 16,
                                        fontWeight: 700,
                                        flexShrink: 0
                                    }}>
                                        {t.initial}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{ 
                                            color: '#ffffff',
                                            fontWeight: 700,
                                            fontSize: 13,
                                            marginBottom: 2,
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                        }}>
                                            {t.name}
                                        </div>
                                        <div style={{ 
                                            color: 'rgba(255,255,255,0.5)',
                                            fontSize: 11,
                                            lineHeight: 1.4
                                        }}>
                                            {t.location}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </Col>
                    ))}
                </Row>
            </Container>
        </section>
    );
}

// 14. SIZE GUIDE
function SizeGuide() {
    const sizes = [
        { 
            icon: <FaSeedling />, 
            label: 'Small (Desk/Shelf)', 
            range: '15cm - 30cm', 
            color: '#81C784', 
            bg: '#E8F5E9', 
            desc: 'Perfect for desks, shelves, and tabletops. Adds a touch of green without taking much space.',
            height: '40%',
            minHeight: 100,
            image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcS4yuFirVlmeUSNIl9rDXot7QPNlYF0eXT6PwjIeqrECA&s=10'
        },
        { 
            icon: <FaTree />, 
            label: 'Medium (Floor Corner)', 
            range: '60cm - 90cm', 
            color: '#4CAF50', 
            bg: '#C8E6C9', 
            desc: 'Ideal for floor corners, beside sofas, or as room dividers. Makes a statement without overwhelming.',
            height: '65%',
            minHeight: 170,
            image: 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcTzxvdic6mRuyv6Dj7oA1_CrAKPc9kuXJxfEWEYs6_9Fw&s=10'
        },
        { 
            icon: <FaPalette />, 
            label: 'Large (Statement)', 
            range: '150cm - 180cm', 
            color: '#2D7D46', 
            bg: '#A5D6A7', 
            desc: 'Dramatic floor plants that become the focal point of any room. Perfect for spacious areas.',
            height: '100%',
            minHeight: 260,
            image: 'https://images.unsplash.com/photo-1614594975525-e45190c55d0b?q=80&w=800'
        }
    ];

    const [hoveredSize, setHoveredSize] = useState(null);

    return (
        <section id="size-guide-section" style={styles.sizeGuideSection}>
            <Container>
                <div style={{ textAlign: 'center', marginBottom: 50 }}>
                    <span style={{
                        display: 'inline-block',
                        background: 'linear-gradient(135deg, #2D7D46, #4CAF50)',
                        color: '#fff',
                        padding: '6px 24px',
                        borderRadius: 30,
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: 2,
                        textTransform: 'uppercase',
                        marginBottom: 12
                    }}>
                        <FaRulerCombined style={{ marginRight: 8 }} /> Size Guide
                    </span>
                    <h2 style={{ ...styles.sectionTitle, fontSize: '2.4rem' }}>
                        Find Your Perfect Plant Size
                    </h2>
                    <p style={{ ...styles.sectionSub, maxWidth: 500 }}>
                        Visualize how different plants will look in your space
                    </p>
                </div>

                <Row className="g-5 align-items-stretch">
                    <Col lg={5}>
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: 16,
                            height: '100%'
                        }}>
                            {sizes.map((s, i) => (
                                <div 
                                    key={i} 
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 16,
                                        padding: '16px 20px',
                                        background: '#fff',
                                        borderRadius: 16,
                                        border: `2px solid ${s.bg}`,
                                        boxShadow: '0 2px 12px rgba(0,0,0,0.04)',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer',
                                        flexShrink: 0
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateX(8px)';
                                        e.currentTarget.style.boxShadow = '0 8px 30px rgba(45,125,70,0.12)';
                                        e.currentTarget.style.borderColor = s.color;
                                        setHoveredSize(i);
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateX(0)';
                                        e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.04)';
                                        e.currentTarget.style.borderColor = s.bg;
                                        setHoveredSize(null);
                                    }}
                                >
                                    <div style={{
                                        width: 52,
                                        height: 52,
                                        borderRadius: 14,
                                        background: s.bg,
                                        color: s.color,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: 24,
                                        flexShrink: 0
                                    }}>
                                        {s.icon}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 700, color: '#1B5E20', fontSize: 15, marginBottom: 2 }}>
                                            {s.label}
                                        </div>
                                        <div style={{ fontSize: 13, color: '#2D7D46', fontWeight: 600, marginBottom: 4 }}>
                                            {s.range}
                                        </div>
                                        <div style={{ fontSize: 12, color: '#888', lineHeight: 1.4 }}>
                                            {s.desc}
                                        </div>
                                    </div>
                                    <div style={{
                                        fontSize: 20,
                                        color: s.color,
                                        opacity: hoveredSize === i ? 1 : 0.3,
                                        transition: 'opacity 0.3s ease'
                                    }}>
                                        →
                                    </div>
                                </div>
                            ))}

                            <div style={{
                                marginTop: 'auto',
                                padding: '20px 24px',
                                background: 'linear-gradient(135deg, #f0faf3, #E8F5E9)',
                                borderRadius: 16,
                                borderLeft: '4px solid #2D7D46',
                                display: 'flex',
                                alignItems: 'flex-start',
                                gap: 12
                            }}>
                                <div style={{
                                    width: 32,
                                    height: 32,
                                    borderRadius: '50%',
                                    background: '#2D7D46',
                                    color: '#fff',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: 16,
                                    flexShrink: 0,
                                    marginTop: 2
                                }}>
                                    💡
                                </div>
                                <div>
                                    <div style={{ fontWeight: 700, color: '#1B5E20', fontSize: 13, marginBottom: 4 }}>
                                        Pro Tip
                                    </div>
                                    <div style={{ fontSize: 13, color: '#555', lineHeight: 1.5 }}>
                                        When choosing a plant, consider the <strong>ceiling height</strong> and <strong>available floor space</strong>. 
                                        A large plant needs at least <strong>1m²</strong> of floor area to grow comfortably.
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Col>

                    <Col lg={7}>
                        <div style={{
                            background: 'linear-gradient(145deg, #f8fdf9, #e8f5e9)',
                            borderRadius: 30,
                            padding: '40px 30px 50px',
                            position: 'relative',
                            height: '100%',
                            minHeight: 550,
                            border: '1px solid rgba(45,125,70,0.1)',
                            boxShadow: '0 20px 60px rgba(45,125,70,0.08)',
                            overflow: 'hidden',
                            display: 'flex',
                            flexDirection: 'column'
                        }}>
                            <div style={{
                                position: 'absolute',
                                top: -50,
                                right: -50,
                                width: 200,
                                height: 200,
                                borderRadius: '50%',
                                background: 'radial-gradient(circle, rgba(45,125,70,0.05) 0%, transparent 70%)',
                                pointerEvents: 'none'
                            }} />
                            <div style={{
                                position: 'absolute',
                                bottom: -30,
                                left: -30,
                                width: 150,
                                height: 150,
                                borderRadius: '50%',
                                background: 'radial-gradient(circle, rgba(76,175,80,0.05) 0%, transparent 70%)',
                                pointerEvents: 'none'
                            }} />

                            <div style={{
                                position: 'absolute',
                                top: 50,
                                bottom: 60,
                                left: 35,
                                width: 50,
                                borderRight: '2px solid rgba(45,125,70,0.15)',
                                zIndex: 1
                            }}>
                                {[180, 150, 120, 90, 60, 30].map(h => (
                                    <div key={h} style={{
                                        position: 'absolute',
                                        width: '100%',
                                        bottom: `${(h / 180) * 100}%`,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8
                                    }}>
                                        <div style={{
                                            flex: 1,
                                            height: 1,
                                            background: 'rgba(45,125,70,0.2)',
                                            borderTop: '1px dashed rgba(45,125,70,0.15)'
                                        }} />
                                        <span style={{
                                            fontSize: 10,
                                            color: '#2D7D46',
                                            fontWeight: 600,
                                            background: 'rgba(255,255,255,0.8)',
                                            padding: '2px 6px',
                                            borderRadius: 4
                                        }}>{h}cm</span>
                                    </div>
                                ))}
                            </div>

                            <div style={{
                                display: 'flex',
                                alignItems: 'flex-end',
                                justifyContent: 'center',
                                gap: 'clamp(20px, 5vw, 50px)',
                                flex: 1,
                                paddingLeft: 50,
                                paddingBottom: 10,
                                position: 'relative',
                                zIndex: 2,
                                minHeight: 420
                            }}>
                                {sizes.map((s, index) => (
                                    <div 
                                        key={index}
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            height: s.height,
                                            minHeight: s.minHeight,
                                            width: index === 0 ? 70 : index === 1 ? 80 : 90,
                                            background: hoveredSize === index 
                                                ? 'rgba(255,255,255,0.95)' 
                                                : 'rgba(255,255,255,0.6)',
                                            borderRadius: '16px 16px 0 0',
                                            padding: '10px 8px 14px',
                                            border: hoveredSize === index 
                                                ? `2px solid ${s.color}` 
                                                : '1px solid rgba(45,125,70,0.08)',
                                            backdropFilter: 'blur(4px)',
                                            transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                            boxShadow: hoveredSize === index 
                                                ? '0 12px 40px rgba(45,125,70,0.2)' 
                                                : 'none',
                                            transform: hoveredSize === index 
                                                ? 'scale(1.08) translateY(-12px)' 
                                                : 'scale(1) translateY(0)',
                                            cursor: 'pointer'
                                        }}
                                        onMouseEnter={() => setHoveredSize(index)}
                                        onMouseLeave={() => setHoveredSize(null)}
                                    >
                                        <div style={{ 
                                            width: '100%', 
                                            flex: 1,
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            overflow: 'hidden',
                                            borderRadius: 8,
                                            background: hoveredSize === index 
                                                ? 'rgba(45,125,70,0.05)' 
                                                : 'transparent',
                                            transition: 'background 0.3s ease'
                                        }}>
                                            <LazyImage 
                                                src={s.image} 
                                                alt={s.label} 
                                                sectionKey="sizeGuide"
                                                style={{ 
                                                    width: '100%', 
                                                    height: '100%', 
                                                    objectFit: 'contain',
                                                    transition: 'transform 0.4s ease'
                                                }} 
                                                onError={(e) => { 
                                                    e.target.src = `https://via.placeholder.com/${index === 0 ? '60x60' : index === 1 ? '70x150' : '80x250'}/2D7D46/fff?text=${index === 0 ? '🌱' : index === 1 ? '🌿' : '🌳'}`; 
                                                }} 
                                            />
                                        </div>
                                        <div style={{
                                            marginTop: 8,
                                            fontSize: 10,
                                            fontWeight: 700,
                                            color: hoveredSize === index ? s.color : '#2D7D46',
                                            textTransform: 'uppercase',
                                            letterSpacing: 0.5,
                                            transition: 'color 0.3s ease'
                                        }}>
                                            {s.label.split(' ')[0]}
                                        </div>
                                        {hoveredSize === index && (
                                            <div style={{
                                                fontSize: 9,
                                                color: '#888',
                                                marginTop: 2,
                                                textTransform: 'uppercase',
                                                letterSpacing: 0.3
                                            }}>
                                                {s.range}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>

                            <div style={{
                                position: 'absolute',
                                bottom: 16,
                                left: 0,
                                right: 0,
                                textAlign: 'center',
                                color: '#888',
                                fontSize: 12,
                                fontWeight: 500,
                                letterSpacing: 0.5,
                                zIndex: 2,
                                background: 'rgba(255,255,255,0.6)',
                                padding: '6px 20px',
                                backdropFilter: 'blur(4px)',
                                width: 'fit-content',
                                margin: '0 auto',
                                borderRadius: 20,
                                transition: 'all 0.3s ease'
                            }}>
                                <FaInfoCircle style={{ marginRight: 6, color: '#2D7D46' }} />
                                {hoveredSize !== null 
                                    ? `Showing: ${sizes[hoveredSize].label} (${sizes[hoveredSize].range})` 
                                    : 'Hover over plants to see them grow ✨'}
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>
        </section>
    );
}

// 15. FAQ SECTION
function FaqSection({ openFaq, setOpenFaq }) {
    const faqs = [
        { icon: <FaTint />, iconColor: '#2196F3', iconBg: 'rgba(33, 150, 243, 0.15)', tag: 'Watering', q: 'How often should I water my indoor plants?', a: "Most indoor plants prefer their soil to dry out slightly between waterings. Check the top inch of soil — if it's dry, it's time to water. Overwatering is the #1 cause of plant death!" },
        { icon: <FaSun />, iconColor: '#F57F17', iconBg: 'rgba(245, 127, 23, 0.15)', tag: 'Light', q: 'Which plants are best for low-light rooms?', a: "Snake Plants, ZZ Plants, and Pothos varieties are perfect for rooms with minimal natural light. They're hardy, air-purifying, and nearly indestructible — ideal for beginners!" },
        { icon: <FaRulerCombined />, iconColor: '#4caf50', iconBg: 'rgba(76, 175, 80, 0.15)', tag: 'Services', q: 'Do you offer landscape consultation?', a: 'Absolutely! We provide professional on-site landscape consultation with 3D design rendering for both residential and commercial projects across the UAE. Book via WhatsApp.' },
        { icon: <FaStore />, iconColor: '#E040FB', iconBg: 'rgba(224, 64, 251, 0.15)', tag: 'Delivery', q: 'Can you deliver plants to my home?', a: 'Yes! We offer home delivery for bulk orders and large plants across Sharjah, Dubai, and Ajman. Contact our WhatsApp support to check availability in your area.' },
        { icon: <FaSeedling />, iconColor: '#81C784', iconBg: 'rgba(129, 199, 132, 0.15)', tag: 'Potting', q: 'How do I choose the right pot size?', a: 'Choose a pot 2–3 cm larger than the root ball. Too big a pot holds excess moisture and causes root rot. Always ensure pots have drainage holes at the bottom.' },
    ];

    const tips = [
        { icon: <FaSun size={18} />, iconColor: '#FFA726', iconBg: 'rgba(255, 167, 38, 0.12)', label: 'Lighting', badge: 'Daily', tip: 'Indirect sunlight is best for most tropicals. Avoid harsh afternoon sun through glass.' },
        { icon: <FaTint size={18} />, iconColor: '#42A5F5', iconBg: 'rgba(66, 165, 245, 0.12)', label: 'Humidity', badge: 'Morning', tip: 'Group plants together to naturally boost humidity. Mist in the morning, not evening.' },
        { icon: <FaLeaf size={18} />, iconColor: '#66BB6A', iconBg: 'rgba(102, 187, 106, 0.12)', label: 'Cleaning', badge: 'Weekly', tip: 'Wipe leaves with a damp cloth weekly. Dusty leaves block sunlight and reduce growth.' },
        { icon: <FaSeedling size={18} />, iconColor: '#FFA726', iconBg: 'rgba(255, 167, 38, 0.12)', label: 'Feeding', badge: 'Monthly', tip: 'Use slow-release organic fertilizer every 4–6 weeks during spring and summer growth season.' },
    ];

    return (
        <section id="faq-section" style={{ 
            padding: '80px 0', 
            background: 'linear-gradient(135deg, #0d3320 0%, #1a5c32 100%)' 
        }}>
            <Container>
                <div style={{ textAlign: 'center', marginBottom: 60 }}>
                    <span style={{ 
                        display: 'inline-block', 
                        background: 'linear-gradient(135deg, #c9a03d, #b8860b)', 
                        color: '#fff', 
                        padding: '6px 24px', 
                        borderRadius: 30, 
                        fontSize: 12, 
                        fontWeight: 700, 
                        letterSpacing: 2, 
                        textTransform: 'uppercase', 
                        marginBottom: 16 
                    }}>
                        <FaLeaf style={{ marginRight: 8 }} /> Plant Expertise
                    </span>
                    <h2 style={{ fontSize: '2.2rem', color: '#ffffff', fontWeight: 700, marginBottom: 12 }}>
                        Frequently Asked Questions
                    </h2>
                    <p style={{ maxWidth: 500, color: 'rgba(255,255,255,0.8)', margin: '0 auto' }}>
                        Expert answers to help your garden thrive
                    </p>
                </div>
                
                <Row className="g-5 align-items-stretch">
                    <Col lg={7}>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, height: '100%' }}>
                            {faqs.map((item, i) => {
                                const isOpen = openFaq === i;
                                return (
                                    <div 
                                        key={i}
                                        onClick={() => setOpenFaq(isOpen ? null : i)}
                                        style={{
                                            background: isOpen ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.06)',
                                            backdropFilter: 'blur(12px)',
                                            WebkitBackdropFilter: 'blur(12px)',
                                            borderRadius: 20,
                                            border: isOpen ? '1px solid rgba(255, 255, 255, 0.25)' : '1px solid rgba(255, 255, 255, 0.1)',
                                            padding: '22px 24px',
                                            boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.15)',
                                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                            cursor: 'pointer',
                                            flex: 1,
                                        }}
                                        onMouseEnter={e => {
                                            if (!isOpen) {
                                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.12)';
                                                e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.2)';
                                            }
                                        }}
                                        onMouseLeave={e => {
                                            if (!isOpen) {
                                                e.currentTarget.style.background = 'rgba(255, 255, 255, 0.06)';
                                                e.currentTarget.style.border = '1px solid rgba(255, 255, 255, 0.1)';
                                            }
                                        }}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                                <div style={{ 
                                                    width: 38, height: 38, borderRadius: 12, 
                                                    background: item.iconBg, color: item.iconColor,
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0
                                                }}>
                                                    {item.icon}
                                                </div>
                                                <div>
                                                    <span style={{ fontSize: 11, fontWeight: 700, color: item.iconColor, textTransform: 'uppercase', letterSpacing: 1 }}>{item.tag}</span>
                                                    <h4 style={{ fontSize: 15, fontWeight: 600, color: '#ffffff', margin: '2px 0 0 0' }}>{item.q}</h4>
                                                </div>
                                            </div>
                                            <div style={{ color: isOpen ? '#c9a03d' : 'rgba(255,255,255,0.4)', fontSize: 18, transition: 'transform 0.3s', transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)' }}>
                                                ＋
                                            </div>
                                        </div>

                                        <div style={{ 
                                            maxHeight: isOpen ? '200px' : '0px', 
                                            overflow: 'hidden', 
                                            transition: 'all 0.3s ease-in-out',
                                            opacity: isOpen ? 1 : 0
                                        }}>
                                            <div style={{ padding: '14px 0 0 52px', fontSize: 14, color: 'rgba(255, 255, 255, 0.75)', lineHeight: 1.6 }}>
                                                {item.a}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </Col>
                    
                    <Col lg={5}>
                        <div style={{ 
                            position: 'sticky', 
                            top: 30, 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: 16,
                            height: '100%',
                        }}>
                            <div style={{ 
                                background: 'rgba(255,255,255,0.08)',
                                backdropFilter: 'blur(12px)',
                                WebkitBackdropFilter: 'blur(12px)',
                                borderRadius: 24, 
                                padding: '24px 28px', 
                                color: '#fff',
                                border: '1px solid rgba(255,255,255,0.12)',
                                boxShadow: '0 8px 30px rgba(0,0,0,0.1)',
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                                    <div style={{ 
                                        width: 44, height: 44, 
                                        background: 'rgba(255,255,255,0.12)', 
                                        borderRadius: 14, 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        fontSize: 20,
                                        color: '#c9a03d'
                                    }}>
                                        <FaLeaf />
                                    </div>
                                    <div>
                                        <div style={{ fontWeight: 700, fontSize: 17, color: '#ffffff' }}>Quick Care Tips</div>
                                        <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)' }}>Expert-curated for UAE climate</div>
                                    </div>
                                </div>
                            </div>

                            {tips.map((tip, i) => (
                                <div 
                                    key={i} 
                                    style={{ 
                                        background: 'rgba(255,255,255,0.05)',
                                        backdropFilter: 'blur(10px)',
                                        WebkitBackdropFilter: 'blur(10px)',
                                        borderRadius: 18, 
                                        padding: '18px 22px', 
                                        border: '1px solid rgba(255,255,255,0.08)', 
                                        display: 'flex', 
                                        gap: 14, 
                                        alignItems: 'flex-start', 
                                        boxShadow: '0 2px 12px rgba(0,0,0,0.08)', 
                                        transition: 'all 0.25s ease',
                                        flex: 1,
                                    }}
                                    onMouseEnter={e => { 
                                        e.currentTarget.style.transform = 'translateX(4px)'; 
                                        e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.12)'; 
                                        e.currentTarget.style.background = 'rgba(255,255,255,0.09)';
                                        e.currentTarget.style.border = '1px solid rgba(255,255,255,0.15)';
                                    }}
                                    onMouseLeave={e => { 
                                        e.currentTarget.style.transform = 'translateX(0)'; 
                                        e.currentTarget.style.boxShadow = '0 2px 12px rgba(0,0,0,0.08)'; 
                                        e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                                        e.currentTarget.style.border = '1px solid rgba(255,255,255,0.08)';
                                    }}
                                >
                                    <div style={{ 
                                        width: 38, height: 38, 
                                        borderRadius: 12, 
                                        background: tip.iconBg, 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        justifyContent: 'center', 
                                        color: tip.iconColor, 
                                        flexShrink: 0,
                                        fontSize: 18
                                    }}>
                                        {tip.icon}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                            <span style={{ fontWeight: 600, color: '#ffffff', fontSize: 14, opacity: 0.9 }}>{tip.label}</span>
                                            <span style={{ 
                                                fontSize: 10, 
                                                fontWeight: 600, 
                                                background: 'rgba(255,255,255,0.08)', 
                                                color: 'rgba(255,255,255,0.5)', 
                                                padding: '2px 12px', 
                                                borderRadius: 20,
                                                letterSpacing: 0.5
                                            }}>
                                                {tip.badge}
                                            </span>
                                        </div>
                                        <p style={{ 
                                            fontSize: 13, 
                                            color: 'rgba(255,255,255,0.6)', 
                                            margin: 0, 
                                            lineHeight: 1.5 
                                        }}>
                                            {tip.tip}
                                        </p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Col>
                </Row>
            </Container>
        </section>
    );
}

// 16. PLANT CARE GUIDE
function PlantCareGuide() {
    return (
        <section id="care-guide-section" style={{ padding: '80px 0', background: '#f8fafc' }}>
            <Container>
                <div style={{ textAlign: 'center', marginBottom: '60px' }}>
                    <span style={{
                        display: 'inline-block',
                        background: 'linear-gradient(135deg, #2D7D46, #4CAF50)',
                        color: '#fff',
                        padding: '8px 24px',
                        borderRadius: 50,
                        fontSize: 11,
                        fontWeight: 700,
                        letterSpacing: '2px',
                        textTransform: 'uppercase',
                        marginBottom: 16,
                        fontFamily: "'Inter', sans-serif",
                        boxShadow: '0 4px 12px rgba(45,125,70,0.15)'
                    }}>
                        <FaLeaf style={{ marginRight: 8, verticalAlign: 'middle' }} /> Plant Care Guide
                    </span>
                    <h2 style={{ 
                        fontFamily: "'Playfair Display', Georgia, serif",
                        fontSize: 'clamp(2rem, 5vw, 2.8rem)',
                        fontWeight: 800,
                        color: '#1B5E20',
                        marginBottom: 16,
                        lineHeight: '1.3'
                    }}>
                        How to Keep Your Plants Thriving in the UAE
                    </h2>
                    <p style={{ 
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '16px',
                        color: '#64748B',
                        maxWidth: '580px',
                        margin: '0 auto',
                        lineHeight: '1.8',
                        fontWeight: 400,
                    }}>
                        Expert tips for every season. Whether you're a beginner or a pro, these care guides will help your plants flourish in desert climates.
                    </p>
                </div>

                <Row className="g-4">
                    <Col lg={4} md={6}>
                        <div style={{
                            background: '#fff',
                            borderRadius: '24px',
                            padding: '32px 28px 24px 28px',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.03)',
                            border: '1px solid #e2e8f0',
                            height: '100%',
                            display: 'flex',
                            flexDirection: 'column',
                            transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-8px)';
                            e.currentTarget.style.boxShadow = '0 20px 40px rgba(45,125,70,0.08)';
                            e.currentTarget.style.borderColor = '#c2e7cc';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.03)';
                            e.currentTarget.style.borderColor = '#e2e8f0';
                        }}
                        >
                            <div style={{
                                width: '60px', height: '60px', borderRadius: '16px',
                                background: 'linear-gradient(135deg, #E3F2FD, #BBDEFB)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '26px', marginBottom: '24px', flexShrink: 0,
                            }}>💧</div>
                            
                            <h4 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: '#1B5E20', fontSize: '20px', marginBottom: '14px' }}>Watering Guide</h4>
                            
                            <div style={{ fontFamily: "'Inter', sans-serif", color: '#475569', fontSize: '14px', lineHeight: '1.7', flex: 1, marginBottom: '20px' }}>
                                <p style={{ marginBottom: '12px' }}>In the UAE's hot climate, watering schedule depends heavily on location:</p>
                                <ul style={{ paddingLeft: '18px', margin: 0, display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <li><strong>Indoor Plants:</strong> Every 3-5 days.</li>
                                    <li><strong>Outdoor Plants:</strong> Daily watering during peak summer.</li>
                                </ul>
                                <p style={{ marginTop: '12px', fontSize: '13px', color: '#64748B', fontStyle: 'italic' }}>* Always check the top inch of soil before watering.</p>
                            </div>

                            <div style={{ padding: '12px 16px', background: '#f0faf3', borderRadius: '12px', borderLeft: '4px solid #2D7D46', marginBottom: '20px' }}>
                                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', fontWeight: 700, color: '#2D7D46', marginBottom: '2px', display: 'flex', alignItems: 'center' }}>
                                    <FaInfoCircle style={{ marginRight: 6 }} /> PRO TIP
                                </div>
                                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: '#334155', lineHeight: '1.5' }}>Water early morning or after sunset to prevent instant evaporation.</span>
                            </div>

                            <button
                                style={{
                                    fontFamily: "'Inter', sans-serif", borderRadius: '50px', fontWeight: 600, fontSize: '13px', padding: '10px 16px',
                                    border: '2px solid #2D7D46', color: '#2D7D46', backgroundColor: 'transparent', transition: 'all 0.3s ease',
                                    width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#2D7D46'; e.currentTarget.style.color = '#fff'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#2D7D46'; }}
                                onClick={() => {
                                    const msg = encodeURIComponent("Hi! I need more watering tips for my plants.");
                                    window.open(`https://wa.me/971552698951?text=${msg}`, '_blank');
                                }}
                            >
                                <FaWhatsapp size={15} /> Ask for more tips
                            </button>
                        </div>
                    </Col>

                    <Col lg={4} md={6}>
                        <div style={{
                            background: '#fff', borderRadius: '24px', padding: '32px 28px 24px 28px',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0', height: '100%',
                            display: 'flex', flexDirection: 'column', transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-8px)';
                            e.currentTarget.style.boxShadow = '0 20px 40px rgba(45,125,70,0.08)';
                            e.currentTarget.style.borderColor = '#c2e7cc';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.03)';
                            e.currentTarget.style.borderColor = '#e2e8f0';
                        }}
                        >
                            <div style={{
                                width: '60px', height: '60px', borderRadius: '16px',
                                background: 'linear-gradient(135deg, #FFF8E1, #FFECB3)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '26px', marginBottom: '24px', flexShrink: 0,
                            }}>☀️</div>
                            
                            <h4 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: '#1B5E20', fontSize: '20px', marginBottom: '14px' }}>Sunlight Guide</h4>
                            
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', flex: 1, marginBottom: '20px', fontFamily: "'Inter', sans-serif", fontSize: '14px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #e2e8f0', paddingBottom: '6px' }}>
                                    <span style={{ color: '#1B5E20', fontWeight: 600 }}>☀️ Direct Sun</span>
                                    <span style={{ color: '#475569', fontSize: '13px' }}>Cacti, Bougainvillea</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px dashed #e2e8f0', paddingBottom: '6px' }}>
                                    <span style={{ color: '#1B5E20', fontWeight: 600 }}>🌤️ Indirect Light</span>
                                    <span style={{ color: '#475569', fontSize: '13px' }}>Snake Plant, ZZ, Pothos</span>
                                </div>
                                <div style={{ display: 'flex', justifyContent: 'space-between', paddingBottom: '6px' }}>
                                    <span style={{ color: '#1B5E20', fontWeight: 600 }}>🍃 Low Light</span>
                                    <span style={{ color: '#475569', fontSize: '13px' }}>Peace Lily, Ferns</span>
                                </div>
                            </div>

                            <div style={{ padding: '12px 16px', background: '#fbf7f0', borderRadius: '12px', borderLeft: '4px solid #F57F17', marginBottom: '20px' }}>
                                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', fontWeight: 700, color: '#F57F17', marginBottom: '2px', display: 'flex', alignItems: 'center' }}>
                                    <FaSun style={{ marginRight: 6 }} /> PRO TIP
                                </div>
                                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: '#334155', lineHeight: '1.5' }}>Rotate your plants 90 degrees weekly for uniform growth.</span>
                            </div>

                            <button
                                style={{
                                    fontFamily: "'Inter', sans-serif", borderRadius: '50px', fontWeight: 600, fontSize: '13px', padding: '10px 16px',
                                    border: '2px solid #2D7D46', color: '#2D7D46', backgroundColor: 'transparent', transition: 'all 0.3s ease',
                                    width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#2D7D46'; e.currentTarget.style.color = '#fff'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#2D7D46'; }}
                                onClick={() => {
                                    const msg = encodeURIComponent("Hi! I need advice on sunlight for my plants.");
                                    window.open(`https://wa.me/971552698951?text=${msg}`, '_blank');
                                }}
                            >
                                <FaWhatsapp size={15} /> Ask for advice
                            </button>
                        </div>
                    </Col>

                    <Col lg={4} md={12}>
                        <div style={{
                            background: '#fff', borderRadius: '24px', padding: '32px 28px 24px 28px',
                            boxShadow: '0 10px 30px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0', height: '100%',
                            display: 'flex', flexDirection: 'column', transition: 'all 0.4s cubic-bezier(0.16, 1, 0.3, 1)',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.transform = 'translateY(-8px)';
                            e.currentTarget.style.boxShadow = '0 20px 40px rgba(45,125,70,0.08)';
                            e.currentTarget.style.borderColor = '#c2e7cc';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.03)';
                            e.currentTarget.style.borderColor = '#e2e8f0';
                        }}
                        >
                            <div style={{
                                width: '60px', height: '60px', borderRadius: '16px',
                                background: 'linear-gradient(135deg, #E8F5E9, #C8E6C9)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '26px', marginBottom: '24px', flexShrink: 0,
                            }}>🌱</div>
                            
                            <h4 style={{ fontFamily: "'Playfair Display', serif", fontWeight: 700, color: '#1B5E20', fontSize: '20px', marginBottom: '14px' }}>Seasonal Guide</h4>
                            
                            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '14px', color: '#475569', flex: 1, marginBottom: '20px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                <div>
                                    <span style={{ color: '#d32f2f', fontWeight: 600 }}>Summer (May-Sept):</span> 
                                    <span style={{ fontSize: '13px', display: 'block', color: '#64748B' }}>Water daily, provide shade & mist indoors.</span>
                                </div>
                                <div>
                                    <span style={{ color: '#0284c7', fontWeight: 600 }}>Winter (Nov-Feb):</span> 
                                    <span style={{ fontSize: '13px', display: 'block', color: '#64748B' }}>Reduce watering, protect from cold winds.</span>
                                </div>
                                <div>
                                    <span style={{ color: '#059669', fontWeight: 600 }}>Spring & Fall:</span> 
                                    <span style={{ fontSize: '13px', display: 'block', color: '#64748B' }}>Best window for fertilizing and repotting.</span>
                                </div>
                            </div>

                            <div style={{ padding: '12px 16px', background: '#f0faf3', borderRadius: '12px', borderLeft: '4px solid #2D7D46', marginBottom: '20px' }}>
                                <div style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', fontWeight: 700, color: '#2D7D46', marginBottom: '2px', display: 'flex', alignItems: 'center' }}>
                                    <FaSeedling style={{ marginRight: 6 }} /> GOLDEN PERIOD
                                </div>
                                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: '#334155', lineHeight: '1.5' }}><strong>October to March</strong> is the prime time for outdoor planting in UAE.</span>
                            </div>

                            <button
                                style={{
                                    fontFamily: "'Inter', sans-serif", borderRadius: '50px', fontWeight: 600, fontSize: '13px', padding: '10px 16px',
                                    border: '2px solid #2D7D46', color: '#2D7D46', backgroundColor: 'transparent', transition: 'all 0.3s ease',
                                    width: '100%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '8px', cursor: 'pointer',
                                }}
                                onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#2D7D46'; e.currentTarget.style.color = '#fff'; }}
                                onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#2D7D46'; }}
                                onClick={() => {
                                    const msg = encodeURIComponent("Hi! What should I plant this season?");
                                    window.open(`https://wa.me/971552698951?text=${msg}`, '_blank');
                                }}
                            >
                                <FaWhatsapp size={15} /> Ask for recommendations
                            </button>
                        </div>
                    </Col>
                </Row>

                <Row className="g-4 mt-4">
<Col lg={7} md={12}>
    <div style={{
        background: '#fff', borderRadius: '24px', padding: '32px',
        boxShadow: '0 10px 30px rgba(0,0,0,0.03)', border: '1px solid #e2e8f0', height: '100%',
    }}>
        <h5 style={{ 
            fontFamily: "'Playfair Display', serif", fontWeight: 700, color: '#1B5E20', 
            fontSize: '18px', marginBottom: '20px', display: 'flex', alignItems: 'center' 
        }}>
            <FaInfoCircle style={{ color: '#2D7D46', marginRight: 10 }} /> Plant Health Quick-Fix
        </h5>
        
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '14px' }}>
            <div style={{ padding: '14px', background: '#fff5f5', borderRadius: '14px', border: '1px solid #fee2e2' }}>
                <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, color: '#e11d48', fontSize: '13px', display: 'block', marginBottom: '4px' }}>🍁 Yellowing Leaves</span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: '#64748B' }}>Overwatering. Let the soil completely dry out.</span>
            </div>
            
            <div style={{ padding: '14px', background: '#fffbeb', borderRadius: '14px', border: '1px solid #fef3c7' }}>
                <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, color: '#d97706', fontSize: '13px', display: 'block', marginBottom: '4px' }}>🍂 Crispy Brown Edges</span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: '#64748B' }}>Underwatering or low humidity. Increase frequency.</span>
            </div>
            
            <div style={{ padding: '14px', background: '#faf5ff', borderRadius: '14px', border: '1px solid #f3e8ff' }}>
                <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, color: '#9333ea', fontSize: '13px', display: 'block', marginBottom: '4px' }}>🥀 Drooping Stems</span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: '#64748B' }}>Thirsty or root bound. Check moisture deep inside soil.</span>
            </div>
            
            <div style={{ padding: '14px', background: '#f0fdf4', borderRadius: '14px', border: '1px solid #dcfce7' }}>
                <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, color: '#16a34a', fontSize: '13px', display: 'block', marginBottom: '4px' }}>🛑 Stagnant Growth</span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: '#64748B' }}>Lacks nutrients. Needs organic fertilizer or light boost.</span>
            </div>
            
            <div style={{ padding: '14px', background: '#e8eaf6', borderRadius: '14px', border: '1px solid #c5cae9' }}>
                <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, color: '#283593', fontSize: '13px', display: 'block', marginBottom: '4px' }}>🌱 Root Rot</span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: '#64748B' }}>Foul smell from soil. Repot with fresh, well-draining soil.</span>
            </div>
            
           

            {/* NEW: Leaf Spot Disease */}
            <div style={{ padding: '14px', background: '#e0f7fa', borderRadius: '14px', border: '1px solid #b2ebf2' }}>
                <span style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, color: '#00695c', fontSize: '13px', display: 'block', marginBottom: '4px' }}>🔴 Leaf Spots</span>
                <span style={{ fontFamily: "'Inter', sans-serif", fontSize: '12px', color: '#64748B' }}>Black spots? Remove infected leaves & apply fungicide spray.</span>
            </div>

          
        </div>
    </div>
</Col>
                   <Col lg={5} md={12}>
                        <div style={{
                            background: 'linear-gradient(135deg, #f5f0e8, #e8e0d5, #ddd5c8)',
                            borderRadius: '24px',
                            padding: '40px 32px', 
                            color: '#1B5E20',
                            textAlign: 'center', 
                            height: '100%',
                            display: 'flex', 
                            flexDirection: 'column', 
                            alignItems: 'center', 
                            justifyContent: 'center',
                            boxShadow: '0 12px 30px rgba(129,199,132,0.3)'
                        }}>
                            <div style={{ fontSize: '46px', marginBottom: '16px' }}>🪴</div>
                            <h5 style={{ 
                                fontFamily: "'Playfair Display', serif", 
                                fontWeight: 700, 
                                fontSize: '20px', 
                                marginBottom: '10px',
                                color: '#1B5E20'
                            }}>
                                Struggling with a dying plant?
                            </h5>
                            <p style={{ 
                                fontFamily: "'Inter', sans-serif", 
                                fontSize: '14px', 
                                opacity: 0.85, 
                                marginBottom: '24px', 
                                fontWeight: 400, 
                                lineHeight: '1.6', 
                                maxWidth: '340px',
                                color: '#2E7D32'
                            }}>
                                Don't worry! Snap a picture of your plant and send it to our UAE gardening experts.
                            </p>
                            <a 
                                href="https://wa.me/971552698951?text=Hi!%20I%20need%20help%20with%20my%20plants" 
                                target="_blank" 
                                rel="noreferrer"
                                style={{
                                    display: 'inline-flex', 
                                    alignItems: 'center', 
                                    justifyContent: 'center', 
                                    gap: '10px',
                                    background: '#25D366',
                                    color: '#fff',
                                    padding: '10px 28px',
                                    borderRadius: '50px',
                                    border: 'none',
                                    fontWeight: 600, 
                                    fontSize: '14px', 
                                    fontFamily: "'Inter', sans-serif", 
                                    textDecoration: 'none',
                                    transition: 'all 0.3s ease', 
                                    minWidth: '150px',
                                    boxShadow: '0 4px 15px rgba(37,211,102,0.3)'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#128C7E';
                                    e.currentTarget.style.transform = 'translateY(-2px) scale(1.02)';
                                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(37,211,102,0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = '#25D366';
                                    e.currentTarget.style.transform = 'translateY(0) scale(1)';
                                    e.currentTarget.style.boxShadow = '0 4px 15px rgba(37,211,102,0.3)';
                                }}
                            >
                                <FaWhatsapp size={16} /> WhatsApp
                            </a>
                        </div>
                    </Col>
                </Row>
            </Container>
        </section>
    );
}

// 17. CTA BANNER
function CtaBanner() {
    const [email, setEmail] = useState('');
    const [subscribed, setSubscribed] = useState(false);

    const handleSubscribe = (e) => {
        e.preventDefault();
        if (email) {
            setSubscribed(true);
            setTimeout(() => setSubscribed(false), 3000);
            setEmail('');
        }
    };

    const workers = [
        {
            name: "Mr. Usman",
            role: "Landscape Design & Project Management",
            image: personworker,
            phone: "+971 52 662 9183",
            whatsapp: "971526629183",
            Specialty: "Landscape Design & Project Management",
            icon: "🌱"
        },
        {
            name: "Mr. M.Ejaz",
            role: "Sales & Client Relations",
            image: personowner,
            phone: "+971 52 869 3972",
            whatsapp: "971528693972",
            specialty: "Sales & Client Relations",
            icon: "🌿"
        }
        
    ];

    return (
        <section style={{
            padding: '50px 20px',
            background: '#133e27',
            position: 'relative',
            overflow: 'hidden',
        }}>
            {/* Decorative Background */}
            <div style={{
                position: 'absolute',
                top: '-30%',
                right: '-10%',
                width: '400px',
                height: '400px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(201,160,61,0.06) 0%, transparent 70%)',
                pointerEvents: 'none',
            }} />
            <div style={{
                position: 'absolute',
                bottom: '-20%',
                left: '-5%',
                width: '300px',
                height: '300px',
                borderRadius: '50%',
                background: 'radial-gradient(circle, rgba(37,211,102,0.04) 0%, transparent 70%)',
                pointerEvents: 'none',
            }} />

            <div style={{
                maxWidth: '1200px',
                margin: '0 auto',
            }}>
                {/* Header */}
                <div style={{
                    textAlign: 'center',
                    marginBottom: '40px',
                }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px',
                        marginBottom: '6px',
                    }}>
                        <GiFlowerPot size={28} color="#c9a03d" />
                        <span style={{
                            color: '#b8a686',
                            fontSize: '11px',
                            fontWeight: 700,
                            letterSpacing: '2.5px',
                            textTransform: 'uppercase',
                            fontFamily: "'Inter', sans-serif",
                        }}>
                            STAY CONNECTED
                        </span>
                    </div>
                    <h2 style={{
                        fontFamily: "'Playfair Display', Georgia, serif",
                        fontSize: 'clamp(1.8rem, 3.2vw, 2.6rem)',
                        fontWeight: 700,
                        color: '#ffffff',
                        margin: '0 0 8px 0',
                        lineHeight: '1.2',
                    }}>
                        Ready to Green Up Your Space?
                    </h2>
                    <p style={{
                        fontFamily: "'Inter', sans-serif",
                        fontSize: '15px',
                        color: 'rgba(255,255,255,0.6)',
                        margin: 0,
                        fontWeight: 300,
                    }}>
                        Join our community for new arrivals & exclusive deals.
                    </p>
                </div>

                {/* Main Content - Split Layout */}
                <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '40px',
                    justifyContent: 'center',
                    alignItems: 'stretch',
                }}>

                    {/* LEFT SIDE - Email Subscription */}
                    <div style={{
                        flex: '1 1 380px',
                        maxWidth: '500px',
                        background: 'rgba(255,255,255,0.04)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '24px',
                        padding: '32px 28px',
                        border: '1px solid rgba(255,255,255,0.06)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            marginBottom: '16px',
                        }}>
                            <span style={{
                                fontSize: '28px',
                            }}>📬</span>
                            <div>
                                <h4 style={{
                                    color: '#ffffff',
                                    fontSize: '18px',
                                    fontWeight: 700,
                                    margin: 0,
                                    fontFamily: "'Playfair Display', Georgia, serif",
                                }}>
                                    Subscribe to Our Newsletter
                                </h4>
                                <p style={{
                                    color: 'rgba(255,255,255,0.4)',
                                    fontSize: '12px',
                                    margin: '2px 0 0 0',
                                    fontFamily: "'Inter', sans-serif",
                                }}>
                                    Get updates on new arrivals & special offers
                                </p>
                            </div>
                        </div>

                        <form onSubmit={handleSubscribe} style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px',
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                background: 'rgba(255, 255, 255, 0.06)',
                                borderRadius: '50px',
                                padding: '4px',
                                border: '1px solid rgba(255, 255, 255, 0.08)',
                                transition: 'border-color 0.3s ease',
                            }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.borderColor = 'rgba(201,160,61,0.3)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.borderColor = 'rgba(255,255,255,0.08)';
                                }}>
                                <input
                                    type="email"
                                    placeholder="Email address"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    style={{
                                        flex: 1,
                                        padding: '14px 20px',
                                        background: 'transparent',
                                        border: 'none',
                                        outline: 'none',
                                        color: '#fff',
                                        fontSize: '14px',
                                        fontFamily: "'Inter', sans-serif",
                                        minWidth: '0',
                                    }}
                                />
                                <button style={{
                                    padding: '12px 32px',
                                    background: '#d49a2a',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '50px',
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    whiteSpace: 'nowrap',
                                    fontFamily: "'Inter', sans-serif",
                                    boxShadow: '0 4px 12px rgba(212, 154, 42, 0.3)',
                                }}>
                                    Subscribe
                                </button>
                            </div>
                        </form>


                        {/* Trust Badges */}
                        <div style={{
                            display: 'flex',
                            gap: '20px',
                            marginTop: '16px',
                            justifyContent: 'center',
                            flexWrap: 'wrap',
                        }}>

                            <span style={{
                                color: 'rgba(255,255,255,0.25)',
                                fontSize: '11px',
                                fontFamily: "'Inter', sans-serif",
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                            }}>📧 Weekly updates</span>
                            <span style={{
                                color: 'rgba(255,255,255,0.25)',
                                fontSize: '11px',
                                fontFamily: "'Inter', sans-serif",
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                            }}>🎁 Exclusive offers</span>
                        </div>
                    </div>

                    {/* RIGHT SIDE - Two Workers */}
                    <div style={{
                        flex: '1 1 400px',
                        maxWidth: '520px',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '16px',
                    }}>
                        {/* Team Label */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            paddingLeft: '8px',
                        }}>
                            <span style={{
                                color: '#c9a03d',
                                fontSize: '12px',
                                fontWeight: 700,
                                letterSpacing: '1px',
                                textTransform: 'uppercase',
                                fontFamily: "'Inter', sans-serif",
                            }}>👥 Our Experts</span>
                            <div style={{
                                flex: 1,
                                height: '1px',
                                background: 'linear-gradient(90deg, rgba(201,160,61,0.3), transparent)',
                            }} />
                        </div>

                        {/* Worker Cards */}
                        <div style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: '16px',
                        }}>
                            {workers.map((worker, index) => (
                                <div key={index} style={{
                                    flex: '1 1 180px',
                                    minWidth: '160px',
                                    background: 'rgba(255,255,255,0.04)',
                                    backdropFilter: 'blur(10px)',
                                    borderRadius: '20px',
                                    padding: '20px 16px 16px',
                                    border: '1px solid rgba(255,255,255,0.06)',
                                    textAlign: 'center',
                                    transition: 'all 0.3s ease',
                                }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform = 'translateY(-4px)';
                                        e.currentTarget.style.background = 'rgba(255,255,255,0.08)';
                                        e.currentTarget.style.borderColor = 'rgba(201,160,61,0.2)';
                                        e.currentTarget.style.boxShadow = '0 8px 30px rgba(0,0,0,0.2)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.background = 'rgba(255,255,255,0.04)';
                                        e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                                        e.currentTarget.style.boxShadow = 'none';
                                    }}>
                                    {/* Profile */}
                                    <div style={{
                                        width: '70px',
                                        height: '70px',
                                        margin: '0 auto 10px',
                                        position: 'relative',
                                    }}>
                                        <div style={{
                                            position: 'absolute',
                                            inset: '-2px',
                                            borderRadius: '50%',
                                            background: 'linear-gradient(135deg, #c9a03d, #b8860b)',
                                            opacity: 0.3,
                                        }} />
                                        <img loading="lazy"
                                            src={worker.image}
                                            alt={worker.name}
                                            style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'cover',
                                                borderRadius: '50%',
                                                border: '2px solid #c9a03d',
                                                position: 'relative',
                                                zIndex: 1,
                                            }}
                                        />
                                        <div style={{
                                            position: 'absolute',
                                            bottom: '0',
                                            right: '0',
                                            width: '14px',
                                            height: '14px',
                                            borderRadius: '50%',
                                            background: '#4CAF50',
                                            border: '2px solid #133e27',
                                            zIndex: 2,
                                        }} />
                                    </div>

                                    {/* Info */}
                                    <h5 style={{
                                        color: '#ffffff',
                                        fontSize: '14px',
                                        fontWeight: 700,
                                        margin: '0 0 2px 0',
                                        fontFamily: "'Playfair Display', Georgia, serif",
                                    }}>
                                        {worker.name}
                                    </h5>
                                    <p style={{
                                        color: 'rgba(255,255,255,0.4)',
                                        fontSize: '10px',
                                        margin: '0 0 8px 0',
                                        fontFamily: "'Inter', sans-serif",
                                    }}>
                                        {worker.role}
                                    </p>

                                    {/* Contact Buttons */}
                                    <div style={{
                                        display: 'flex',
                                        gap: '6px',
                                        justifyContent: 'center',
                                        flexWrap: 'wrap',
                                    }}>
                                        <a
                                            href={`tel:${worker.phone}`}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                padding: '5px 12px',
                                                background: 'rgba(255,255,255,0.06)',
                                                borderRadius: '20px',
                                                color: '#c9a03d',
                                                fontSize: '10px',
                                                fontWeight: 600,
                                                textDecoration: 'none',
                                                fontFamily: "'Inter', sans-serif",
                                                border: '1px solid rgba(255,255,255,0.06)',
                                                transition: 'all 0.3s ease',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = 'rgba(201,160,61,0.15)';
                                                e.currentTarget.style.borderColor = 'rgba(201,160,61,0.3)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = 'rgba(255,255,255,0.06)';
                                                e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)';
                                            }}>
                                            📞 Call
                                        </a>
                                        <a
                                            href={`https://wa.me/${worker.whatsapp}`}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '4px',
                                                padding: '5px 12px',
                                                background: 'rgba(37, 211, 102, 0.1)',
                                                borderRadius: '20px',
                                                color: '#4fe08a',
                                                fontSize: '10px',
                                                fontWeight: 600,
                                                textDecoration: 'none',
                                                fontFamily: "'Inter', sans-serif",
                                                border: '1px solid rgba(37, 211, 102, 0.1)',
                                                transition: 'all 0.3s ease',
                                            }}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.background = 'rgba(37, 211, 102, 0.2)';
                                                e.currentTarget.style.borderColor = 'rgba(37, 211, 102, 0.3)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.background = 'rgba(37, 211, 102, 0.1)';
                                                e.currentTarget.style.borderColor = 'rgba(37, 211, 102, 0.1)';
                                            }}>
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="#25D366">
                                                <path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2.05 22l5.25-1.38c1.45.79 3.08 1.21 4.74 1.21 5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.816 9.816 0 0 0 12.04 2zm.01 16.5c-1.48 0-2.93-.4-4.2-1.15l-.3-.18-3.12.82.83-3.04-.2-.31a8.217 8.217 0 0 1-1.26-4.38c0-4.54 3.7-8.24 8.24-8.24 2.2 0 4.27.86 5.82 2.42a8.18 8.18 0 0 1 2.41 5.83c.02 4.54-3.68 8.23-8.22 8.23z" />
                                            </svg> WhatsApp
                                        </a>
                                    </div>

                                    {/* Phone number small */}

                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
            `}</style>
        </section>
    );
}



// 18. FOOTER
function Footer({ openLandscapeQuote, setActiveTab }) {
    const [isMobile, setIsMobile] = useState(false);
    
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        handleResize();
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const navLinks = [
        { text: 'Plants & Flowers', action: () => setActiveTab('plants') },
        { text: 'Get a Quote', action: () => openLandscapeQuote('both') },
    ];

    return (
        <footer style={{
            ...styles.footer,
            padding: isMobile ? '40px 0 20px' : '64px 0 28px',
        }}>
            <div style={styles.footerTopAccent} />
            
            {!isMobile && (
                <div style={{
                    position: 'absolute',
                    bottom: '0',
                    right: '0',
                    width: '43%',
                    height: '100%',
                    backgroundImage: 'url(/nursery/slideshow/banner.webp)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center left',
                    opacity: 1,
                    pointerEvents: 'none',
                    zIndex: 0,
                    borderTopLeftRadius: '80px',
                    filter: 'brightness(0.9) contrast(1.5)',
                }} />
            )}
            
            <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: isMobile 
                    ? 'linear-gradient(to bottom, #072415 0%, rgba(7,36,21,0.95) 100%)'
                    : 'linear-gradient(to right, #072415 55%, rgba(7,36,21,0.4) 100%)',
                pointerEvents: 'none',
                zIndex: 0
            }} />

            <Container style={{ position: 'relative', zIndex: 1 }}>
                <Row className="gy-4">
                    <Col lg={3} md={6} xs={12} style={{ 
                        marginLeft: isMobile ? '0' : '-20px', 
                        marginRight: isMobile ? '0' : '50px',
                        textAlign: isMobile ? 'center' : 'left',
                    }}>
                        <div style={styles.footerLogoWrapper}>
                            <div style={{
                                ...styles.footerLogoContainer,
                                alignItems: 'center',
                            }}>
                                <div style={styles.footerLogoImageWrapper}>
                                    <div style={styles.footerLogoGlowRing}></div>
                                    <img loading="lazy"
                                        src={logo}
                                        alt="Nasmat Al Zohour Logo"
                                        style={{
                                            ...styles.footerLogoImage,
                                            width: isMobile ? '60px' : '70px',
                                            height: isMobile ? '60px' : '70px',
                                        }}
                                        onError={(e) => {
                                            e.currentTarget.style.display = 'none';
                                            const parent = e.currentTarget.parentElement;
                                            if (parent) {
                                                const fallback = parent.querySelector('.logo-fallback-icon');
                                                if (fallback) fallback.style.display = 'flex';
                                            }
                                        }}
                                    />
                                    <div className="logo-fallback-icon" style={{
                                        ...styles.footerLogoFallback,
                                        width: isMobile ? '60px' : '70px',
                                        height: isMobile ? '60px' : '70px',
                                    }}>
                                        <FaTree />
                                    </div>
                                    <div style={{
                                        ...styles.footerLogoBadge,
                                        width: isMobile ? '18px' : '22px',
                                        height: isMobile ? '18px' : '22px',
                                        fontSize: isMobile ? '10px' : '12px',
                                    }}>✨</div>
                                </div>
                                <div style={styles.footerLogoTextWrapper}>
                                    <div style={styles.footerLogoTopLine}></div>
                                    <h2 style={{
                                        ...styles.footerLogoMainText,
                                        fontSize: isMobile ? '14px' : '16px',
                                    }}>
                                        <span style={{
                                            ...styles.footerLogoFirstWord,
                                            fontSize: isMobile ? '16px' : '18px',
                                        }}>NASMAT</span>
                                        <span style={{
                                            ...styles.footerLogoSecondWord,
                                            fontSize: isMobile ? '13px' : '15px',
                                        }}>AL ZOHOUR</span>
                                    </h2>
                                   
                                    <p style={{
                                        ...styles.footerLogoSubText,
                                        fontSize: isMobile ? '8px' : '9px',
                                    }}>PREMIUM NURSERY & GARDEN CENTER</p>
                                    <div style={styles.footerLogoBottomLine}></div>
                                </div>
                            </div>
                        </div>
                        <p style={{ 
                            ...styles.footerAbout, 
                            fontSize: isMobile ? '12px' : '13px', 
                            marginTop: '15px',
                            maxWidth: isMobile ? '100%' : '320px',
                            marginLeft: isMobile ? 'auto' : '0',
                            marginRight: isMobile ? 'auto' : '0',
                            textAlign: isMobile ? 'center' : 'center',
                        }}>
                            Premium plants and complete landscaping solutions across the UAE — locally
                            grown, expertly cared for, and delivered with a passion for green living.
                        </p>
                    </Col>

                    <Col lg={3} md={6} xs={12} style={{ 
                        marginLeft: isMobile ? '0' : '10px',
                        textAlign: isMobile ? 'center' : 'left',
                    }}>
                        <h6 style={{
                            ...styles.footerColTitle,
                            textAlign: isMobile ? 'center' : 'left',
                        }}>Explore</h6>
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '12px', 
                            marginBottom: '25px',
                            alignItems: isMobile ? 'center' : 'flex-start',
                        }}>
                            {navLinks.map((item, idx) => (
                                <a 
                                    key={idx} href={item.url || "#"} 
                                    style={{ 
                                        color: 'rgba(255,255,255,0.65)',
                                        textDecoration: 'none',
                                        fontSize: isMobile ? '13px' : '14px',
                                        transition: 'all 0.25s ease',
                                        cursor: 'pointer',
                                        display: 'block',
                                        textAlign: isMobile ? 'center' : 'left',
                                    }} 
                                    onClick={(e) => { if(item.action) { e.preventDefault(); item.action(); } }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.color = '#4ADE80';
                                        e.currentTarget.style.transform = 'translateX(6px)';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.color = 'rgba(255,255,255,0.65)';
                                        e.currentTarget.style.transform = 'translateX(0)';
                                    }}
                                >
                                    {item.text}
                                </a>
                            ))}
                        </div>

                        <div>
                            <h6 style={{ 
                                ...styles.footerColTitle, 
                                fontSize: isMobile ? '12px' : '13px',
                                marginBottom: '10px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: isMobile ? 'center' : 'flex-start',
                                gap: '8px',
                            }}>
                                <FaMapMarkerAlt style={{ color: '#c9a03d', fontSize: '14px' }} />
                                Find Us
                            </h6>
                            <div style={{ 
                                borderRadius: '12px',
                                overflow: 'hidden',
                                border: '1px solid rgba(255,255,255,0.1)',
                                boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                                width: isMobile ? '100%' : '70%',
                                maxWidth: isMobile ? '320px' : '100%',
                                margin: isMobile ? '0 auto' : '0',
                            }}>
                                <iframe 
                                    src="https://www.google.com/maps/embed?pb=!1m14!1m8!1m3!1d57795.52417032471!2d55.398517!3d25.1282432!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f623ade982a6b%3A0x965d82424083d91!2sNASMAT%20AL%20ZOHOUR%20NURSERY%20AND%20LANDSCAPING!5e0!3m2!1sen!2sin!4v1781758234184!5m2!1sen!2sin" 
                                    width="100%" 
                                    height={isMobile ? "80" : "100"} 
                                    style={{ border: 0, display: 'block' }} 
                                    allowFullScreen="" 
                                    loading="lazy" 
                                    referrerPolicy="no-referrer-when-downgrade" 
                                    title="Nursery Location Map"
                                ></iframe>
                                <div style={{
                                    padding: '8px 12px',
                                    background: 'rgba(255,255,255,0.05)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    fontSize: isMobile ? '10px' : '11px',
                                    flexWrap: isMobile ? 'wrap' : 'nowrap',
                                    gap: isMobile ? '4px' : '0',
                                }}>
                                    <span style={{ color: 'rgba(255,255,255,0.5)' }}>
                                        <FaMapMarkerAlt style={{ marginRight: '6px', color: '#c9a03d' }} />
                                        Dubai UAE
                                    </span>
                                    <a 
                                        href="https://maps.app.goo.gl/vipwYarn1dukRYHZ8" 
                                        target="_blank" 
                                        rel="noreferrer"
                                        style={{ 
                                            color: '#4ADE80', 
                                            textDecoration: 'none',
                                            fontWeight: 600,
                                            fontSize: isMobile ? '10px' : '11px',
                                        }}
                                    >
                                        Directions →
                                    </a>
                                </div>
                            </div>
                        </div>
                    </Col>

                    <Col lg={3} md={6} xs={12} style={{ 
                        marginLeft: isMobile ? '0' : '-50px',
                        textAlign: isMobile ? 'center' : 'left',
                    }}>
                        <h6 style={{
                            ...styles.footerColTitle,
                            textAlign: isMobile ? 'center' : 'left',
                        }}>Contact</h6>
                        <div style={{ 
                            display: 'flex', 
                            flexDirection: 'column', 
                            gap: '16px',
                            alignItems: isMobile ? 'center' : 'flex-start',
                        }}>
                            <div style={{ 
                                display: 'flex',
                                gap: '12px',
                                fontSize: isMobile ? '13px' : '14px',
                                color: 'rgba(255,255,255,0.7)',
                                lineHeight: '1.5',
                                alignItems: 'center',
                                justifyContent: isMobile ? 'center' : 'flex-start',
                                flexWrap: isMobile ? 'wrap' : 'nowrap',
                                textAlign: isMobile ? 'center' : 'left',
                            }}>
                                <FaMapMarkerAlt style={{ color: '#c9a03d', fontSize: '15px', minWidth: '16px' }} />
                                <span>Warsan Third - Warsan 3 - Dubai - UAE</span>
                            </div>

                            <div style={{ 
                                display: 'flex',
                                gap: '12px',
                                fontSize: isMobile ? '13px' : '14px',
                                color: 'rgba(255,255,255,0.7)',
                                lineHeight: '1.5',
                                alignItems: 'center',
                                justifyContent: isMobile ? 'center' : 'flex-start',
                            }}>
                                <FaWhatsapp style={{ color: '#25D366', fontSize: '16px', minWidth: '16px' }} />
                                <div>
                                    <a href="tel:+971552698951" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}>+971 55 269 8951</a>
                                    <span style={{ margin: '0 6px', color: 'rgba(255,255,255,0.2)' }}>|</span>
                                    <a href="tel:+971526629183" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}>+971 52 662 9183</a>
                                </div>
                            </div>

                            <div style={{ 
                                display: 'flex',
                                gap: '12px',
                                fontSize: isMobile ? '13px' : '14px',
                                color: 'rgba(255,255,255,0.7)',
                                alignItems: 'center',
                                justifyContent: isMobile ? 'center' : 'flex-start',
                            }}>
                                <FaTiktok style={{ color: '#fff', fontSize: '15px', minWidth: '16px' }} />
                                <a href="https://www.tiktok.com/@nasmatnursery" target="_blank" rel="noreferrer" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}>
                                    @nasmatnursery
                                </a>
                            </div>

                            <div style={{ 
                                display: 'flex',
                                gap: '12px',
                                fontSize: isMobile ? '13px' : '14px',
                                color: 'rgba(255,255,255,0.7)',
                                alignItems: 'center',
                                justifyContent: isMobile ? 'center' : 'flex-start',
                            }}>
                                <FaInstagram style={{ color: '#E1306C', fontSize: '15px', minWidth: '16px' }} />
                                <a href="https://instagram.com/nasmatnursery" target="_blank" rel="noreferrer" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}>
                                    @nasmatnursery
                                </a>
                            </div>

                            <div style={{ 
                                display: 'flex',
                                gap: '12px',
                                fontSize: isMobile ? '13px' : '14px',
                                color: 'rgba(255,255,255,0.7)',
                                alignItems: 'center',
                                justifyContent: isMobile ? 'center' : 'flex-start',
                            }}>
                                <FaEnvelope style={{ color: '#38BDF8', fontSize: '15px', minWidth: '16px' }} />
                                <a href="mailto:nasmatalzohour@gmail.com" style={{ color: 'rgba(255,255,255,0.7)', textDecoration: 'none' }}>
                                    nasmatalzohour@gmail.com
                                </a>
                            </div>
                        </div>

                        <div style={{
                            marginTop: '20px',
                            padding: '12px 15px',
                            width: isMobile ? '100%' : '85%',
                            maxWidth: isMobile ? '320px' : '100%',
                            marginLeft: isMobile ? 'auto' : '0',
                            marginRight: isMobile ? 'auto' : '0',
                            background: 'rgba(201,160,61,0.12)',
                            borderRadius: '10px',
                            border: '1px solid rgba(201,160,61,0.2)',
                            backdropFilter: 'blur(4px)',
                            textAlign: 'center',
                        }}>
                            <div style={{
                                fontSize: isMobile ? '10px' : '11px',
                                color: '#e2ba5e',
                                fontWeight: 700,
                                letterSpacing: '0.5px',
                                textTransform: 'uppercase',
                                fontStyle: 'italic',
                            }}>
                                "Quality is what we believe in"
                            </div>
                        </div>
                    </Col>
                </Row>
            </Container>
        </footer>
    );
}

// 19. WHATSAPP FAB
function WhatsAppFab() {
    return (
        <a href="https://wa.me/971552698951" target="_blank" rel="noreferrer" style={styles.whatsappFab}>
            <FaWhatsapp size={32} color="#fff" />
            <span style={styles.whatsappTooltip}>Questions? Chat with us!</span>
        </a>
    );
}

// 20. FREE DELIVERY BANNER
function FreeDeliveryBanner() {
    const [showPopup, setShowPopup] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [showTooltip, setShowTooltip] = useState(false);

    const handleClosePopup = () => {
        setShowPopup(false);
    };

    return (
        <>
            <div
                style={{
                    position: 'fixed',
                    bottom: '110px',
                    right: '30px',
                    zIndex: 999,
                    cursor: 'pointer',
                    animation: 'danceMove 2s ease-in-out infinite',
                    transition: 'transform 0.3s ease',
                }}
                onMouseEnter={() => {
                    setIsHovered(true);
                    setShowTooltip(true);
                }}
                onMouseLeave={() => {
                    setIsHovered(false);
                    setShowTooltip(false);
                }}
                onClick={() => {
                    setShowPopup(true);
                }}
            >
                <div style={{
                    position: 'relative',
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 8px 30px rgba(255, 215, 0, 0.4)',
                    animation: isHovered ? 'pulseGlow 1s ease-in-out infinite' : 'none',
                    transform: isHovered ? 'scale(1.15)' : 'scale(1)',
                    transition: 'transform 0.3s ease',
                }}>
                    <FaTruck size={32} color="#fff" style={{ 
                        textShadow: '0 2px 10px rgba(0,0,0,0.2)',
                    }} />
                    
                    <div style={{
                        position: 'absolute',
                        top: '-5px',
                        right: '-5px',
                        background: '#FF6B6B',
                        color: '#fff',
                        borderRadius: '50%',
                        width: '24px',
                        height: '24px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px',
                        fontWeight: 800,
                        boxShadow: '0 2px 10px rgba(255,107,107,0.4)',
                        border: '2px solid #fff',
                    }}>
                        ✓
                    </div>

                    <div style={{
                        position: 'absolute',
                        inset: '-8px',
                        borderRadius: '50%',
                        border: '2px solid rgba(255, 215, 0, 0.3)',
                        animation: 'pulseGlow 2s ease-in-out infinite',
                    }} />
                    <div style={{
                        position: 'absolute',
                        inset: '-16px',
                        borderRadius: '50%',
                        border: '2px solid rgba(255, 215, 0, 0.15)',
                        animation: 'pulseGlow 2.5s ease-in-out infinite 0.5s',
                    }} />
                </div>

                {showTooltip && (
                    <div style={{
                        position: 'absolute',
                        right: '85px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'rgba(0,0,0,0.85)',
                        backdropFilter: 'blur(10px)',
                        color: '#fff',
                        padding: '10px 16px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        boxShadow: '0 8px 30px rgba(0,0,0,0.3)',
                        border: '1px solid rgba(255,215,0,0.2)',
                        animation: 'floatUp 0.3s ease',
                    }}>
                        🚚 Free Delivery Above AED 2,000
                        <div style={{
                            position: 'absolute',
                            right: '-8px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            borderLeft: '8px solid rgba(0,0,0,0.85)',
                            borderTop: '8px solid transparent',
                            borderBottom: '8px solid transparent',
                        }} />
                    </div>
                )}
            </div>

            {showPopup && (
                <div
                    style={{
                        position: 'fixed',
                        inset: 0,
                        background: 'rgba(0,0,0,0.7)',
                        backdropFilter: 'blur(8px)',
                        zIndex: 9999,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px',
                        animation: 'fadeIn 0.3s ease',
                    }}
                    onClick={(e) => {
                        if (e.target === e.currentTarget) handleClosePopup();
                    }}
                >
                    <div
                        style={{
                            background: 'linear-gradient(145deg, #ffffff, #f8fdf6)',
                            borderRadius: '28px',
                            maxWidth: '440px',
                            width: '100%',
                            padding: '40px 36px 32px',
                            position: 'relative',
                            animation: 'bounceIn 0.5s cubic-bezier(0.34, 1.56, 0.64, 1)',
                            boxShadow: '0 40px 80px rgba(0,0,0,0.3)',
                            border: '1px solid rgba(255,215,0,0.2)',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={handleClosePopup}
                            style={{
                                position: 'absolute',
                                top: '14px',
                                right: '14px',
                                background: 'rgba(0,0,0,0.05)',
                                border: 'none',
                                borderRadius: '50%',
                                width: '36px',
                                height: '36px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '16px',
                                transition: 'all 0.3s ease',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.background = 'rgba(0,0,0,0.1)';
                                e.currentTarget.style.transform = 'scale(1.1)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.background = 'rgba(0,0,0,0.05)';
                                e.currentTarget.style.transform = 'scale(1)';
                            }}
                        >
                            ✕
                        </button>

                        <div style={{
                            width: '80px',
                            height: '80px',
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #FFD700, #FFA500)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            margin: '0 auto 20px',
                            boxShadow: '0 12px 40px rgba(255, 215, 0, 0.3)',
                        }}>
                            <FaTruck size={40} color="#fff" />
                        </div>

                        <h3 style={{
                            fontFamily: "'Playfair Display', Georgia, serif",
                            fontSize: '24px',
                            fontWeight: 700,
                            color: '#1B5E20',
                            textAlign: 'center',
                            marginBottom: '8px',
                        }}>
                            We Deliver Green
                        </h3>

                        <p style={{
                            fontFamily: "'Inter', sans-serif",
                            fontSize: '14px',
                            color: '#555',
                            textAlign: 'center',
                            marginBottom: '20px',
                            lineHeight: '1.6',
                        }}>
                            From our nursery to your doorstep — we bring the freshest 
                            plants and landscaping materials right to you.
                            <br/><br/>
                            Enjoy <strong style={{ color: '#FF6B00' }}>FREE delivery</strong> on orders above{' '}
                            <strong style={{ color: '#2D7D46' }}>AED 2,000</strong>
                            <br/>
                            <span style={{ fontSize: '13px', color: '#999' }}>
                                Visit our nursery to select and purchase plants in person!
                            </span>
                        </p>

                        <div style={{
                            background: '#E8F5E9',
                            borderRadius: '10px',
                            height: '8px',
                            marginBottom: '20px',
                            overflow: 'hidden',
                        }}>
                            <div style={{
                                width: '70%',
                                height: '100%',
                                background: 'linear-gradient(90deg, #4CAF50, #FFD700)',
                                borderRadius: '10px',
                                transition: 'width 0.8s ease',
                            }} />
                        </div>

                        <div style={{
                            background: '#f0faf3',
                            borderRadius: '16px',
                            padding: '16px 20px',
                            marginBottom: '24px',
                            border: '1px solid rgba(45,125,70,0.1)',
                        }}>
                            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                <div style={{
                                    width: '36px',
                                    height: '36px',
                                    borderRadius: '50%',
                                    background: '#2D7D46',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: '#fff',
                                    fontSize: '16px',
                                    flexShrink: 0,
                                }}>
                                    ✓
                                </div>
                                <div>
                                    <div style={{
                                        fontWeight: 600,
                                        color: '#1B5E20',
                                        fontSize: '13px',
                                    }}>
                                        Free delivery on orders above AED 2,000
                                    </div>
                                    <div style={{
                                        fontSize: '12px',
                                        color: '#666',
                                        lineHeight: '1.4',
                                    }}>
                                        Available across UAE for landscaping projects & bulk purchases.
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div style={{
                            display: 'flex',
                            gap: '12px',
                            flexDirection: 'column',
                        }}>
                            <a
                                href="https://wa.me/971552698951?text=Hi!%20I%20need%20plant%20delivery%20for%20my%20project"
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                    width: '100%',
                                    padding: '14px',
                                    background: 'linear-gradient(135deg, #25D366, #128C7E)',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '50px',
                                    fontWeight: 700,
                                    fontSize: '15px',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 8px 25px rgba(37,211,102,0.3)',
                                    textDecoration: 'none',
                                    textAlign: 'center',
                                    display: 'inline-block',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.02)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'scale(1)';
                                }}
                            >
                                <FaWhatsapp style={{ marginRight: '10px' }} /> Enquire on WhatsApp
                            </a>

                            <a
                                href="https://maps.app.goo.gl/vipwYarn1dukRYHZ8"
                                target="_blank"
                                rel="noreferrer"
                                style={{
                                    width: '100%',
                                    padding: '12px',
                                    background: 'transparent',
                                    color: '#2D7D46',
                                    border: '2px solid #2D7D46',
                                    borderRadius: '50px',
                                    fontWeight: 600,
                                    fontSize: '14px',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    textDecoration: 'none',
                                    textAlign: 'center',
                                    display: 'inline-block',
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.background = '#2D7D46';
                                    e.currentTarget.style.color = '#fff';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.background = 'transparent';
                                    e.currentTarget.style.color = '#2D7D46';
                                }}
                            >
                                <FaMapMarkerAlt style={{ marginRight: '8px' }} /> Visit Our Nursery
                            </a>
                        </div>

                        <p style={{
                            fontSize: '11px',
                            color: '#999',
                            textAlign: 'center',
                            marginTop: '16px',
                            marginBottom: 0,
                        }}>
                            * Free delivery on orders above AED 2,000. Terms and conditions apply.
                        </p>
                    </div>
                </div>
            )}
        </>
    );
}

// ============================================================
// MAIN COMPONENT: AdvertisementPage
// ============================================================

export default function AdvertisementPage() {
    const [data, setData] = useState(null);
    const [plantOffers, setPlantOffers] = useState([]);
    const [landscapingOffers, setLandscapingOffers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statsVisible, setStatsVisible] = useState(false);
    const [scrollY, setScrollY] = useState(0);
    const [refreshKey, setRefreshKey] = useState(0);
    const statsRef = useRef(null);
    const [quizResult, setQuizResult] = useState(null);
    const [quizStep, setQuizStep] = useState(0);
    const [quizAnswers, setQuizAnswers] = useState({});
    const [slideIdx, setSlideIdx] = useState(0);
    const [selectedCategory, setSelectedCategory] = useState('');
    const [activeTab, setActiveTab] = useState('plants');
    const [hoveredCard, setHoveredCard] = useState(null);
    const [hoveredPill, setHoveredPill] = useState(null);
    const [baAfter, setBaAfter] = useState(false);
    const [activePackage, setActivePackage] = useState(1);
    const [openFaq, setOpenFaq] = useState(null);
    const [lightboxImage, setLightboxImage] = useState(null);
    
    const [showLandscape, setShowLandscape] = useState(false);
    const [quoteModal, setQuoteModal] = useState({ show: false, type: 'landscaping', preset: '' });
    const [savedScrollPosition, setSavedScrollPosition] = useState(0);

    // SECTION VISIBILITY STATE - Hero visible immediately, others hidden
    const [sectionVisibility, setSectionVisibility] = useState({
        hero: true,
        about: true,
        featurePills: false,
        featuredPlants: false,
        landscaping: false,
        portfolio: false,
        beforeAfter: false,
        quiz: false,
        packages: false,
        testimonials: false,
        sizeGuide: false,
        faq: false,
        careGuide: false,
    });

    // Show all sections after 5 seconds with stagger effect
    useEffect(() => {
        const sectionKeys = [
           
            'featurePills',
            'featuredPlants',
            'landscaping',
            'portfolio',
            'beforeAfter',
            'quiz',
            'packages',
            'testimonials',
            'sizeGuide',
            'faq',
            'careGuide'
        ];
        
        sectionKeys.forEach((key, index) => {
            setTimeout(() => {
                setSectionVisibility(prev => ({
                    ...prev,
                    [key]: true
                }));
            }, 50 + (index * 50));
        });
    }, []);

    const saveScrollPosition = () => {
        const scrollY = window.scrollY;
        sessionStorage.setItem('landscapeScrollPosition', scrollY.toString());
        setSavedScrollPosition(scrollY);
    };

    const openLandscapeQuote = (type = 'landscaping', preset = '') => {
        saveScrollPosition();
        setQuoteModal({ show: false, type, preset });
        setShowLandscape(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };
    
    const openQuoteModal = (type = 'landscaping', preset = '') => {
        if (type === 'landscaping' || type === 'both') {
            openLandscapeQuote(type, preset);
        } else {
            setQuoteModal({ show: true, type, preset });
        }
    };
    
    const closeQuoteModal = () => setQuoteModal(q => ({ ...q, show: false }));
    
    const closeLandscape = () => {
        setShowLandscape(false);
        setTimeout(() => {
            const saved = sessionStorage.getItem('landscapeScrollPosition');
            if (saved) {
                const position = parseInt(saved, 10);
                window.scrollTo({ top: position, behavior: 'smooth' });
                sessionStorage.removeItem('landscapeScrollPosition');
            }
        }, 300);
    };

    const categorySectionRef = useRef(null);
    const [featuresRef, featuresVis] = useScrollReveal();
    const [productsRef, productsVis] = useScrollReveal();
    const [whyRef, whyVis] = useScrollReveal();
    const [testimonialRef, testimonialVis] = useScrollReveal();
    const [portfolioRef, portfolioVis] = useScrollReveal();
    const [baRef, baVis] = useScrollReveal();
    const [processRef, processVis] = useScrollReveal();
    const [packagesRef, packagesVis] = useScrollReveal();

    const closeLightbox = () => {
        setLightboxImage(null);
    };

    useEffect(() => {
        const timer = setInterval(() => setSlideIdx(i => (i + 1) % SLIDES.length), 7000);
        return () => clearInterval(timer);
    }, []);

    useEffect(() => {
        const handleScroll = () => setScrollY(window.scrollY);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const forceResetData = () => {
        localStorage.removeItem('nursery_landscaping_offers');
        localStorage.removeItem('nursery_exclusive_offers');
        setLandscapingOffers(DEFAULT_LANDSCAPING_PORTFOLIO.filter(o => o.active !== false));
        setPlantOffers(DEFAULT_PLANT_OFFERS.filter(o => o.active !== false));
        localStorage.setItem('nursery_landscaping_offers', JSON.stringify(DEFAULT_LANDSCAPING_PORTFOLIO));
        localStorage.setItem('nursery_exclusive_offers', JSON.stringify(DEFAULT_PLANT_OFFERS));
        setRefreshKey(prev => prev + 1);
    };

    useEffect(() => { window.forceResetNurseryData = forceResetData; }, []);

    const loadData = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_BASE}/api/advertisement`);
            const apiData = await response.json();
            setData(apiData);
            
            if (apiData?.landscaping_portfolio && Array.isArray(apiData.landscaping_portfolio)) {
                const processedPortfolio = apiData.landscaping_portfolio.map(item => ({
                    ...item,
                    image: item.image || item.image_url,
                    image_url: item.image || item.image_url,
                    beforeImage: item.beforeImage || item.before_image_url,
                    before_image_url: item.beforeImage || item.before_image_url,
                }));
                setLandscapingOffers(processedPortfolio.filter(o => o.active !== false));
                localStorage.setItem('nursery_landscaping_offers', JSON.stringify(processedPortfolio));
            } else {
                loadLandscapingFromLocalStorage();
            }
            
            if (apiData?.exclusive_offers && Array.isArray(apiData.exclusive_offers)) {
                setPlantOffers(apiData.exclusive_offers.filter(o => o.active !== false));
                localStorage.setItem('nursery_exclusive_offers', JSON.stringify(apiData.exclusive_offers));
            } else {
                loadPlantOffersFromLocalStorage();
            }
        } catch (error) {
            console.error('Error loading data:', error);
            loadPlantOffersFromLocalStorage();
            loadLandscapingFromLocalStorage();
        } finally { 
            setLoading(false); 
        }
    };

    const loadPlantOffersFromLocalStorage = () => {
        const saved = localStorage.getItem('nursery_exclusive_offers');
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                if (parsed && Array.isArray(parsed) && parsed.length > 0) { setPlantOffers(parsed.filter(o => o.active !== false)); return; }
            } catch (e) {}
        }
        setPlantOffers(DEFAULT_PLANT_OFFERS.filter(o => o.active !== false));
    };

const loadLandscapingFromLocalStorage = () => {
    // Directly use static default data without checking localStorage
    console.log('Using static landscaping portfolio from frontend');
    setLandscapingOffers(DEFAULT_LANDSCAPING_PORTFOLIO.filter(o => o.active !== false));
};
    useEffect(() => { loadData(); }, [refreshKey]);

    useEffect(() => {
        const handleStorageChange = (e) => {
            if (e.key === 'nursery_landscaping_offers') {
                try { const parsed = JSON.parse(e.newValue); if (parsed && Array.isArray(parsed) && parsed.length > 0) { setLandscapingOffers(parsed.filter(o => o.active !== false)); setRefreshKey(prev => prev + 1); } } catch (err) {}
            }
            if (e.key === 'nursery_exclusive_offers') {
                try { const parsed = JSON.parse(e.newValue); if (parsed && Array.isArray(parsed) && parsed.length > 0) { setPlantOffers(parsed.filter(o => o.active !== false)); setRefreshKey(prev => prev + 1); } } catch (err) {}
            }
        };
        window.addEventListener('storage', handleStorageChange);
        return () => window.removeEventListener('storage', handleStorageChange);
    }, []);

    useEffect(() => {
        const handleMarketingUpdate = () => loadData();
        window.addEventListener('marketingDataUpdated', handleMarketingUpdate);
        window.addEventListener('nurseryDataUpdated', handleMarketingUpdate);
        return () => {
            window.removeEventListener('marketingDataUpdated', handleMarketingUpdate);
            window.removeEventListener('nurseryDataUpdated', handleMarketingUpdate);
        };
    }, []);




    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
            { threshold: 0.3 }
        );
        if (statsRef.current) observer.observe(statsRef.current);
        return () => observer.disconnect();
    }, [loading]);

    const handleQuizAnswer = (key, value) => {
        const newAnswers = { ...quizAnswers, [key]: value };
        setQuizAnswers(newAnswers);
        if (quizStep < 2) {
            setQuizStep(quizStep + 1);
        } else {
            let targetCategory = "Indoor";
            if (newAnswers.light === 'High' && newAnswers.effort === 'Low') targetCategory = "Desert/Succulent";
            else if (newAnswers.space === 'Large') targetCategory = "Palm Tree";
            else if (newAnswers.light === 'High') targetCategory = "Flowering";
            else if (newAnswers.light === 'Low' || (newAnswers.effort === 'Low' && newAnswers.space === 'Small')) targetCategory = "Indoor";
            
            const stock = data?.featured_plants || [];
            
            let matches = stock.filter(p => p.category === targetCategory);
            if (matches.length === 0) {
                matches = stock.slice(0, 2);
            }
            
            if (matches.length === 0) {
                setQuizResult({ category: targetCategory, plants: [], noMatches: true });
            } else {
                setQuizResult({ category: targetCategory, plants: matches.slice(0, 2) });
            }
        }
    };

    const resetQuiz = () => { setQuizStep(0); setQuizAnswers({}); setQuizResult(null); };

    const handleCategoryClick = (category) => {
        const cleanCategory = category.replace(/[🌿🌵🍋🌸🪴🌴🌻🌹]/g, '').trim();
        setSelectedCategory(cleanCategory);
        setTimeout(() => { if (categorySectionRef.current) categorySectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, 100);
    };

    const totalCustomers = parseInt(data?.stats?.total_customers || 0);
    const c1 = useCounter(100, 1800, statsVisible);
    const c3 = useCounter(6, 1500, statsVisible);
    const c4 = useCounter(totalCustomers || 500, 2000, statsVisible);

    const getActiveOffers = () => {
        if (activeTab === 'plants') return plantOffers.filter(o => o.active !== false);
        return landscapingOffers.filter(o => o.active !== false);
    };

    const renderLandscapingCard = (item, index) => {
        const getImageUrl = (url) => {
            if (!url) return 'https://images.pexels.com/photos/1595696/pexels-photo-1595696.jpeg?auto=compress&cs=tinysrgb&w=800';
            if (url.startsWith('http://') || url.startsWith('https://')) return url;
            if (url.startsWith('/uploads/')) return `${API_BASE}${url}`;
            if (url.startsWith('uploads/')) return `${API_BASE}/${url}`;
            if (!url.includes('/')) return `${API_BASE}/uploads/${url}`;
            return url;
        };
        let imageUrl = getImageUrl(item.image || item.image_url || item.imageUrl);
        const features = item.features || [];
        
        return (
            <div key={item.id || index} style={{ ...styles.offerCard, cursor: 'default' }}>
                <div style={{ position: 'absolute', top: -15, right: 30, padding: '6px 16px', borderRadius: 20, fontSize: 12, fontWeight: 700, background: '#2D7D46', color: '#fff', boxShadow: '0 4px 10px rgba(0,0,0,0.2)', zIndex: 2 }}>
                    <FaImage style={{ marginRight: 6 }} /> PORTFOLIO
                </div>
                <div style={{ height: 220, overflow: 'hidden', borderRadius: 16, marginBottom: 16, position: 'relative', background: '#1a1a1a' }}>
                    <LazyImage 
                        src={imageUrl} 
                        alt={item.title} 
                        sectionKey="portfolio"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }} 
                        onError={(e) => { 
                            e.target.src = 'https://images.pexels.com/photos/1595696/pexels-photo-1595696.jpeg?auto=compress&cs=tinysrgb&w=800'; 
                        }} 
                    />
                    <div style={{ position: 'absolute', top: 10, left: 10, background: 'rgba(0,0,0,0.7)', padding: '4px 14px', borderRadius: 20, fontSize: 12, color: '#A5D6A7' }}>
                        <FaMapMarkerAlt style={{ marginRight: 4 }} /> {item.location}
                    </div>
                </div>
                <h3 style={styles.offerTitle}>{item.title}</h3>
                <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 12 }}>{item.description}</p>
                <div style={{ display: 'flex', gap: 20, marginBottom: 16, flexWrap: 'wrap' }}>
                    <span style={{ color: '#A5D6A7', fontSize: 13 }}><FaBuilding style={{ marginRight: 6 }} />{item.category || 'Residential'}</span>
                    <span style={{ color: '#A5D6A7', fontSize: 13 }}><FaClock style={{ marginRight: 6 }} />Completed {item.completed || '2024'}</span>
                    <span style={{ color: '#FDD835', fontSize: 13, fontWeight: 700 }}>AED {item.price || '0'}</span>
                </div>
                <div style={styles.offerDivider} />
                {features.length > 0 && (
                    <ul style={styles.offerFeatures}>
                        {features.slice(0, 3).map((f, i) => (<li key={i}><FaCheckCircle size={14} style={{ marginRight: 8, color: '#A5D6A7' }} />{f}</li>))}
                        {features.length > 3 && <li style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>+{features.length - 3} more features</li>}
                    </ul>
                )}
                <button
                    onClick={() => openLandscapeQuote('landscaping', item.title)}
                    style={{ ...styles.offerBtn, background: '#FDD835', color: '#1B5E20', marginTop: 'auto' }}
                >
                    Get a Quote Like This
                </button>
            </div>
        );
    };

    const renderPlantCard = (item, index) => (
        <div key={item.id || index} style={{ ...styles.offerCard, animationDelay: `${index * 0.2}s` }}>
            <div style={{ ...styles.offerBadge, background: item.color || '#4CAF50' }}>SAVE 20%</div>
            <h3 style={styles.offerTitle}>{item.title}</h3>
            <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 14, marginBottom: 12 }}>{item.description}</p>
            <div style={styles.offerDivider} />
            <ul style={styles.offerFeatures}>
                {item.features?.map((f, i) => (<li key={i}><FaCheckCircle size={14} style={{ marginRight: 8, color: '#A5D6A7' }} />{f}</li>))}
            </ul>
            <Button style={{ ...styles.offerBtn, background: item.color || '#4CAF50' }} href={`https://wa.me/971552698951?text=I'm interested in the ${item.title} offer`} target="_blank">Claim Offer</Button>
        </div>
    );

    const activeOffers = getActiveOffers();
    const portfolioProjects = landscapingOffers.filter(p => p.active !== false);

    if (showLandscape) {
        return (
            <div style={{ position: 'relative' }}>
                <button
                    onClick={closeLandscape}
                    style={{
                        position: 'fixed',
                        top: 20,
                        right: 20,
                        zIndex: 9999,
                        background: 'rgba(0,0,0,0.7)',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '50%',
                        width: 50,
                        height: 50,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 20,
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        backdropFilter: 'blur(10px)',
                        boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.1)';
                        e.currentTarget.style.background = 'rgba(0,0,0,0.9)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.background = 'rgba(0,0,0,0.7)';
                    }}
                >
                    <FaTimes />
                </button>
                <Landscape />
            </div>
        );
    }

    return (
        <div className="adv-page" style={{ fontFamily: "'Segoe UI', sans-serif", background: '#f9fdf6', overflowX: 'hidden' }}>
            <QuoteModal
                show={quoteModal.show}
                onClose={closeQuoteModal}
                initialType={quoteModal.type}
                presetProject={quoteModal.preset}
            />

            {/* HERO */}
            <HeroSection slideIdx={slideIdx} setSlideIdx={setSlideIdx} />

            {/* WAVE DIVIDER */}
            <div style={{ marginTop: -4, lineHeight: 0, background: 'linear-gradient(135deg, #de9b16, #1a5c32)' }}>
                <svg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ width: '100%', height: 40, display: 'block' }}>
                    <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#f9fdf6" />
                </svg>
            </div>

            {/* ABOUT */}
            <AboutSection/>

            {/* MARQUEE */}
            <MarqueeBar onCategoryClick={handleCategoryClick} />

            {/* FILTERED COLLECTION */}
            <div ref={categorySectionRef}>
                {selectedCategory && (
                    <section style={{ ...styles.sectionLight, background: '#fff', borderBottom: '1px solid #f0f0f0' }}>
                        <Container>
                            <div style={{ ...styles.sectionHeader, textAlign: 'left', marginBottom: 30 }}>
                                <Badge style={{ ...styles.categoryBadge, marginBottom: 10 }}>Collection</Badge>
                                <h2 style={{ ...styles.sectionTitle, margin: 0 }}>{selectedCategory} Variety</h2>
                                <p className="text-muted mt-2">Explore our premium selection for your unique lifestyle.</p>
                            </div>
                            <Row className="g-4">
                                {data?.featured_plants && data.featured_plants.length > 0 ? (
                                    data.featured_plants
                                        .filter(p => {
                                            const categoryMatch = p.category?.toLowerCase().includes(selectedCategory.toLowerCase());
                                            const nameMatch = p.name?.toLowerCase().includes(selectedCategory.toLowerCase());
                                            return categoryMatch || nameMatch;
                                        })
                                        .map((plant, idx) => {
                                            let imageUrl = plant.image_url;
                                            if (imageUrl && imageUrl.startsWith('/uploads/')) {
                                                imageUrl = `${API_BASE}${imageUrl}`;
                                            }
                                            if (!imageUrl || imageUrl === 'null' || imageUrl === 'undefined') {
                                                imageUrl = null;
                                            }
                                            return (
                                                <Col key={plant.id || idx} xs={12} sm={6} md={3}>
                                                    <div style={styles.modalPlantCard} className="modal-plant-hover">
                                                        <div style={styles.modalPlantImgBox}>
                                                            {imageUrl ? 
                                                                <LazyImage src={imageUrl} alt={plant.name} sectionKey="featuredPlants" style={styles.modalPlantImg} onError={(e) => { e.target.src = 'https://via.placeholder.com/400x300/2D7D46/fff?text=Plant'; }} /> : 
                                                                <PlantSVG color={PlantPlaceholderColors[idx % PlantPlaceholderColors.length]} index={idx} />
                                                            }
                                                        </div>
                                                        <div style={styles.modalPlantContent}>
                                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                                                                <Badge style={styles.categoryBadge}>{plant.category || 'Plant'}</Badge>
                                                            </div>
                                                            <h5 style={{ fontWeight: 700, color: '#1B5E20', marginBottom: 4 }}>{plant.name}</h5>
                                                            <p style={{ fontSize: 12, color: '#666', margin: 0 }}>
                                                                <FaSun size={10} /> {plant.sunlight || 'Various'}
                                                            </p>
                                                            <div style={{ marginTop: '12px' }}>
                                                                <PlantCareScheduler plantName={plant.name} plantId={plant.id} />
                                                            </div>
                                                            <Button variant="success" size="sm" className="mt-3 w-100" style={{ borderRadius: 20, fontWeight: 600, background: 'linear-gradient(135deg, #2D7D46, #388E3C)', border: 'none' }} href={`https://wa.me/971552698951?text=I'm interested in the ${plant.name} from the ${selectedCategory} collection.`} target="_blank">
                                                                Enquire <FaWhatsapp style={{ marginLeft: 6 }} />
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </Col>
                                            );
                                        })
                                ) : (
                                    <div className="text-center py-5">
                                        <p className="text-muted">No plants available in this category.</p>
                                    </div>
                                )}
                            </Row>
                            <div className="text-center mt-5">
                                <Button variant="outline-success" style={{ borderRadius: 50, padding: '8px 24px', fontSize: 13 }} onClick={() => setSelectedCategory('')}>Clear Selection</Button>
                            </div>
                        </Container>
                    </section>
                )}
            </div>

            {/* FEATURE PILLS */}
            <FeaturePills 
                featuresRef={featuresRef} 
                featuresVis={featuresVis} 
                hoveredPill={hoveredPill} 
                setHoveredPill={setHoveredPill}
                visible={sectionVisibility.featurePills}
            />

            {/* WAVE DIVIDER */}
            <div style={{ marginTop: -4, lineHeight: 0, background: 'linear-gradient(135deg, #2E7D32, #43A047)' }}>
                <svg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ width: '100%', height: 40, display: 'block' }}>
                    <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#f9fdf6" />
                </svg>
            </div>

            {/* FEATURED PLANTS */}
            <FeaturedPlants 
                loading={loading} 
                data={data} 
                lightboxImage={lightboxImage} 
                setLightboxImage={setLightboxImage} 
                closeLightbox={closeLightbox}
                visible={sectionVisibility.featuredPlants} 
            />

            {/* LANDSCAPING EXCELLENCE */}
            <LandscapingExcellence openLandscapeQuote={openLandscapeQuote} />

            {/* WAVE DIVIDER */}
            <div style={{ marginTop: -4, lineHeight: 0, background: 'linear-gradient(135deg, #2E7D32, #43A047)' }}>
                <svg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ width: '100%', height: 40, display: 'block' }}>
                    <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#f9fdf6" />
                </svg>
            </div>

           {/* PORTFOLIO */}
<PortfolioSection 
    portfolioProjects={portfolioProjects} 
    portfolioRef={portfolioRef} 
    portfolioVis={portfolioVis} 
    openLandscapeQuote={openLandscapeQuote}
    visible={sectionVisibility.portfolio} 
/>

            <WhyChooseUs whyRef={whyRef} whyVis={whyVis} data={data} />
            <div style={{ marginTop: -4, lineHeight: 0, background: 'linear-gradient(135deg, #2E7D32, #43A047)' }}>
                <svg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ width: '100%', height: 40, display: 'block' }}>
                    <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#f9fdf6" />
                </svg>
            </div>

            {/* BEFORE & AFTER */}
            <BeforeAfter 
                baRef={baRef} 
                baVis={baVis} 
                baAfter={baAfter} 
                setBaAfter={setBaAfter} 
                openLandscapeQuote={openLandscapeQuote} 
            />

            <QuizSection 
                data={data} 
                quizStep={quizStep} 
                quizAnswers={quizAnswers} 
                quizResult={quizResult} 
                handleQuizAnswer={handleQuizAnswer} 
                resetQuiz={resetQuiz} 
            />
            <div style={{ marginTop: -4, lineHeight: 0, background: 'linear-gradient(135deg, #2E7D32, #43A047)' }}>
                <svg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ width: '100%', height: 40, display: 'block' }}>
                    <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#f9fdf6" />
                </svg>
            </div>

            {/* PROCESS */}
            <ProcessSection processRef={processRef} processVis={processVis} />

            {/* WAVE DIVIDER */}
            <div style={{ marginTop: -4, lineHeight: 0, background: 'linear-gradient(135deg, #2E7D32, #43A047)' }}>
                <svg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ width: '100%', height: 40, display: 'block' }}>
                    <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#f9fdf6" />
                </svg>
            </div>

            {/* PACKAGES */}
            <PackagesSection 
                packagesRef={packagesRef} 
                packagesVis={packagesVis} 
                activePackage={activePackage} 
                setActivePackage={setActivePackage} 
                openLandscapeQuote={openLandscapeQuote} 
            />

            {/* TESTIMONIALS */}
            <Testimonials testimonialRef={testimonialRef} testimonialVis={testimonialVis} />
            <div style={{ marginTop: -4, lineHeight: 0, background: 'linear-gradient(135deg, #2E7D32, #43A047)' }}>
                <svg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ width: '100%', height: 40, display: 'block' }}>
                    <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#f9fdf6" />
                </svg>
            </div>

            {/* SIZE GUIDE */}
            <SizeGuide />

            {/* FAQ */}
            <FaqSection openFaq={openFaq} setOpenFaq={setOpenFaq} />
            <div style={{ marginTop: -4, lineHeight: 0, background: 'linear-gradient(135deg, #2E7D32, #43A047)' }}>
                <svg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ width: '100%', height: 40, display: 'block' }}>
                    <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#f9fdf6" />
                </svg>
            </div>

            {/* PLANT CARE GUIDE */}
            <PlantCareGuide />

            {/* CTA BANNER */}
            <CtaBanner />

            {/* WAVE DIVIDER */}
            <div style={{ marginTop: -4, lineHeight: 0, background: 'linear-gradient(135deg, #2E7D32, #43A047)' }}>
                <svg viewBox="0 0 1440 80" preserveAspectRatio="none" style={{ width: '100%', height: 40, display: 'block' }}>
                    <path d="M0,40 C360,80 1080,0 1440,40 L1440,80 L0,80 Z" fill="#f9fdf6" />
                </svg>
            </div>

            {/* FOOTER */}
            <Footer openLandscapeQuote={openLandscapeQuote} setActiveTab={setActiveTab} />

            {/* WHATSAPP FAB */}
            <WhatsAppFab />
            <FreeDeliveryBanner />

            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:wght@600;700&family=Inter:wght@300;400;500;600&display=swap');
                
                @keyframes contentFadeIn {
                    0% { opacity: 0; transform: translateY(30px); }
                    100% { opacity: 1; transform: translateY(0); }
                }
                @keyframes wordPopIn {
                    0% { opacity: 0; transform: translateY(20px) scale(0.95); }
                    100% { opacity: 1; transform: translateY(0) scale(1); }
                }
                @keyframes floatLeaf { 
                    0%, 100% { transform: translateY(0px) rotate(0deg); } 
                    33% { transform: translateY(-18px) rotate(12deg); } 
                    66% { transform: translateY(-8px) rotate(-8deg); } 
                }
                @keyframes heroFadeIn { 
                    from { opacity: 0; transform: translateY(40px); } 
                    to { opacity: 1; transform: translateY(0); } 
                }
                @keyframes pulse { 
                    0%, 100% { transform: scale(1); box-shadow: 0 6px 25px rgba(0,0,0,0.25); } 
                    50% { transform: scale(1.04); box-shadow: 0 10px 40px rgba(255,255,255,0.4); } 
                }
                @keyframes shimmer { 
                    0% { background-position: -200% center; } 
                    100% { background-position: 200% center; } 
                }
                @keyframes bounceIn { 
                    0% { opacity: 0; transform: scale(0.6) translateY(30px); } 
                    60% { opacity: 1; transform: scale(1.08) translateY(-6px); } 
                    80% { transform: scale(0.97); } 
                    100% { transform: scale(1) translateY(0); } 
                }
                @keyframes spinOnce { 
                    from { transform: rotate(0deg); } 
                    to { transform: rotate(360deg); } 
                }
                @keyframes spin { 
                    from { transform: rotate(0deg); } 
                    to { transform: rotate(360deg); } 
                }
                @keyframes spinSlow { 
                    from { transform: rotate(0deg); } 
                    to { transform: rotate(360deg); } 
                }
                @keyframes bounce { 
                    0%, 100% { transform: translateY(0); } 
                    50% { transform: translateY(-12px); } 
                }
                @keyframes starPop { 
                    from { opacity: 0; transform: scale(0) rotate(-30deg); } 
                    to { opacity: 1; transform: scale(1) rotate(0deg); } 
                }
                @keyframes marqueeScroll { 
                    0% { transform: translateX(0); } 
                    100% { transform: translateX(-50%); } 
                }
                @keyframes leafFall { 
                    0% { opacity: 0; transform: translateY(-50px) rotate(0deg); } 
                    10% { opacity: 0.6; } 
                    90% { opacity: 0.6; } 
                    100% { opacity: 0; transform: translateY(90vh) rotate(720deg); } 
                }
                @keyframes fabPulse { 
                    0% { box-shadow: 0 0 0 0 rgba(37, 211, 102, 0.7); } 
                    70% { box-shadow: 0 0 0 15px rgba(37, 211, 102, 0); } 
                    100% { box-shadow: 0 0 0 0 rgba(37, 211, 102, 0); } 
                }
                @keyframes fadeIn { 
                    from { opacity: 0; } 
                    to { opacity: 1; } 
                }
                @keyframes heroContentFadeOut {
                    0% { opacity: 1; transform: scale(1); }
                    100% { opacity: 0; transform: scale(0.98); }
                }
                @keyframes heroContentFadeIn {
                    0% { opacity: 0; transform: scale(0.98); }
                    100% { opacity: 1; transform: scale(1); }
                }
                @keyframes slideFromLeft {
                    0% { opacity: 0; transform: translateX(-80px); }
                    100% { opacity: 1; transform: translateX(0); }
                }
                @keyframes slideFromBottom {
                    0% { opacity: 0; transform: translateY(50px); }
                    100% { opacity: 1; transform: translateY(0); }
                }
                @keyframes gradientShift {
                    0%, 100% { background-position: 0% 50%; }
                    50% { background-position: 100% 50%; }
                }
                @keyframes pulseDot {
                    0%, 100% { opacity: 1; transform: scale(1); }
                    50% { opacity: 0.5; transform: scale(0.8); }
                }
                @keyframes floatLeafPattern {
                    0%, 100% { transform: translateX(0px) translateY(0px) rotate(0deg); }
                    25% { transform: translateX(30px) translateY(-20px) rotate(90deg); }
                    50% { transform: translateX(20px) translateY(10px) rotate(180deg); }
                    75% { transform: translateX(-20px) translateY(-10px) rotate(270deg); }
                }
                
                .leaf-container { position: absolute; inset: 0; overflow: hidden; pointer-events: none; z-index: 1; }
                .leaf-particle { position: absolute; top: -30px; color: rgba(165,214,167,0.3); font-size: 24px; animation: leafFall linear infinite; }
                .adv-hero-badge { animation: shimmer 3s linear infinite; background: linear-gradient(90deg, rgba(255,255,255,0.1), rgba(255,255,255,0.3), rgba(255,255,255,0.1)); background-size: 200% auto; }
                .adv-cta-btn:hover { transform: translateY(-3px) scale(1.04) !important; }
                .animate__fadeIn { animation: fadeIn 0.8s ease; }
                .adv-page { overflow-x: hidden; }
                .quote-modal-content { border: none !important; border-radius: 24px !important; overflow: hidden; }
                .quote-modal-content .modal-content { border: none !important; }
                
                .hero-section {
                    position: relative;
                    min-height: 100vh;
                    width: 100%;
                    overflow: hidden;
                    background: #0d3320;
                }
                .hero-bg-container {
                    position: absolute;
                    inset: 0;
                    width: 100%;
                    height: 100%;
                    min-height: 100vh;
                    overflow: hidden;
                }
                .hero-slide-bg {
                    position: absolute;
                    inset: 0;
                    width: 100%;
                    height: 100%;
                    background-size: cover !important;
                    background-position: center center !important;
                    background-repeat: no-repeat !important;
                }
                .hero-content-wrapper {
                    min-height: 350px;
                    display: flex;
                    flex-direction: column;
                    justify-content: center;
                }

                @keyframes scrollPlants {
                    0%   { transform: translateX(0); }
                    100% { transform: translateX(-100%); }
                }

                @keyframes danceMove {
                    0%, 100% { transform: translateY(0) rotate(0deg); }
                    10% { transform: translateY(-10px) rotate(-5deg); }
                    20% { transform: translateY(-20px) rotate(8deg); }
                    30% { transform: translateY(-10px) rotate(-5deg); }
                    40% { transform: translateY(0) rotate(5deg); }
                    50% { transform: translateY(-15px) rotate(-3deg); }
                    60% { transform: translateY(-25px) rotate(8deg); }
                    70% { transform: translateY(-15px) rotate(-5deg); }
                    80% { transform: translateY(-5px) rotate(3deg); }
                    90% { transform: translateY(-10px) rotate(-2deg); }
                }

                @keyframes bounceIn {
                    0% { opacity: 0; transform: scale(0.3); }
                    50% { opacity: 1; transform: scale(1.05); }
                    70% { transform: scale(0.9); }
                    100% { opacity: 1; transform: scale(1); }
                }

                @keyframes floatUp {
                    0% { opacity: 0; transform: translateY(20px); }
                    100% { opacity: 1; transform: translateY(0); }
                }

                @keyframes pulseGlow {
                    0%, 100% { box-shadow: 0 0 20px rgba(255, 215, 0, 0.3); }
                    50% { box-shadow: 0 0 40px rgba(255, 215, 0, 0.6); }
                }

                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }

                /* Lazy loading fade-in */
                .lazy-image-loaded {
                    animation: fadeIn 0.5s ease;
                }
            `}</style>
        </div>
    );
}

// ============================================================
// STYLES
// ============================================================

const styles = {
    hero: {
        position: 'relative',
        padding: '100px 0',
        overflow: 'hidden',
        minHeight: '75vh',
        display: 'flex',
        alignItems: 'center',
    },
    heroOverlay: { position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 30% 50%, rgba(255,255,255,0.05) 0%, transparent 60%)', pointerEvents: 'none' },
    heroContentContainer: { width: '100%', maxWidth: '100%', padding: '0 5%', zIndex: 2, position: 'relative' },
    heroBadge: { display: 'inline-flex', alignItems: 'center', background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', color: '#A5D6A7', padding: '8px 18px', borderRadius: 30, fontSize: 13, fontWeight: 600, letterSpacing: 1, marginBottom: 24, border: '1px solid rgba(165,214,167,0.3)' },
    heroTitle: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(2.5rem, 6vw, 4.5rem)', fontWeight: 700, color: '#fff', lineHeight: 1.1, marginBottom: 8, textShadow: '0 2px 20px rgba(0,0,0,0.3)' },
    heroSubtitle: { fontFamily: "'Playfair Display', Georgia, serif", fontSize: 'clamp(1.4rem, 3vw, 2rem)', color: 'rgba(255,255,255,0.7)', marginBottom: 20, fontStyle: 'italic' },
    heroTagline: { fontSize: 'clamp(1rem, 2vw, 1.2rem)', color: 'rgba(255,255,255,0.85)', marginBottom: 36, fontWeight: 300, lineHeight: 1.6 },
    heroButtons: { display: 'flex', gap: 16, flexWrap: 'wrap' },
    btnPrimary: { display: 'inline-flex', alignItems: 'center', background: '#fff', color: '#1B5E20', padding: '14px 30px', borderRadius: 50, fontWeight: 700, fontSize: 15, textDecoration: 'none', transition: 'all 0.3s', boxShadow: '0 6px 25px rgba(0,0,0,0.25)', animation: 'pulse 3s infinite' },
    btnSecondary: { display: 'inline-flex', alignItems: 'center', background: 'transparent', color: '#fff', padding: '14px 30px', borderRadius: 50, fontWeight: 600, fontSize: 15, textDecoration: 'none', border: '2px solid rgba(255,255,255,0.5)', transition: 'all 0.3s', cursor: 'pointer' },
    slideDot: { width: 8, height: 8, borderRadius: '50%', border: 'none', cursor: 'pointer', padding: 0, transition: 'all 0.3s ease' },
    heroSlideDotsWrap: { display: 'flex', justifyContent: 'center', gap: 8, marginTop: 40 },
    featureBar: { background: '#fff', padding: '30px 0', boxShadow: '0 4px 20px rgba(0,0,0,0.06)' },
    featurePill: { textAlign: 'center', padding: '20px 16px', borderRadius: 16, border: '2px solid #E8F5E9', width: '100%', background: '#f9fdf6', transition: 'transform 0.3s, box-shadow 0.3s', cursor: 'default' },
    featureIcon: { color: '#2D7D46', marginBottom: 10, fontSize: 28 },
    featureLabel: { fontWeight: 700, color: '#1B5E20', fontSize: 14, marginBottom: 4 },
    featureSub: { color: '#666', fontSize: 11 },
    marqueeBar: { background: 'linear-gradient(135deg, #1B5E20, #2D7D46)', padding: '14px 0', overflow: 'hidden', position: 'relative' },
    marqueeTrack: { display: 'flex', width: 'max-content', animation: 'marqueeScroll 30s linear infinite' },
    marqueeItem: { color: 'rgba(255,255,255,0.9)', fontWeight: 600, fontSize: 14, padding: '0 32px', whiteSpace: 'nowrap', letterSpacing: 0.5 },
    statsSection: { background: 'linear-gradient(135deg, #1a5c32, #2D7D46)', padding: '50px 0' },
    statCard: { background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(10px)', borderRadius: 20, padding: '30px 20px', border: '1px solid rgba(255,255,255,0.2)', transition: 'transform 0.3s', color: '#fff' },
    statIcon: { color: '#A5D6A7', marginBottom: 16 },
    statValue: { fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2.5rem, 5vw, 3.5rem)', fontWeight: 700, color: '#fff', lineHeight: 1, marginBottom: 8 },
    statLabel: { color: 'rgba(255,255,255,0.75)', fontSize: 14, fontWeight: 500, textTransform: 'uppercase', letterSpacing: 1 },
    sectionLight: { padding: '60px 0', background: '#f9fdf6' },
    sectionHeader: { textAlign: 'center', marginBottom: 40 },
    sectionTitle: { fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.8rem, 4vw, 2.6rem)', fontWeight: 700, color: '#1B5E20', marginTop: 12, marginBottom: 12 },
    sectionSub: { color: '#555', fontSize: 16, maxWidth: 550, margin: '0 auto' },
    plantCard: { borderRadius: 24, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.4)', background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(12px)', transition: 'transform 0.3s, box-shadow 0.3s', boxShadow: '0 4px 15px rgba(0,0,0,0.05)' },
    categoryBadge: { background: 'linear-gradient(135deg, #2D7D46, #388E3C)', color: '#fff', fontSize: 11, padding: '5px 12px', borderRadius: 30, fontWeight: 600 },
    plantName: { fontSize: 17, fontWeight: 700, color: '#1B5E20', marginBottom: 4 },
    botanicalName: { color: '#777', fontSize: 13, marginBottom: 8 },
    plantTag: { display: 'inline-flex', alignItems: 'center', background: '#E8F5E9', color: '#2D7D46', borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 500 },
    whySection: { background: 'linear-gradient(135deg, #0d3320, #1a5c32 50%, #2D7D46)', padding: '60px 0' },
    checkRow: { display: 'flex', alignItems: 'flex-start', marginBottom: 16, padding: '10px 14px', background: 'rgba(255,255,255,0.06)', borderRadius: 10, backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.08)' },
    categoriesGrid: { background: 'rgba(255,255,255,0.08)', borderRadius: 20, padding: 32, backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.12)' },
    catChip: { display: 'flex', alignItems: 'center', background: 'rgba(255,255,255,0.1)', color: '#fff', padding: '10px 14px', borderRadius: 10, fontSize: 14, fontWeight: 500, border: '1px solid rgba(255,255,255,0.15)', transition: 'background 0.2s' },
    offersSection: { background: 'linear-gradient(135deg, #1B5E20, #2D7D46)', padding: '70px 0', position: 'relative', overflow: 'hidden' },
    offersQuoteCta: { marginTop: 20, background: '#FDD835', color: '#1B5E20', border: 'none', borderRadius: 50, padding: '13px 32px', fontWeight: 800, fontSize: 14, cursor: 'pointer', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', display: 'inline-flex', alignItems: 'center' },
    offerCard: { background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(15px)', borderRadius: 30, padding: 40, border: '1px solid rgba(255,255,255,0.2)', color: '#fff', height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' },
    offerBadge: { position: 'absolute', top: -15, right: 30, padding: '6px 16px', borderRadius: 20, fontSize: 12, fontWeight: 700, boxShadow: '0 4px 10px rgba(0,0,0,0.2)' },
    offerTitle: { fontSize: '1.5rem', fontWeight: 700, marginBottom: 15, color: '#E8F5E9' },
    offerDivider: { height: 1, background: 'rgba(255,255,255,0.1)', margin: '20px 0' },
    offerFeatures: { listStyle: 'none', padding: 0, margin: '0 0 30px 0', flex: 1 },
    offerBtn: { border: 'none', borderRadius: 50, padding: '12px 0', fontWeight: 700, fontSize: 14, boxShadow: '0 8px 20px rgba(0,0,0,0.2)', transition: 'all 0.3s', cursor: 'pointer', color: '#fff' },
    landscapeSection: { padding: '70px 0', background: 'linear-gradient(135deg, #0d3320 0%, #1a5c32 100%)', position: 'relative', overflow: 'hidden' },
    landscapeHeader: { textAlign: 'center', marginBottom: 60 },
    landscapeBadge: { display: 'inline-block', padding: '8px 20px', background: 'rgba(255,255,255,0.1)', borderRadius: 30, color: '#A5D6A7', fontSize: 12, fontWeight: 800, letterSpacing: 2, marginBottom: 15, border: '1px solid rgba(255,255,255,0.2)' },
    serviceCard: { background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(10px)', borderRadius: 24, padding: 40, border: '1px solid rgba(255,255,255,0.1)', height: '100%', transition: 'all 0.3s ease', cursor: 'default' },
    serviceIcon: { fontSize: '2.5rem', color: '#A5D6A7', marginBottom: 24 },
    serviceTitle: { color: '#fff', fontWeight: 700, fontSize: '1.25rem', marginBottom: 15 },
    serviceText: { color: 'rgba(255,255,255,0.7)', fontSize: 14, lineHeight: 1.6, margin: 0 },
    portfolioSection: { background: 'linear-gradient(135deg, #0d3320, #1a5c32)', padding: '70px 0' },
    baSection: { padding: '70px 0', background: '#fff' },
    baBtnGroup: { display: 'flex', justifyContent: 'center', gap: 20, marginTop: 24 },
    baToggleBtn: { padding: '12px 30px', borderRadius: 50, fontWeight: 700, fontSize: 14, border: 'none', transition: 'all 0.3s ease', cursor: 'pointer', letterSpacing: 1, textTransform: 'uppercase' },
    baList: { listStyle: 'none', padding: 0, margin: 0 },
    baListItem: { marginBottom: 16, fontSize: 15, color: '#444', lineHeight: 1.5, display: 'flex', alignItems: 'flex-start' },
    baFrame: { position: 'relative', borderRadius: 30, overflow: 'hidden', boxShadow: '0 25px 60px rgba(45,125,70,0.15)', background: '#eee' },
    baFullImg: { width: '100%', height: '100%', objectFit: 'cover' },
    baBadgeFloat: { position: 'absolute', top: 24, right: 24, padding: '8px 20px', borderRadius: 30, fontWeight: 800, fontSize: 12, boxShadow: '0 8px 20px rgba(0,0,0,0.1)', zIndex: 10 },
    baInfoPanel: { background: '#f9fdf6', padding: '40px', borderRadius: 30, height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', border: '2px solid #E8F5E9', boxShadow: '0 10px 30px rgba(45,125,70,0.05)' },
    baInfoBadge: { display: 'inline-block', padding: '8px 20px', borderRadius: 30, fontSize: 12, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 24 },
    baSeeResultBtn: { marginTop: 20, background: '#2D7D46', color: '#fff', border: 'none', padding: '14px 30px', borderRadius: 50, fontWeight: 700, cursor: 'pointer', transition: 'all 0.3s', boxShadow: '0 8px 20px rgba(45,125,70,0.3)', fontSize: 14 },
    baWhatsappBtn: { marginTop: 20, background: '#25D366', color: '#fff', padding: '14px 30px', borderRadius: 50, fontWeight: 700, textDecoration: 'none', display: 'inline-block', transition: 'all 0.3s', boxShadow: '0 8px 20px rgba(37, 211, 102, 0.3)', fontSize: 14, textAlign: 'center' },
    processSection: { padding: '70px 0', background: '#f9fdf6' },
    timelineRow: { display: 'flex', justifyContent: 'space-between', gap: 20, marginTop: 50, flexWrap: 'wrap' },
    processStep: { flex: 1, minWidth: 200, textAlign: 'center', position: 'relative' },
    processIconBox: { width: 80, height: 80, background: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px', fontSize: 32, color: '#2D7D46', boxShadow: '0 12px 30px rgba(45,125,70,0.1)', border: '1px solid #E8F5E9' },
    stepNum: { position: 'absolute', top: 0, right: 'calc(50% - 45px)', width: 28, height: 28, background: '#2D7D46', color: '#fff', borderRadius: '50%', fontSize: 12, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '3px solid #fff' },
    processStepTitle: { fontWeight: 700, color: '#1B5E20', fontSize: 18, marginBottom: 12 },
    processStepText: { fontSize: 14, color: '#666', lineHeight: 1.6 },
    packagesSection: { padding: '70px 0', background: '#fff' },
    packageCard: { background: '#fff', borderRadius: 30, padding: 40, border: '1px solid #eee', position: 'relative', transition: 'all 0.5s ease', textAlign: 'center', height: '100%' },
    popularBadge: { position: 'absolute', top: -15, left: '50%', transform: 'translateX(-50%)', background: '#E91E63', color: '#fff', padding: '6px 20px', borderRadius: 30, fontSize: 11, fontWeight: 800, boxShadow: '0 8px 20px rgba(233,30,99,0.3)' },
    packageIconBox: { width: 70, height: 70, borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 30px', fontSize: 30, color: '#fff', boxShadow: '0 12px 30px rgba(0,0,0,0.1)' },
    packageName: { fontWeight: 800, color: '#1B5E20', fontSize: 22, marginBottom: 10 },
    packageDivider: { height: 1, background: '#eee', marginBottom: 24 },
    packageFeatures: { listStyle: 'none', padding: 0, textAlign: 'left', marginBottom: 36 },
    packageBtn: { display: 'block', padding: '14px 20px', borderRadius: 50, color: '#fff', fontWeight: 700, textDecoration: 'none', transition: 'all 0.3s', fontSize: 15, boxShadow: '0 8px 25px rgba(0,0,0,0.1)' },
    mapSection: { padding: '70px 0', background: '#fff' },
    mapContainer: { borderRadius: 30, overflow: 'hidden', boxShadow: '0 20px 50px rgba(0,0,0,0.1)', border: '1px solid #f0f0f0', height: '100%', minHeight: 450 },
    mapCard: { background: '#f9fdf6', borderRadius: 30, padding: 40, height: '100%', border: '2px solid #E8F5E9', boxShadow: '0 10px 30px rgba(45,125,70,0.05)' },
    btnSecondaryShort: { display: 'inline-flex', alignItems: 'center', background: '#2D7D46', color: '#fff', padding: '10px 20px', borderRadius: 50, fontWeight: 600, fontSize: 14, textDecoration: 'none', transition: 'all 0.3s', boxShadow: '0 4px 15px rgba(45,125,70,0.2)' },
    careTipsBox: { background: '#E8F5E9', borderRadius: 30, padding: 40, height: '100%', boxShadow: '0 10px 30px rgba(45,125,70,0.05)' },
    tipRow: { display: 'flex', gap: 20, alignItems: 'flex-start', marginBottom: 25, fontSize: 15, color: '#2E7D32' },
    testimonialsSection: { background: '#fff', padding: '60px 0' },
    testimonialCard: { maxWidth: 700, margin: '0 auto', padding: '40px 60px', textAlign: 'center' },
    testimonialText: { fontSize: '1.4rem', fontStyle: 'italic', color: '#444', marginBottom: 20, lineHeight: 1.6 },
    stars: { color: '#FFD600', fontSize: '1.5rem', marginBottom: 15 },
    testimonialAuthor: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, marginTop: 16 },
    testimonialAvatar: { width: 40, height: 40, borderRadius: '50%', background: '#2D7D46', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16 },
    testimonialName: { fontWeight: 700, color: '#1B5E20' },
    testimonialLocation: { fontSize: 12, color: '#666' },
    sizeGuideSection: { padding: '70px 0', background: '#fff' },
    sizeItem: { display: 'flex', alignItems: 'center', gap: 15, fontSize: 16, fontWeight: 600, color: '#444' },
    sizeVisualizer: { background: 'linear-gradient(to bottom, #f0f9f0, #e8f5e9)', borderRadius: 40, padding: '60px 40px 80px', position: 'relative', minHeight: 600, display: 'flex', alignItems: 'stretch', justifyContent: 'center', border: '1px solid #C8E6C9', boxShadow: 'inset 0 0 50px rgba(45,125,70,0.05)', overflow: 'hidden' },
    rulerContainer: { position: 'absolute', top: 60, bottom: 80, left: 30, width: 60, borderRight: '1px solid #2D7D4644', zIndex: 1 },
    rulerLine: { position: 'absolute', width: '100%', height: 1, borderTop: '1px dashed #2D7D4633', left: 0 },
    rulerLabel: { position: 'absolute', right: 10, top: -10, fontSize: 10, color: '#2D7D46', fontWeight: 600 },
    sizeVisualWrap: { display: 'flex', alignItems: 'flex-end', gap: 'clamp(10px, 4vw, 50px)', width: '100%', height: '100%', justifyContent: 'center', zIndex: 2, paddingLeft: 40, paddingBottom: 20 },
    plantBox: { width: 80, background: 'rgba(255,255,255,0.4)', borderRadius: '15px 15px 0 0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', paddingBottom: 20, transition: 'all 0.3s ease', cursor: 'default', border: '1px solid rgba(45,125,70,0.1)', position: 'relative' },
    plantVisual: { width: '100%', height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'flex-end', overflow: 'hidden' },
    plantLabel: { fontSize: 11, fontWeight: 700, color: '#1B5E20', textTransform: 'uppercase', letterSpacing: 0.5 },
    humanFigure: { display: 'flex', flexDirection: 'column', alignItems: 'center', width: 100, height: '97.2%', position: 'relative', zIndex: 3 },
    humanImg: { width: '100%', height: '100%', objectFit: 'contain', filter: 'drop-shadow(0 10px 20px rgba(0,0,0,0.15))' },
    humanText: { position: 'absolute', top: -25, fontSize: 11, fontWeight: 700, color: '#333', whiteSpace: 'nowrap' },
    visualizerCaption: { position: 'absolute', bottom: 25, width: '100%', textAlign: 'center', color: '#666', fontSize: 13, fontStyle: 'italic', fontWeight: 500 },
    quizSection: { padding: '70px 0', background: 'linear-gradient(135deg, #0d3320 0%, #1a5c32 50%, #2D7D46 100%)' },
    glassCard: { background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(16px)', borderRadius: 24, padding: '32px 36px', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', boxShadow: '0 8px 32px rgba(0,0,0,0.18)' },
    quizWrap: { marginTop: 30 },
    quizProgress: { fontSize: 12, textTransform: 'uppercase', letterSpacing: 2, color: 'rgba(255,255,255,0.7)', marginBottom: 10 },
    quizBtnGroup: { display: 'flex', flexDirection: 'column', gap: 12 },
    quizOption: { textAlign: 'left', padding: '15px 25px', borderRadius: 15, border: '1px solid rgba(255,255,255,0.3)', transition: 'all 0.3s', fontSize: 15 },
    resultIcon: { fontSize: 48, marginBottom: 16 },
    compactPlantCard: { background: '#fff', borderRadius: 20, overflow: 'hidden', width: 160, boxShadow: '0 10px 25px rgba(0,0,0,0.2)', textAlign: 'left' },
    compactPlantImg: { width: '100%', height: 120, objectFit: 'cover' },
    compactPlantBody: { padding: '12px' },
    compactPlantLink: { display: 'inline-flex', alignItems: 'center', marginTop: 8, color: '#2D7D46', fontSize: 11, fontWeight: 700, textDecoration: 'none' },
    climateCard: { background: '#fff', borderRadius: 30, padding: 40, boxShadow: '0 20px 50px rgba(0,0,0,0.05)', border: '1px solid #eee', height: '100%', position: 'relative', overflow: 'hidden' },
    weatherIcon: { fontSize: '5rem', color: '#FFB300', textAlign: 'center', margin: '20px 0' },
    tempWrap: { textAlign: 'center', marginBottom: 30 },
    tempVal: { display: 'block', fontSize: '4rem', fontWeight: 800, color: '#2E7D32' },
    weatherLabel: { fontSize: '1.2rem', color: '#666', fontWeight: 600 },
    divider: { height: 1, background: '#eee', margin: '20px 0' },
    climateTip: { marginTop: 25, background: '#FFF3E0', padding: 20, borderRadius: 20, borderLeft: '5px solid #F57C00' },
    tipTitle: { fontWeight: 700, color: '#E65100', marginBottom: 10, fontSize: 15 },
    tipText: { margin: 0, fontSize: 14, color: '#795548', lineHeight: 1.6 },
    ctaBanner: { background: 'linear-gradient(135deg, #1a5c32 0%, #2D7D46 50%, #4CAF50 100%)', padding: '60px 20px', textAlign: 'center', position: 'relative', overflow: 'hidden' },
    ctaTitle: { fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.8rem, 4vw, 2.8rem)', fontWeight: 700, color: '#fff', marginTop: 20, marginBottom: 16, textShadow: '0 2px 10px rgba(0,0,0,0.2)' },
    ctaSubtitle: { color: 'rgba(255,255,255,0.85)', fontSize: 'clamp(0.95rem, 2vw, 1.1rem)', maxWidth: 600, margin: '0 auto 36px', lineHeight: 1.7 },
    newsletterWrap: { maxWidth: 500, margin: '30px auto', display: 'flex', gap: 10, background: 'rgba(255,255,255,0.1)', padding: 6, borderRadius: 50, border: '1px solid rgba(255,255,255,0.2)' },
    newsletterInput: { flex: 1, background: 'transparent', border: 'none', padding: '10px 25px', color: '#fff', outline: 'none' },
    newsletterBtn: { background: '#fff', color: '#2D7D46', border: 'none', padding: '10px 30px', borderRadius: 50, fontWeight: 700, transition: 'all 0.3s' },
    ctaBtnLight: { display: 'inline-flex', alignItems: 'center', background: '#fff', color: '#1B5E20', padding: '15px 34px', borderRadius: 50, fontWeight: 700, fontSize: 15, textDecoration: 'none', transition: 'all 0.3s', boxShadow: '0 10px 30px rgba(0,0,0,0.15)' },
    ctaBtnOutline: { display: 'inline-flex', alignItems: 'center', background: 'transparent', color: '#fff', padding: '15px 34px', borderRadius: 50, fontWeight: 600, fontSize: 15, textDecoration: 'none', border: '2px solid rgba(255,255,255,0.4)', transition: 'all 0.3s' },
    footer: {
        background: 'linear-gradient(160deg, #0a2415 0%, #0d3320 45%, #123d27 100%)',
        padding: '64px 0 28px',
        color: 'rgba(255,255,255,0.8)',
        overflow: 'hidden',
        position: 'relative',
        marginTop: 0,
    },
    footerTopAccent: {
        position: 'absolute',
        top: 0, left: 0, right: 0,
        height: 3,
        background: 'linear-gradient(90deg, transparent, #c9a03d, #f4e4bc, #c9a03d, transparent)',
    },
    footerLogoWrapper: {
        marginBottom: '20px',
    },
    footerLogoContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '14px',
        width: '100%',
    },
    footerLogoImageWrapper: {
        position: 'relative',
        display: 'inline-flex',
        justifyContent: 'center',
        alignItems: 'center',
    },
    footerLogoGlowRing: {
        position: 'absolute',
        top: '-4px',
        left: '-4px',
        right: '-4px',
        bottom: '-4px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #c9a03d, #f4e4bc, #c9a03d)',
        opacity: 0.6,
        zIndex: 0,
        animation: 'pulse 2s ease-in-out infinite',
    },
    footerLogoImage: {
        width: '70px',
        height: '70px',
        objectFit: 'cover',
        borderRadius: '50%',
        boxShadow: '0 8px 20px rgba(201, 160, 61, 0.25), 0 2px 4px rgba(0,0,0,0.05)',
        border: '3px solid #ffffff',
        display: 'block',
        position: 'relative',
        zIndex: 1,
        transition: 'transform 0.3s ease',
    },
    footerLogoFallback: {
        display: 'none',
        position: 'absolute',
        zIndex: 1,
        width: '70px',
        height: '70px',
        borderRadius: '50%',
        background: '#1B5E20',
        color: '#A5D6A7',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '30px',
        border: '3px solid #ffffff',
        boxShadow: '0 8px 20px rgba(201, 160, 61, 0.25)',
    },
    footerLogoBadge: {
        position: 'absolute',
        bottom: '-5px',
        right: '-8px',
        background: 'linear-gradient(135deg, #c9a03d, #b8860b)',
        borderRadius: '50%',
        width: '22px',
        height: '22px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '12px',
        boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
        zIndex: 2,
        border: '2px solid #ffffff'
    },
    footerLogoTextWrapper: {
        textAlign: 'center',
        width: '100%',
    },
    footerLogoTopLine: {
        width: '50px',
        height: '2px',
        background: 'linear-gradient(90deg, transparent, #c9a03d, transparent)',
        margin: '0 auto 12px auto',
    },
    footerLogoMainText: {
        margin: 0,
        fontSize: '16px',
        fontWeight: '700',
        letterSpacing: '1px',
        lineHeight: '1.4',
        fontFamily: "'Book Antiqua', 'Times New Roman', serif",
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    },
    footerLogoFirstWord: {
        color: '#ffffff',
        display: 'block',
        fontSize: '18px',
        fontWeight: '800',
        letterSpacing: '1.5px',
    },
    footerLogoSecondWord: {
        color: '#c9a03d',
        display: 'block',
        fontSize: '15px',
        fontWeight: '700',
        letterSpacing: '2px',
    },
    footerLogoDivider: {
        width: '100%',
        textAlign: 'center',
        margin: '8px 0',
        position: 'relative',
    },
    footerLogoDividerDot: {
        color: '#c9a03d',
        fontSize: '14px',
        background: 'transparent',
        padding: '0 10px',
        position: 'relative',
        zIndex: 1,
    },
    footerLogoSubText: {
        margin: '6px 0 0 0',
        fontSize: '9px',
        fontWeight: '600',
        color: 'rgba(255,255,255,0.5)',
        letterSpacing: '1.5px',
        fontFamily: "'Book Antiqua', 'Times New Roman', serif",
        textTransform: 'uppercase',
    },
    footerLogoBottomLine: {
        width: '70px',
        height: '1px',
        background: 'linear-gradient(90deg, #c9a03d, #f4e4bc, #c9a03d)',
        margin: '10px auto 0 auto',
    },
    footerAbout: {
        fontSize: '13.5px',
        color: 'rgba(255,255,255,0.55)',
        lineHeight: '1.8',
        maxWidth: '320px',
        margin: '0 auto 22px auto',
        textAlign: 'center',
    },
    footerColTitle: {
        color: '#c9a03d',
        fontSize: '12px',
        fontWeight: 800,
        letterSpacing: '1.5px',
        textTransform: 'uppercase',
        marginBottom: '20px',
        fontFamily: "'Book Antiqua', 'Times New Roman', serif",
    },
    footerLinkList: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
    },
    footerNavLink: {
        color: 'rgba(255,255,255,0.65)',
        textDecoration: 'none',
        fontSize: '14px',
        transition: 'all 0.25s ease',
        fontFamily: "'Book Antiqua', 'Times New Roman', serif",
        cursor: 'pointer',
    },
    footerContactGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    footerContactItem: {
        display: 'flex',
        alignItems: 'flex-start',
        gap: '12px',
        fontSize: '14px',
        color: 'rgba(255,255,255,0.7)',
        lineHeight: '1.5',
        fontFamily: "'Book Antiqua', 'Times New Roman', serif",
    },
    footerIcon: {
        color: '#c9a03d',
        fontSize: '15px',
        marginTop: '2px',
        minWidth: '16px',
    },
    footerLink: {
        color: 'rgba(255,255,255,0.7)',
        textDecoration: 'none',
        transition: 'color 0.3s ease',
        fontSize: '14px',
        fontFamily: "'Book Antiqua', 'Times New Roman', serif",
    },
    footerNewsletterText: {
        fontSize: '13.5px',
        color: 'rgba(255,255,255,0.55)',
        marginBottom: '16px',
        lineHeight: '1.6',
        fontFamily: "'Book Antiqua', 'Times New Roman', serif",
    },
    footerNewsletterWrap: {
        display: 'flex',
        gap: '8px',
        marginBottom: '18px',
    },
    footerNewsletterInput: {
        flex: 1,
        background: 'rgba(255,255,255,0.07)',
        border: '1px solid rgba(255,255,255,0.15)',
        borderRadius: '12px',
        padding: '11px 14px',
        color: '#fff',
        fontSize: '13px',
        outline: 'none',
        fontFamily: "'Book Antiqua', 'Times New Roman', serif",
    },
    footerNewsletterBtn: {
        background: 'linear-gradient(135deg, #c9a03d, #dcb85a)',
        border: 'none',
        borderRadius: '12px',
        width: '42px',
        color: '#0d3320',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        flexShrink: 0,
        transition: 'transform 0.2s ease',
    },
    footerWhatsappCta: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        background: 'rgba(37,211,102,0.12)',
        border: '1px solid rgba(37,211,102,0.35)',
        color: '#4fe08a',
        padding: '10px 16px',
        borderRadius: '12px',
        fontSize: '13px',
        fontWeight: 700,
        textDecoration: 'none',
        transition: 'all 0.3s ease',
        fontFamily: "'Book Antiqua', 'Times New Roman', serif",
    },
    footerSocialGroup: {
        display: 'flex',
        gap: '12px',
        justifyContent: 'center',
    },
    footerSocialIcon: {
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: '38px',
        height: '38px',
        borderRadius: '50%',
        background: 'rgba(255,255,255,0.08)',
        color: 'rgba(255,255,255,0.75)',
        fontSize: '16px',
        transition: 'all 0.3s ease',
        textDecoration: 'none',
        border: '1px solid rgba(255,255,255,0.1)',
    },
    footerDivider: {
        height: '1px',
        background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.12), transparent)',
        margin: '40px 0 24px',
    },
    footerCopyright: {
        color: 'rgba(255,255,255,0.4)',
        fontSize: '12.5px',
        fontFamily: "'Book Antiqua', 'Times New Roman', serif",
    },
    footerPowered: {
        color: 'rgba(255,255,255,0.3)',
        fontSize: '11.5px',
        letterSpacing: '0.5px',
        fontFamily: "'Book Antiqua', 'Times New Roman', serif",
    },
    footerImageCol: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
    },
    storeCardWrapper: {
        position: 'relative',
        width: '100%',
        maxWidth: '400px',
        borderRadius: '12px',
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
        border: '1px solid rgba(255, 255, 255, 0.08)',
        aspectRatio: '4 / 3', 
        marginTop: '-160px',
        backgroundColor: '#051d11'
    },
    storeFrontImage: {
        width: '100%',
        height: '100%',
        objectFit: 'cover', 
        objectPosition: 'center center',
    },
    storeCardOverlay: {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'linear-gradient(to top, rgba(5, 29, 17, 0.45) 0%, transparent 40%)',
        pointerEvents: 'none'
    },
    modalPlantCard: { background: '#fff', borderRadius: 25, overflow: 'hidden', border: '1px solid #E8F5E9', transition: 'all 0.4s ease', height: '100%', boxShadow: '0 8px 25px rgba(0,0,0,0.04)' },
    modalPlantImgBox: { height: 180, overflow: 'hidden', position: 'relative' },
    modalPlantImg: { width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.6s ease' },
    modalPlantContent: { padding: '20px' },
    faqAccordion: { background: 'transparent' },
    faqItem: { background: 'transparent', border: 'none', borderBottom: '1px solid #E8F5E9', marginBottom: 10 },
    whatsappFab: { position: 'fixed', bottom: 30, right: 30, width: 65, height: 65, background: '#25D366', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 25px rgba(37, 211, 102, 0.4)', zIndex: 1000, animation: 'fabPulse 2s infinite', textDecoration: 'none', transition: 'transform 0.3s' },
    whatsappTooltip: { position: 'absolute', right: 80, background: '#075E54', color: '#fff', padding: '8px 16px', borderRadius: 12, fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', boxShadow: '0 4px 15px rgba(0,0,0,0.1)', pointerEvents: 'none', opacity: 0, transform: 'translateX(10px)', transition: 'all 0.3s' },
};
